<template>
  <div class="import-job-main">
    <div class="left-menu" ref="leftMenu">
      <Menus :menu="menu" @menuClicked="menuClicked" />
    </div>

    <div class="right-tabulator">
      <div class="filter-area">
        <div>{{ filterText }}</div>
        <ButtonFilter
          class="button-filter"
          filterPosition="right"
          buttonText="Change Filter"
          :filterWidth="300"
          :isOpen.sync="mainFilterIsOpen"
        >
          <div class="filter-item">
            <BoxContainer
              class="filter-check"
              title="Date"
              :isChecked.sync="dateFilterChecked"
              :isCheckBox="true"
              style="margin-bottom: 5px"
            >
              <div class="dates">
                <DateTimePicker
                  placeholder="Start"
                  title="Start"
                  :showTime="false"
                  :value.sync="refreshStart"
                  :isDisabled="!dateFilterChecked"
                />
                <DateTimePicker
                  placeholder="End"
                  title="End"
                  :showTime="false"
                  :value.sync="refreshEnd"
                  :isDisabled="!dateFilterChecked"
                />
              </div>
            </BoxContainer>
          </div>
          <div class="filter-item">
            <BoxContainer
              class="filter-check"
              title="Search"
              :isChecked.sync="searchFilterChecked"
              :isCheckBox="true"
              style="margin-bottom: 5px"
            >
              <Dropdown
                placeholder="Search By"
                :items="searchFilterItems"
                itemKey="Search Criteria"
                :isDisabled="!searchFilterChecked"
                :isRequired="searchFilterChecked"
                :selectedObject.sync="selectedSearchBy"
                :errorMessage.sync="searchByError"
                @selected="searchBySelected"
                validator="text,40"
              />
              <Dropdown
                placeholder="Search Criteria"
                :items="searchCriteriaItems"
                itemKey="name"
                :isDisabled="!searchFilterChecked || criteriaDisabled"
                :isRequired="searchFilterChecked"
                :selectedObject.sync="selectedCriteria"
                validator="text,40"
              />
              <Input
                placeholder="Search"
                :inputValue.sync="searchByValue"
                :isDisabled="!searchFilterChecked"
                :isRequired="searchFilterChecked"
                :errorMessage.sync="searchByValueError"
              />
            </BoxContainer>
          </div>
          <button @click="refreshClicked" :disabled="!dateFilterChecked && !searchFilterChecked">Refresh</button>
        </ButtonFilter>
        <div>Last Refreshed: {{ lastRefreshed }}</div>
      </div>
      <div class="tabulator-area">
        <TableBox
          :tableData="jobsList"
          title="Freight Booking"
          :selectedRow.sync="selectedFreightBooking"
          :loading="tableLoading"
        />
      </div>
    </div>

    <PopupEntry
      v-if="showPopup"
      :show.sync="showPopup"
      :title="popupTitle"
      @save="saveClicked"
      @requiredClicked="requiredClicked"
      :createdBy="createdBy"
      :updatedBy="updatedBy"
      :errorMessages="eMessages"
      :successMessages="sMessages"
      :requiredError="rMessages"
      :viewMode="viewMode"
      :cancelButton="viewMode ? 'Close' : 'Cancel'"
    >
      <div class="popup-details" id="popupDetails" :style="{ height: '100%' }">
        <Tab :style="{ width: '100%' }" :tabs="tabsItems" :tabIndex.sync="currentTabIndex">
          <!-- Quick Entry tab -->
          <div slot="Quick Entry">
            <div class="tab-block">
              <div class="tab-title">Booking Info</div>
              <div class="details-column">
                <Dropdown
                  placeholder="Trans Mode"
                  :items="bookingInfoData['transModeList']"
                  itemKey="Description"
                  ref="transmode"
                  :errorMessage.sync="errorMessages['transmode']"
                  :selectedObject.sync="selectedBookingInfoData['selectedTransMode']"
                  :isRequired="true"
                  :isDisabled="viewMode"
                  validator="text,50"
                />
                <InputQuery
                  placeholder="Cargo Type"
                  :items="bookingInfoData['cargoTypeList']"
                  itemKey="Cargo Type"
                  descKey="Description"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedBookingInfoData['selectedCargoType']"
                  :errorMessage.sync="errorMessages['cargotype']"
                  :isRequired="true"
                  validator="text,50"
                  ref="cargotype"
                />
                <Dropdown
                  placeholder="Delivery Type"
                  :items="bookingInfoData['deliveryTypeList']"
                  itemKey="Description"
                  :errorMessage.sync="errorMessages['deliverytype']"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedBookingInfoData['selectedDeliveryType']"
                  :isRequired="true"
                  validator="text,25"
                  ref="deliverytype"
                />
                <Dropdown
                  placeholder="Freight Terms"
                  :items="bookingInfoData['freightTermsList']"
                  :errorMessage.sync="errorMessages['freightterms']"
                  itemKey="Freight Type Desc"
                  :isRequired="true"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedBookingInfoData['selectedFreightTerms']"
                  validator="text,25"
                  ref="freightterms"
                />
              </div>
              <div class="details-column">
                <InputQuery
                  placeholder="Shipper"
                  :items.sync="bookingInfoData['shipper']"
                  itemKey="Code - Name"
                  :errorMessage.sync="errorMessages['shipper']"
                  descKey="Shipper Name"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedBookingInfoData['selectedShipper']"
                  :isRequired="true"
                  @selected="shipperSelected"
                  addLink="/Home/MasterFiles/ProfileMaster/ShipperConsignee?new=1"
                  :canRefresh="true"
                  @refreshClicked="shipperInputQueryRefreshClicked"
                  :allowFreeText="false"
                  validator="text,115"
                  ref="shipper"
                />
              </div>
              <div class="details-column">
                <Input
                  placeholder="Booking #"
                  :inputValue.sync="selectedBookingInfoData['bookingNo']"
                  :isDisabled="true"
                />
                <DateTimePicker
                  placeholder="Booking Time"
                  title="Booking Time"
                  :errorMessage.sync="errorMessages['bookingtime']"
                  :value.sync="selectedBookingInfoData['bookingDateTime']"
                  :isRequired="true"
                  :isDisabled="viewMode"
                  ref="bookingtime"
                  :maxDate="smallDateTimeMaxDate"
                  :minDate="smallDateTimeMinValue"
                  @dateChanged="checkDateTime('bookingtime', $event)"
                />
              </div>
            </div>
            <div class="tab-block">
              <div class="tab-title">Shipping Info</div>
              <div class="details-column">
                <InputQuery
                  placeholder="Port of Loading"
                  :items.sync="shippingInfoData['portOfLoading']"
                  itemKey="Code - Desc"
                  descKey="Port Location"
                  :errorMessage.sync="errorMessages['pol']"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedPortLoading']"
                  :isRequired="true"
                  addLink="/Home/MasterFiles/OperationMaster/Port?new=1"
                  :canRefresh="true"
                  @refreshClicked="portOfLoadingInputQueryRefreshClicked"
                  validator="text,10"
                  ref="pol"
                />
                <InputQuery
                  placeholder="Port of Discharge"
                  :items.sync="shippingInfoData['portOfDischarge']"
                  itemKey="Code - Desc"
                  descKey="Port Location"
                  :errorMessage.sync="errorMessages['pod']"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedPortDischarge']"
                  :isRequired="true"
                  addLink="/Home/MasterFiles/OperationMaster/Port?new=1"
                  :canRefresh="true"
                  @refreshClicked="portOfDischargeInputQueryRefreshClicked"
                  validator="text,10"
                  ref="pod"
                />
              </div>
            </div>
          </div>
          <!-- Booking Info tab -->
          <div slot="Booking Info" style="height: 100%">
            <div>
              <div class="slot-body-block">
                <div class="slot-content-column column-flex">
                  <Dropdown
                    placeholder="Trans Mode"
                    :items="bookingInfoData['transModeList']"
                    itemKey="Description"
                    :isDisabled="viewMode"
                    :selectedObject.sync="selectedBookingInfoData['selectedTransMode']"
                    :isRequired="true"
                    validator="text,50"
                  />
                  <InputQuery
                    placeholder="Cargo Type"
                    :items="bookingInfoData['cargoTypeList']"
                    itemKey="Cargo Type"
                    descKey="Description"
                    :isDisabled="viewMode"
                    :selectedObject.sync="selectedBookingInfoData['selectedCargoType']"
                    :isRequired="true"
                    validator="text,50"
                  />

                  <Dropdown
                    placeholder="Delivery Type"
                    :items="bookingInfoData['deliveryTypeList']"
                    itemKey="Description"
                    :isDisabled="viewMode"
                    :selectedObject.sync="selectedBookingInfoData['selectedDeliveryType']"
                    :isRequired="true"
                    validator="text,25"
                  />
                  <Dropdown
                    placeholder="Freight Terms"
                    :items="bookingInfoData['freightTermsList']"
                    itemKey="Freight Type Desc"
                    :isRequired="true"
                    :isDisabled="viewMode"
                    :selectedObject.sync="selectedBookingInfoData['selectedFreightTerms']"
                    validator="text,25"
                  />
                  <Dropdown
                    placeholder="Cargo Terms"
                    :items="bookingInfoData['cargoTermsList']"
                    itemKey="Code - Desc"
                    :isDisabled="viewMode"
                    :selectedObject.sync="selectedBookingInfoData['selectedCargoTerms']"
                    validator="text,25"
                  />
                  <Dropdown
                    placeholder="Currency"
                    :items="bookingInfoData['currencyList']"
                    itemKey="Currency Code"
                    :isDisabled="viewMode"
                    :selectedObject.sync="selectedBookingInfoData['selectedCurrency']"
                    validator="text,3"
                  />
                  <Dropdown
                    placeholder="Payment Terms"
                    :items="bookingInfoData['paymentTermsList']"
                    itemKey="Description"
                    :isDisabled="viewMode"
                    :selectedObject.sync="selectedBookingInfoData['selectedPaymentTerms']"
                    validator="text,25"
                  />
                  <RadioButtonSet
                    title="Container Sharing?"
                    :buttons="sharingRadioButton"
                    labelKey="label"
                    :selected.sync="selectedSharing"
                    :isDisabled="viewMode"
                    :isHorizontal="true"
                    style="margin-bottom: 5px"
                  />
                  <RadioButtonSet
                    title="Insurance?"
                    :buttons="insuranceRadioButton"
                    labelKey="label"
                    :selected.sync="selectedInsurance"
                    :isDisabled="viewMode"
                    :isHorizontal="true"
                    style="margin-bottom: 5px"
                  />
                </div>
                <div class="slot-content-column">
                  <InputQuery
                    placeholder="Shipper"
                    :items.sync="bookingInfoData['shipper']"
                    itemKey="Code - Name"
                    descKey="Shipper Name"
                    :isDisabled="viewMode"
                    :selectedObject.sync="selectedBookingInfoData['selectedShipper']"
                    :isRequired="true"
                    @selected="shipperSelected"
                    addLink="/Home/MasterFiles/ProfileMaster/ShipperConsignee?new=1"
                    :canRefresh="true"
                    @refreshClicked="shipperInputQueryRefreshClicked"
                    :allowFreeText="false"
                    validator="text,115"
                  />
                  <div class="address" :class="{ 'address-collapse': !showShipperAddress }">
                    <div class="edit-area">
                      <button
                        v-if="!viewMode && selectedBookingInfoData['selectedShipper'] !== null"
                        @click="editDetailsPopup('shipper')"
                        >Edit</button
                      >
                      <img
                        src="https://dsmlib.logistics.systems/Icons/icons8-expand-arrow-24.png"
                        alt=""
                        height="20"
                        @click="showHideAddress('shipper')"
                        :class="[showShipperAddress ? 'img-show' : 'img-hide']"
                      />
                    </div>

                    <div v-if="showShipperAddress" class="address-block">
                      <div v-if="shipperAddress">
                        {{ shipperAddress['name'] }}<br />
                        {{ shipperAddress['add1'] }}<br />
                        {{ shipperAddress['add2'] }}<br />
                        {{ shipperAddress['add3'] }}<br />
                        {{ shipperAddress['add4'] }}<br />
                        {{ shipperAddress['postcode'] }} {{ shipperAddress['city'] }}<br />
                        {{ shipperAddress['state'] }} {{ shipperAddress['country'] }}<br /><br />
                        Attn: {{ shipperAddress['attn'] }}<br />
                        Tel: {{ shipperAddress['tel'] }}<br />
                        Fax: {{ shipperAddress['fax'] }}<br />
                        GST No: {{ shipperAddress['gst'] }}<br />
                      </div>
                      <div v-else> No Shipper Selected </div>
                    </div>
                    <div v-else> Shipper Details </div>
                  </div>
                  <InputQuery
                    placeholder="Consignee"
                    :items.sync="bookingInfoData['consignee']"
                    itemKey="Code - Name"
                    descKey="Consignee Name"
                    :isDisabled="viewMode"
                    :selectedObject.sync="selectedBookingInfoData['selectedConsignee']"
                    @selected="consigneeSelected"
                    addLink="/Home/MasterFiles/ProfileMaster/ShipperConsignee?new=1"
                    :canRefresh="true"
                    @refreshClicked="consigneeInputQueryRefreshClicked"
                    :allowFreeText="false"
                    validator="text,115"
                  />
                  <div class="address" :class="{ 'address-collapse': !showConsigneeAddress }">
                    <div class="edit-area">
                      <button v-if="!viewMode" @click="editDetailsPopup('consignee')">Edit</button>
                      <img
                        src="https://dsmlib.logistics.systems/Icons/icons8-expand-arrow-24.png"
                        alt=""
                        height="20"
                        @click="showHideAddress('consignee')"
                        :class="[showConsigneeAddress ? 'img-show' : 'img-hide']"
                      />
                    </div>

                    <div v-if="showConsigneeAddress" class="address-block">
                      <div v-if="consigneeAddress">
                        {{ consigneeAddress['name'] }}<br />
                        {{ consigneeAddress['add1'] }}<br />
                        {{ consigneeAddress['add2'] }}<br />
                        {{ consigneeAddress['add3'] }}<br />
                        {{ consigneeAddress['add4'] }}<br />
                        {{ consigneeAddress['postcode'] }} {{ consigneeAddress['city'] }}<br />
                        {{ consigneeAddress['state'] }} {{ consigneeAddress['country'] }}<br /><br />
                        Attn: {{ consigneeAddress['attn'] }}<br />
                        Tel: {{ consigneeAddress['tel'] }}<br />
                        Fax: {{ consigneeAddress['fax'] }}<br />
                        GST No: {{ consigneeAddress['gst'] }}<br />
                      </div>
                      <div v-else> No Consignee Selected </div>
                    </div>
                    <div v-else> Consignee Details </div>
                  </div>
                  <InputQuery
                    placeholder="Notify Party"
                    :items="bookingInfoData['notifyParty']"
                    itemKey="Code - Name"
                    descKey="Notify Party Name"
                    :isDisabled="viewMode"
                    :selectedObject.sync="selectedBookingInfoData['selectedNotifyParty']"
                    @selected="npartySelected"
                    validator="text,115"
                  />
                  <div class="address" :class="{ 'address-collapse': !showNPartyAddress }">
                    <div class="edit-area">
                      <button v-if="!viewMode" @click="editDetailsPopup('nparty')">Edit</button>
                      <img
                        src="https://dsmlib.logistics.systems/Icons/icons8-expand-arrow-24.png"
                        alt=""
                        height="20"
                        @click="showHideAddress('nparty')"
                        :class="[showNPartyAddress ? 'img-show' : 'img-hide']"
                      />
                    </div>

                    <div v-if="showNPartyAddress" class="address-block">
                      <div v-if="notifyPartyAddress">
                        {{ notifyPartyAddress['name'] }}<br />
                        {{ notifyPartyAddress['add1'] }}<br />
                        {{ notifyPartyAddress['add2'] }}<br />
                        {{ notifyPartyAddress['add3'] }}<br />
                        {{ notifyPartyAddress['add4'] }}<br />
                        {{ notifyPartyAddress['postcode'] }} {{ notifyPartyAddress['city'] }}<br />
                        {{ notifyPartyAddress['state'] }} {{ notifyPartyAddress['country'] }}<br /><br />
                        Attn: {{ notifyPartyAddress['attn'] }}<br />
                        Tel: {{ notifyPartyAddress['tel'] }}<br />
                        Fax: {{ notifyPartyAddress['fax'] }}<br />
                        GST No: {{ notifyPartyAddress['gst'] }}<br />
                      </div>
                      <div v-else> No Notify Party Selected </div>
                    </div>
                    <div v-else> Notify Party Details </div>
                  </div>
                  <Input
                    placeholder="Customer Ref #"
                    :inputValue.sync="selectedBookingInfoData['custRefNo']"
                    :isDisabled="viewMode"
                    validator="text,40"
                  />

                  <InputQuery
                    placeholder="Bill To"
                    :items.sync="bookingInfoData['billTo']"
                    itemKey="Code - Name"
                    descKey="Customer Name"
                    :isDisabled="viewMode"
                    :selectedObject.sync="selectedBookingInfoData['selectedBillTo']"
                    @selected="billToSelected"
                    ref="billToInput"
                    addLink="/Home/MasterFiles/ProfileMaster/Customer?new=1"
                    :canRefresh="true"
                    @refreshClicked="billToInputQueryRefreshClicked"
                    validator="text,115"
                    :isRequired="!isSaveAsJobChecked"
                  />
                  <div v-if="selectedBookingInfoData['billToDetails']" style="display: flex; align-items: center">
                    <span>Status</span>
                    <span
                      class="status-container"
                      :style="{ backgroundColor: selectedBookingInfoData['billToDetails']['status']['color'] }"
                    >
                      {{ selectedBookingInfoData['billToDetails']['status']['text'] }}
                    </span>
                    <span>Outstanding</span>
                    <Tooltip
                      position="top"
                      :tooltipText="`${selectedBookingInfoData['billToDetails']['currency']} ${currencyFormattedValue}`"
                      style="width: 100%"
                      :xOffset="6"
                      :yOffset="2"
                    >
                      <span class="outstanding-prog">
                        <div style="z-index: 1; display: flex; justify-content: center; width: 100%; cursor: default"
                          >{{ selectedBookingInfoData['billToDetails']['outstanding']['perc'] }}%</div
                        >
                        <div
                          class="progress-ind"
                          :style="{
                            width: `${selectedBookingInfoData['billToDetails']['outstanding']['perc'] > 100 ? 100 : selectedBookingInfoData['billToDetails']['outstanding']['perc']}%`,
                            backgroundColor: selectedBookingInfoData['billToDetails']['outstanding']['color'],
                          }"
                        ></div>
                      </span>
                    </Tooltip>
                  </div>
                  <span v-if="selectedBookingInfoData['billToDetails']"
                    >Credit Limit : {{ selectedBookingInfoData['billToDetails']['currency'] }}
                    {{ selectedBookingInfoData['billToDetails']['creditLimit'] }} Credit Terms :
                    {{ selectedBookingInfoData['billToDetails']['creditTerms'] }}</span
                  >
                </div>
                <div class="slot-content-column" style="display: flex; flex-direction: column">
                  <Input
                    placeholder="Booking #"
                    :inputValue.sync="selectedBookingInfoData['bookingNo']"
                    :isDisabled="true"
                  />
                  <DateTimePicker
                    placeholder="Booking Time"
                    title="Booking Time"
                    :value.sync="selectedBookingInfoData['bookingDateTime']"
                    :isRequired="true"
                    :isDisabled="viewMode"
                    :maxDate="smallDateTimeMaxDate"
                    :minDate="smallDateTimeMinValue"
                    @dateChanged="checkDateTime('bookingtime', $event)"
                  />
                  <Input
                    placeholder="Requested By"
                    :inputValue.sync="selectedBookingInfoData['requestedBy']"
                    :isDisabled="viewMode"
                    validator="text,20"
                  />
                  <RemarksBox
                    title="Remarks"
                    :text.sync="selectedBookingInfoData['remarks']"
                    style="height: 120px"
                    :maxLength="200"
                    :isDisabled="viewMode"
                  />
                  <InputQuery
                    placeholder="Quotation #"
                    :items.sync="bookingInfoData['quotationNo']"
                    itemKey="Quotation No"
                    descKey="Valid From - Valid To"
                    :isDisabled="viewMode"
                    :selectedObject.sync="selectedBookingInfoData['selectedQuotationNo']"
                    validator="text,25"
                    :showDesc="true"
                  />

                  <Input
                    placeholder="Project #"
                    :inputValue.sync="selectedBookingInfoData['projectNo']"
                    :isDisabled="viewMode"
                    validator="text,25"
                  />
                </div>

                <div class="edit-address-popup" v-if="edittingDetails">
                  <div class="edit-content">
                    <div class="title">Edit {{ detailsEditTitle }} Details</div>
                    <Input placeholder="Name" :inputValue.sync="detailsEditObject['name']" validator="text,100" />
                    <AddressFormV2
                      :address.sync="detailsEditObject"
                      :postcodes="addressDetails.postcodeList"
                      :states="addressDetails.stateList"
                      :countries="addressDetails.countryList"
                    />
                    <Input placeholder="Attn To" :inputValue.sync="detailsEditObject['attn']" validator="text,100" />
                    <div style="display: flex">
                      <Input
                        placeholder="Tel #"
                        style="padding-right: 5px"
                        :inputValue.sync="detailsEditObject['tel']"
                        validator="text,20"
                      />
                      <Input
                        placeholder="Fax #"
                        style="padding-left: 5px"
                        :inputValue.sync="detailsEditObject['fax']"
                        validator="text,20"
                      />
                    </div>
                    <Input
                      placeholder="GST License #"
                      :inputValue.sync="detailsEditObject['gst']"
                      validator="text,50"
                    />
                    <div class="button-section">
                      <button @click="detailsEditSave('save')" style="background-color: green; color: white"
                        >Update</button
                      >
                      <button @click="detailsEditSave('cancel')" style="background-color: red; color: white"
                        >Cancel</button
                      >
                    </div>
                  </div>
                </div>
              </div>
              <BoxContainer title="Cargo Info" :isCollapsible="true">
                <div class="cargo-block" style="height: 100%">
                  <div class="details-column" style="width: 15%">
                    <div style="margin-right: 10px">
                      <div style="display: flex; align-items: center">
                        <Input
                          placeholder="Gross Weight"
                          textAlign="right"
                          :inputValue.sync="selectedCargoInfoData['gWeight']"
                          :isDisabled="viewMode"
                          validator="positiveDecimal,5,4"
                        />
                        <span style="margin-left: 8px">KG</span>
                      </div>
                      <div style="display: flex; align-items: center">
                        <Input
                          placeholder="Volume"
                          textAlign="right"
                          :inputValue.sync="selectedCargoInfoData['volume']"
                          validator="positiveDecimal,5,4"
                          :isDisabled="viewMode"
                        />
                        <span style="margin-left: 8px">M<sup>3</sup></span>
                      </div>
                      <div style="display: flex; align-items: center">
                        <Input
                          placeholder="Chargeable"
                          textAlign="right"
                          :inputValue.sync="selectedCargoInfoData['chargeable']"
                          validator="positiveDecimal,5,4"
                          :isDisabled="viewMode"
                        />
                        <span style="margin-left: 8px">KG</span>
                      </div>
                    </div>
                  </div>
                  <div style="width: 30%; margin-right: 5px">
                    <InputQuery
                      placeholder="Commodity"
                      :items="cargoInfoData['commodityList']"
                      itemKey="Code - Desc"
                      :isDisabled="viewMode"
                      :selectedObject.sync="selectedCargoInfoData['selectedCommodity']"
                      validator="text,10"
                    />
                    <Input
                      placeholder="No. of Pkg"
                      :isDisabled="viewMode"
                      :inputValue.sync="selectedCargoInfoData['packageCount']"
                      validator="positiveDecimal,8,2"
                      ref="packagecount"
                      :isRequired="!isSaveAsJobChecked"
                    />
                    <InputQuery
                      placeholder="Pkg Type"
                      :items="cargoInfoData['pkgTypeList']"
                      itemKey="Code - Desc"
                      descKey="Description"
                      :isDisabled="viewMode"
                      :selectedObject.sync="selectedCargoInfoData['selectedPackageType']"
                      validator="text,65"
                      ref="pkgtype"
                      :isRequired="!isSaveAsJobChecked"
                    />
                  </div>
                </div>
                <div class="table-container">
                  <div class="tabulator-title">
                    <span v-if="viewMode">Container Summary</span>
                    <span v-if="viewMode" class="status-container">
                      {{ contSummary ? contSummary : '-' }}
                    </span>
                    <div style="flex-grow: 1; display: flex; justify-content: flex-end; margin-right: 5px"
                      >Container Details</div
                    >
                    <input
                      type="file"
                      accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      style="display: none"
                      ref="excelCont"
                      @change="excelContChange"
                    />
                    <button v-if="!viewMode" @click="importFromExcel">Import From Excel</button>
                    <button v-if="!viewMode" @click="addContainerClicked">Add Container</button>
                    <button v-if="!viewMode" @click="editContainerClicked">Edit Container</button>
                    <button v-if="!viewMode" @click="deleteContainerClicked">Delete Container</button>
                  </div>
                  <TableBox
                    title="Container Details"
                    tableID="contDetails"
                    :tableData.sync="selectedCargoInfoData['contDetailsList']"
                    :selectedRow.sync="selectedCargoInfoData['selectedContDetails']"
                    :hideColumns="[
                      'item_action',
                      'Old Cont #',
                      'Old Seal No',
                      'Old Cont Size',
                      'Old Cont Type',
                      'Old Handling Type',
                      'Old Delivery Type',
                      'ContCharges',
                      'Invoices',
                      'UpdateType',
                      'Cont Weight',
                      'SOC/COC',
                      'Send To',
                      'ROT No',
                      'Delivery Type',
                    ]"
                  />
                </div>
              </BoxContainer>
              <JobPageCargoInfoPopup
                v-if="cargoPopupDetails['cargoAddType']"
                :title="cargoPopupDetails['cargoAddType'] == 'sub' ? 'Sub Package Details' : 'Container Details'"
                @cancel="cargoPopupCancel"
                @addDetails="cargoInfoPopupAddDetails"
                :pageType="pageType"
                :isEdit="cargoPopupDetails['isEdit']"
                :editDetails="cargoPopupDetails['editDetails']"
                :pkgTypeList="cargoInfoData['pkgTypeList']"
                :itemCode="cargoInfoData['itemCode']"
                :contSizeList="cargoInfoData['contSizeList']"
                :contTypeList="cargoInfoData['contTypeList']"
                :contStatusList="cargoInfoData['contStatusList']"
                :contHandlingTypeList="cargoInfoData['contHandlingTypeList']"
              />
            </div>
          </div>
          <!-- Shipping Info Tab -->
          <div slot="Shipping Info" style="height: 100%">
            <div class="slot-body-block">
              <div class="slot-content-column">
                <Input
                  placeholder="Booking Ref #"
                  :inputValue.sync="selectedShippingInfoData['bookingRefNo']"
                  :isDisabled="viewMode"
                  validator="text,10"
                />
                <Input
                  placeholder="No. of Original BLs"
                  :inputValue.sync="selectedShippingInfoData['originalBLNo']"
                  :isDisabled="viewMode"
                  validator="positiveDecimal,2"
                />
                <InputQuery
                  placeholder="Ship Call #"
                  :items="shippingInfoData['shipCallNoList']"
                  itemKey="SCN - Terminal"
                  descKey="Vessel Name"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedShipCall']"
                  :search.sync="safeShipCallNoValue"
                  validator="text,45"
                  :isLoading="shipCallNoLoading"
                />
                <Input
                  placeholder="Voyage #"
                  :inputValue.sync="selectedShippingInfoData['voyNo']"
                  :isDisabled="viewMode"
                  validator="text,10"
                />
                <InputQuery
                  placeholder="Vessel ID"
                  :items="shippingInfoData['vesselIDList']"
                  itemKey="Vessel ID"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedVesselID']"
                  :canRefresh="true"
                  @selected="vesselIDselected"
                  @refreshClicked="vesselIDInputQueryRefreshClicked"
                  validator="text,45"
                />
                <InputQuery
                  placeholder="Vessel Name"
                  :items="shippingInfoData['vesselIDList']"
                  itemKey="Vessel Name"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedVesselName']"
                  :search.sync="selectedShippingInfoData['vesselNameValue']"
                  :canRefresh="true"
                  :allowFreeText="true"
                  @selected="vesselNameselected"
                  @refreshClicked="vesselIDInputQueryRefreshClicked"
                  validator="text,45"
                />
                <InputQuery
                  placeholder="Shipping Agent"
                  :items="shippingInfoData['shippingAgentList']"
                  itemKey="Code - Name"
                  descKey="Agent Name"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedShippingAgent']"
                  :canRefresh="true"
                  @refreshClicked="shippingAgentInputQueryRefreshClicked"
                  addLink="/Home/MasterFiles/ProfileMaster/Supplier?new=1&supplier=SA"
                  validator="text,115"
                />
                <InputQuery
                  placeholder="Shipping Line"
                  :items="shippingInfoData['shippingLineList']"
                  itemKey="Code - Name"
                  descKey="Agent Name"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedShippingLine']"
                  validator="text,115"
                />
              </div>
              <div class="slot-content-column">
                <InputQuery
                  placeholder="Port of Loading"
                  :items.sync="shippingInfoData['portOfLoading']"
                  itemKey="Code - Desc"
                  descKey="Port Location"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedPortLoading']"
                  :isRequired="true"
                  addLink="/Home/MasterFiles/OperationMaster/Port?new=1"
                  :canRefresh="true"
                  @refreshClicked="portOfLoadingInputQueryRefreshClicked"
                  validator="text,10"
                />
                <DateTimePicker
                  placeholder="Closing Date"
                  title="Closing Date"
                  :value.sync="selectedShippingInfoData['closingDate']"
                  :isDisabled="viewMode"
                  ref="closingDate"
                  :errorMessage.sync="errorMessages['closingDate']"
                  :maxDate="smallDateTimeMaxDate"
                  :minDate="smallDateTimeMinValue"
                  @dateChanged="checkDateTime('closingDate', $event)"
                  :isRequired="!isSaveAsJobChecked"
                />
                <InputQuery
                  placeholder="Port of Discharge"
                  :items.sync="shippingInfoData['portOfDischarge']"
                  itemKey="Code - Desc"
                  descKey="Port Location"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedPortDischarge']"
                  :isRequired="true"
                  addLink="/Home/MasterFiles/OperationMaster/Port?new=1"
                  :canRefresh="true"
                  @refreshClicked="portOfDischargeInputQueryRefreshClicked"
                  validator="text,10"
                />
                <InputQuery
                  placeholder="Final Destination"
                  :items.sync="shippingInfoData['finalDestination']"
                  itemKey="Code - Desc"
                  descKey="Port Location"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedFinalDestination']"
                  addLink="/Home/MasterFiles/OperationMaster/Port?new=1"
                  :canRefresh="true"
                  @refreshClicked="finalDestinationInputQueryRefreshClicked"
                  validator="text,10"
                />
                <!-- <InputQuery
                  placeholder="Place of Issue"
                  :items.sync="shippingInfoData['placeOfIssue']"
                  itemKey="Code - Desc"
                  descKey="Port Location"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedPlaceOfIssue']"
                  addLink="/Home/MasterFiles/OperationMaster/Port?new=1"
                  :canRefresh="true"
                  @refreshClicked="finalDestinationInputQueryRefreshClicked"
                  validator="text,10"
                />
                <InputQuery
                  placeholder="Place of Delivery"
                  :items.sync="shippingInfoData['placeOfDelivery']"
                  itemKey="Code - Desc"
                  descKey="Port Location"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedPlaceOfDelivery']"
                  addLink="/Home/MasterFiles/OperationMaster/Port?new=1"
                  :canRefresh="true"
                  @refreshClicked="finalDestinationInputQueryRefreshClicked"
                  validator="text,10"
                /> -->

                <div class="box-container">
                  <CheckBoxV2
                    title="Transhipment"
                    :isChecked.sync="selectedShippingInfoData['isTranshipment']"
                    :isDisabled="viewMode"
                    style="position: absolute; background-color: white; padding: 0 5px; top: -10px"
                  />
                  <InputQuery
                    placeholder="M. Vessel ID"
                    :items="shippingInfoData['vesselIDList']"
                    itemKey="ID - Name"
                    descKey="Vessel Name"
                    :isDisabled="isTranshipmentDisabled"
                    :isRequired="isTranshipmentRequired"
                    :errorMessage.sync="errorMessages['vesselID']"
                    ref="vesselID"
                    :selectedObject.sync="selectedShippingInfoData['selectedMVesselID']"
                    :canRefresh="true"
                    @refreshClicked="vesselIDInputQueryRefreshClicked"
                    validator="text,45"
                  />
                  <Input
                    placeholder="M. Voyage #"
                    :inputValue.sync="selectedShippingInfoData['mVoyNo']"
                    :isDisabled="isTranshipmentDisabled"
                    :isRequired="isTranshipmentRequired"
                    :errorMessage.sync="errorMessages['voyageNo']"
                    ref="voyageNo"
                    validator="text,10"
                  />
                  <InputQuery
                    placeholder="Port of Transhipment"
                    :items="shippingInfoData['portOfDischarge']"
                    itemKey="Code - Desc"
                    descKey="Port Location"
                    :isDisabled="isTranshipmentDisabled"
                    :selectedObject.sync="selectedShippingInfoData['selectedPortDischarge2']"
                    validator="text,10"
                  />
                  <DateTimePicker
                    placeholder="ETD"
                    :value.sync="selectedShippingInfoData['ETATime']"
                    :isDisabled="isTranshipmentDisabled"
                    :errorMessage.sync="errorMessages['ETATime']"
                    :maxDate="smallDateTimeMaxDate"
                    :minDate="smallDateTimeMinValue"
                    @dateChanged="checkDateTime('ETATime', $event)"
                    ref="ETATime"
                  />
                </div>
              </div>
              <div class="slot-content-column">
                <DateTimePicker
                  placeholder="POL ETA"
                  title="POL ETA"
                  :value.sync="selectedShippingInfoData['polETA']"
                  :isDisabled="viewMode"
                  ref="polETA"
                  :errorMessage.sync="errorMessages['polETA']"
                  :maxDate="smallDateTimeMaxDate"
                  :minDate="smallDateTimeMinValue"
                  @dateChanged="checkDateTime('polETA', $event)"
                  :isRequired="!isSaveAsJobChecked"
                />
                <DateTimePicker
                  placeholder="POL ETD"
                  title="POL ETD"
                  :value.sync="selectedShippingInfoData['polETD']"
                  :isDisabled="viewMode"
                  ref="polETD"
                  :errorMessage.sync="errorMessages['polETD']"
                  :maxDate="smallDateTimeMaxDate"
                  :minDate="smallDateTimeMinValue"
                  @dateChanged="checkDateTime('polETD', $event)"
                  :isRequired="!isSaveAsJobChecked"
                />
                <DateTimePicker
                  placeholder="POD ETA"
                  title="POD ETA "
                  :value.sync="selectedShippingInfoData['podETA']"
                  :isDisabled="viewMode"
                  :errorMessage.sync="errorMessages['podETA']"
                  :maxDate="smallDateTimeMaxDate"
                  :minDate="smallDateTimeMinValue"
                  @dateChanged="checkDateTime('podETA', $event)"
                  ref="podETA"
                  :isRequired="!isSaveAsJobChecked"
                />
                <DateTimePicker
                  placeholder="Final Dest. ETA"
                  title="Final Dest. ETA"
                  :value.sync="selectedShippingInfoData['fDestETA']"
                  :isDisabled="viewMode"
                  ref="fDestETA"
                  :errorMessage.sync="errorMessages['fDestETA']"
                  :maxDate="smallDateTimeMaxDate"
                  :minDate="smallDateTimeMinValue"
                  @dateChanged="checkDateTime('fDestETA', $event)"
                />
                <InputQuery
                  placeholder="Carrier Agent"
                  :items="shippingInfoData['coloaderList']"
                  itemKey="Code - Name"
                  descKey="Agent Name"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedCarrierAgent']"
                  validator="text,115"
                />
                <InputQuery
                  placeholder="Forwarding Agent"
                  :items="shippingInfoData['fwdgAgentList']"
                  itemKey="Code - Name"
                  descKey="FA Name"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedFwdgAgent']"
                  validator="text,115"
                />
                <InputQuery
                  placeholder="Oversea Agent"
                  :items="shippingInfoData['overseaAgentList']"
                  itemKey="Code - Name"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedOverseaAgent']"
                  validator="text,115"
                />
                <InputQuery
                  placeholder="Port Operator"
                  :items="shippingInfoData['portOperator']"
                  itemKey="Code - Name"
                  descKey="Operator Name"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedPortOperator']"
                  validator="text,115"
                />
                <Input
                  placeholder="Cont Operator"
                  :inputValue.sync="selectedShippingInfoData['selectedContOperator']"
                  :isDisabled="viewMode"
                  validator="text,10"
                />
                <InputQuery
                  placeholder="Co-loader"
                  :items="shippingInfoData['coloaderList']"
                  itemKey="Code - Name"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedCoLoader']"
                  validator="text,25"
                />
                <!-- <DateTimePicker
                  placeholder="Issue Date"
                  title="Issue Date"
                  :value.sync="selectedShippingInfoData['issueDate']"
                  :isDisabled="viewMode"
                /> -->
              </div>
            </div>
            <div class="shipping-textarea">
              <RemarksBox
                title="Goods Description"
                :text.sync="selectedShippingInfoData['goodsDescription']"
                style="height: 100%"
                :freeText="true"
                :isDisabled="viewMode"
              />
              <Tooltip
                position="left"
                :minWidth="300"
                tooltipText="The text will be reformatted upon save to accommodate printing of official forms and EDI declaration."
              />
              <RemarksBox
                title="Shipping Marks"
                :text.sync="selectedShippingInfoData['shippingMarks']"
                style="height: 100%"
                :freeText="true"
                :isDisabled="viewMode"
              />
              <Tooltip
                position="left"
                :minWidth="300"
                tooltipText="The text will be reformatted upon save to accommodate printing of official forms and EDI declaration."
              />
            </div>
          </div>

          <!-- Goods Received Tab -->
          <div slot="Goods Received" style="height: 100%">
            <div class="slot-body-block">
              <div class="slot-content-column">
                <CheckBox
                  title="Booking Confirmed (Save as Job)"
                  :isChecked.sync="selectedGoodsReceivedData['saveAsJob']"
                  :isDisabled="viewMode || disableSaveAsJob"
                />
                <div v-if="!isSaveAsJobChecked">
                  <Input
                    placeholder="Job #"
                    :inputValue.sync="selectedGoodsReceivedData['jobNo']"
                    :isDisabled="true"
                    style="margin-top: 5px"
                  />
                  <RadioButtonSet
                    title="Urgent?"
                    :buttons="urgentRadioButton"
                    labelKey="label"
                    :selected.sync="selectedUrgent"
                    :isDisabled="viewMode"
                    :isHorizontal="true"
                  />
                </div>
              </div>
              <div class="slot-content-column">
                <div v-if="!isSaveAsJobChecked">
                  <DateTimePicker
                    placeholder="Job Time"
                    title="Job Time"
                    :errorMessage.sync="errorMessages['jobtime']"
                    :value.sync="selectedGoodsReceivedData['jobDateTime']"
                    :isRequired="true"
                    :isDisabled="viewMode || isSaveAsJobChecked"
                    ref="jobtime"
                    :maxDate="smallDateTimeMaxDate"
                    :minDate="smallDateTimeMinValue"
                    @dateChanged="checkDateTime('jobtime', $event)"
                    style="margin-top: 15px"
                  />
                  <DateTimePicker
                    placeholder="Cargo Readiness Time"
                    title="Cargo Readiness Time"
                    :errorMessage.sync="errorMessages['cargoreadinesstime']"
                    :value.sync="selectedGoodsReceivedData['cargoReadinessTime']"
                    :isDisabled="viewMode"
                    ref="cargoreadinesstime"
                    :maxDate="smallDateTimeMaxDate"
                    :minDate="smallDateTimeMinValue"
                    @dateChanged="checkDateTime('cargoreadinesstime', $event)"
                  />
                  <DateTimePicker
                    placeholder="Required Date"
                    title="Required Date"
                    :errorMessage.sync="errorMessages['requireddate']"
                    :value.sync="selectedGoodsReceivedData['requiredDate']"
                    :isDisabled="viewMode"
                    ref="requireddate"
                    :maxDate="smallDateTimeMaxDate"
                    :minDate="smallDateTimeMinValue"
                    @dateChanged="checkDateTime('requireddate', $event)"
                  />
                  <Input
                    placeholder="Cargo Go Down"
                    :isDisabled="viewMode"
                    :inputValue.sync="selectedGoodsReceivedData['cargoGoDown']"
                    validator="text,50"
                  />
                </div>
              </div>
              <div class="slot-content-column">
                <div class="box-container" v-if="!isSaveAsJobChecked">
                  <div v-if="!isSaveAsJobChecked">
                    <CheckBoxV2
                      title="Fumigation"
                      :isChecked.sync="selectedGoodsReceivedData['isFumigation']"
                      :isDisabled="viewMode"
                      style="position: absolute; background-color: white; padding: 0 5px; top: -10px"
                    />
                    <InputQuery
                      placeholder="Fumigator"
                      :items="shippingInfoData['fumigatorList']"
                      itemKey="Supplier Code"
                      descKey="Supplier Name"
                      :isDisabled="isFumigationDisabled"
                      :isRequired="isFumigationRequired"
                      :errorMessage.sync="errorMessages['fumigator']"
                      ref="fumigator"
                      :selectedObject.sync="selectedGoodsReceivedData['selectedFumigator']"
                      validator="text,10"
                    />
                    <InputQuery
                      placeholder="Location"
                      :items="shippingInfoData['fumigatorLocationList']"
                      itemKey="Destination Code"
                      descKey="Company"
                      :isDisabled="isFumigationDisabled"
                      :isRequired="isFumigationRequired"
                      :errorMessage.sync="errorMessages['location']"
                      ref="location"
                      :selectedObject.sync="selectedGoodsReceivedData['selectedLocation']"
                      validator="text,10"
                    />
                    <DateTimePicker
                      placeholder="Fumigate on"
                      title="Fumigate on"
                      :errorMessage.sync="errorMessages['fumigateon']"
                      :value.sync="selectedGoodsReceivedData['fumigateTime']"
                      :isDisabled="isFumigationDisabled"
                      :isRequired="isFumigationRequired"
                      ref="fumigateon"
                      :maxDate="smallDateTimeMaxDate"
                      :minDate="smallDateTimeMinValue"
                      @dateChanged="checkDateTime('fumigateon', $event)"
                    />
                    <DateTimePicker
                      placeholder="Ventilation"
                      title="Ventilation"
                      :errorMessage.sync="errorMessages['ventilation']"
                      :value.sync="selectedGoodsReceivedData['ventilationTime']"
                      :isDisabled="isFumigationDisabled"
                      :isRequired="isFumigationRequired"
                      ref="ventilation"
                      :maxDate="smallDateTimeMaxDate"
                      :minDate="smallDateTimeMinValue"
                      @dateChanged="checkDateTime('ventilation', $event)"
                    />
                    <Input
                      placeholder="Phyto Marking"
                      :inputValue.sync="selectedGoodsReceivedData['phytoMarking']"
                      :isDisabled="isFumigationDisabled"
                      validator="text,30"
                    />
                  </div> </div
              ></div> </div
            ><br />
            <div class="table-container" v-if="!isSaveAsJobChecked">
              <div class="tabulator-title">
                <div style="flex-grow: 1; display: flex; justify-content: flex-end; margin-right: 5px"
                  >Sub Package Details</div
                >
                <button v-if="!viewMode" @click="addPackageClicked(false)">Add Package</button>
                <button v-if="!viewMode" @click="addPackageClicked(true)">Edit Package</button>
                <button v-if="!viewMode" @click="cargoInfoPopupDeleteDetails()">Delete Package</button>
              </div>
              <TableBox
                title="Sub Package Details"
                tableID="subpkg"
                :tableData="selectedGoodsReceivedData['subPkgList']"
                :selectedRow.sync="selectedGoodsReceivedData['selectedSubPkgs']"
                :hideColumns="['item_action']"
              />
              <JobPageCargoInfoPopup
                v-if="cargoPopupDetails['cargoAddType']"
                :title="cargoPopupDetails['cargoAddType'] == 'sub' ? 'Sub Package Details' : 'Container Details'"
                @cancel="cargoPopupCancel"
                @addDetails="cargoInfoPopupAddDetails"
                :pageType="pageType"
                :isEdit="cargoPopupDetails['isEdit']"
                :editDetails="cargoPopupDetails['editDetails']"
                :pkgTypeList="cargoInfoData['pkgTypeList']"
                :itemCode="cargoInfoData['itemCode']"
                :contSizeList="cargoInfoData['contSizeList']"
                :contTypeList="cargoInfoData['contTypeList']"
                :contStatusList="cargoInfoData['contStatusList']"
                :contHandlingTypeList="cargoInfoData['contHandlingTypeList']"
              />
            </div>
          </div>

          <!-- B/L Info Tab -->
          <div slot="B/L Info" style="height: 100%">
            <div class="slot-body-block">
              <div class="slot-content-column">
                <CheckBox
                  title="Save B/L Info"
                  :isChecked.sync="selectedBLInfoData['saveBL']"
                  :isDisabled="viewMode || disableSaveBL"
                />
                <div v-if="!isSaveBLChecked">
                  <Input placeholder="B/L #" :inputValue.sync="selectedBLInfoData['blNo']" :isDisabled="true" />
                  <InputQuery
                    placeholder="Place of Issue"
                    :items.sync="shippingInfoData['portOfLoading']"
                    itemKey="Code - Desc"
                    descKey="Port Location"
                    :isDisabled="viewMode"
                    :selectedObject.sync="selectedBLInfoData['selectedPlaceOfIssue']"
                    addLink="/Home/MasterFiles/OperationMaster/Port?new=1"
                    :canRefresh="true"
                    @refreshClicked="portOfLoadingInputQueryRefreshClicked"
                    validator="text,10"
                  />
                  <DateTimePicker
                    placeholder="Date of Issue"
                    title="Date of Issue"
                    :errorMessage.sync="errorMessages['dateofissue']"
                    :value.sync="selectedBLInfoData['dateOfIssue']"
                    :isDisabled="viewMode"
                    ref="dateofissue"
                    :maxDate="smallDateTimeMaxDate"
                    :minDate="smallDateTimeMinValue"
                    @dateChanged="checkDateTime('dateofissue', $event)"
                  />
                  <InputQuery
                    placeholder="Place of Receipt"
                    :items.sync="shippingInfoData['portOfLoading']"
                    itemKey="Code - Desc"
                    descKey="Port Location"
                    :isDisabled="viewMode"
                    :selectedObject.sync="selectedBLInfoData['selectedPlaceOfReceipt']"
                    addLink="/Home/MasterFiles/OperationMaster/Port?new=1"
                    :canRefresh="true"
                    @refreshClicked="portOfLoadingInputQueryRefreshClicked"
                    validator="text,10"
                  />
                  <InputQuery
                    placeholder="Place of Delivery"
                    :items.sync="shippingInfoData['portOfLoading']"
                    itemKey="Code - Desc"
                    descKey="Port Location"
                    :isDisabled="viewMode"
                    :selectedObject.sync="selectedBLInfoData['selectedPlaceOfDelivery']"
                    addLink="/Home/MasterFiles/OperationMaster/Port?new=1"
                    :canRefresh="true"
                    @refreshClicked="portOfLoadingInputQueryRefreshClicked"
                    validator="text,10"
                  />
                </div>
              </div>
              <div class="slot-content-column"> </div>
              <div class="slot-content-column"> </div>
            </div>
          </div>

          <!-- Documents Tab -->
          <div slot="Documents" :style="{ height: viewMode ? '100%' : 'calc(100% - 28px)' }">
            <div class="top-button-section" id="docPopup" v-if="!viewMode">
              <button @click="addDoc">
                <img src="https://dsmapi.logistics.systems/dsm6gicons/icons8-add-new-50.png" height="15" />
                Add Documents
              </button>
              <button @click="editDoc">
                <img src="https://dsmapi.logistics.systems/dsm6gicons/icons8-edit-64%20(1).png" height="15" />
                Edit Documents
              </button>
              <button @click="deleteDoc">
                <img src="https://dsmapi.logistics.systems/dsm6gicons/icons8-reduce-50.png" height="15" />
                Delete Documents
              </button>
            </div>

            <DocStatusUpdate
              :showPopup.sync="documentInfoData['updatePopup']"
              :statusList.sync="documentInfoData['statusList']"
              :docTypeList.sync="documentInfoData['docTypeList']"
              :docStatusList.sync="documentInfoData['docStatusList']"
              defaultCategory="document"
              @updateClicked="documentUpdate"
              :title="`Update Documents for Booking # ${selectedBookingInfoData['bookingNo']}`"
              :editDetailsList.sync="editDocumentsList"
            />

            <TableBox
              :tableData="selectedDocumentInfoData['docData']"
              groupBy="ID"
              :selectedRow.sync="selectedDocumentInfoData['selectedDoc']"
              :additionalButtons="viewDocumentButtons"
              @buttonClicked="viewDocumentClicked"
              title="Documents"
              :maxSelection="1"
              :hideColumns="['documentguid', 'statusseqno', 'ID', 'fileAtt', 'filetype', 'Action', 'Row #']"
            />

            <PopupV2
              :show="defaultPopup['show']"
              :type="defaultPopup['type']"
              :title="defaultPopup['title']"
              @buttonClicked="defaultPopupButtonClicked"
            >
              <div style="white-space: pre-wrap">{{ defaultPopup['content'] }}</div>
            </PopupV2>

            <PopupV2
              :show="excelContConfirm"
              type="info"
              title="Container List"
              @buttonClicked="excelContClicked"
              :buttons="contPreviewButtons"
              height="90%"
              width="90%"
            >
              We've detected {{ importContPreview.length }} containers from selected file.<br />Do you want to confirm
              these containers?
              <TableBox
                :style="{ height: `calc(100% - ${importHasError ? 251 : 233}px)` }"
                :tableData="importContPreview"
                :maxSelection="0"
                title="Containers"
                :loading="importContLoading"
              />
              <div v-if="importHasError"
                >There are 1 or more errors in the imported excel, please modify it in your excel first and
                re-upload.</div
              >
            </PopupV2>
          </div>

          <!-- TAS & KPI -->
          <div slot="TAS & KPI" style="height: calc(100% - 33px)">
            <StatusPage
              :isComponent="true"
              :statusData.sync="statusTaskData['statusTaskListing']"
              :statusJobNo="statusTaskData['statusJobNo']"
              :statusDocuments.sync="statusTaskData['statusDocumentListing']"
            />
          </div>
        </Tab>
      </div>
    </PopupEntry>

    <!-- #region Container Popup -->
    <PopupV2
      :show="cargoInfoContainerData['showContainerPopup']"
      title="Add Container Details"
      @buttonClicked="addEditContainerClicked"
      :buttons="cargoInfoContainerData['popupButtonList']"
    >
      <div class="container-popup-row">
        <Dropdown
          style="margin-right: 5px; width: 50%"
          placeholder="Please select"
          :isRequired="true"
          :items="cargoInfoContainerData['containerCategoryItems']"
          itemKey="desc"
          :selectedObject.sync="cargoInfoContainerData['selectedCategory']"
          :errorMessage.sync="cargoInfoContainerData['selectedCategoryError']"
          :isDisabled="cargoInfoContainerData['containerEditIndex'] >= 0"
          validator="text,40"
        />
        <div v-if="cargoInfoContainerData['selectedCategory']" style="display: flex; align-items: center; width: 100%">
          <Input
            v-if="cargoInfoContainerData['selectedCategory']['desc'] == 'Container Required'"
            style="margin-left: 5px"
            placeholder="Cont Quantity"
            :inputValue.sync="cargoInfoContainerData['contQty']"
            :isRequired="true"
            :errorMessage.sync="cargoInfoContainerData['contQtyError']"
            validator="positiveInteger,3"
          />
          <Input
            v-if="cargoInfoContainerData['selectedCategory']['desc'] == 'Cont/Seal #'"
            style="margin-left: 5px"
            placeholder="Container #"
            validator="text,20"
            :inputValue.sync="cargoInfoContainerData['contNo']"
            :isRequired="true"
            :errorMessage.sync="cargoInfoContainerData['contNoError']"
          />
          <Input
            v-if="cargoInfoContainerData['selectedCategory']['desc'] == 'Cont/Seal #'"
            style="margin-left: 5px"
            placeholder="Seal #"
            validator="text,20"
            :inputValue.sync="cargoInfoContainerData['sealNo']"
          />
        </div>
      </div>
      <div class="container-popup-row">
        <Dropdown
          :items="cargoInfoData['contSizeList']"
          placeholder="Size"
          :isRequired="true"
          itemKey="Size"
          :selectedObject.sync="cargoInfoContainerData['contSelectedSize']"
          :errorMessage.sync="cargoInfoContainerData['contSelectedSizeError']"
          validator="text,2"
        />
        <Dropdown
          :items="cargoInfoData['contTypeList']"
          style="margin-left: 5px"
          placeholder="Type"
          :isRequired="true"
          itemKey="Code - Desc"
          :selectedObject.sync="cargoInfoContainerData['contSelectedType']"
          :errorMessage.sync="cargoInfoContainerData['contSelectedTypeError']"
          validator="text,60"
        />
      </div>
      <div class="container-popup-row">
        <Dropdown
          :items="cargoInfoData['contStatusList']"
          placeholder="Status"
          itemKey="Cont Status"
          :selectedObject.sync="cargoInfoContainerData['contSelectedStatus']"
          validator="text,10"
        />
        <!-- <Input
          style="margin-left: 5px"
          placeholder="Weight (KG)"
          :inputValue.sync="cargoInfoContainerData['contWeight']"
          validator="positiveDecimal,5,4"
        /> -->
      </div>
      <InputQuery
        :items="cargoInfoData['contHandlingTypeList']"
        placeholder="Handling Type"
        itemKey="Handling Type"
        descKey="Description"
        :isRequired="true"
        :selectedObject.sync="cargoInfoContainerData['contSelectedHdgType']"
        :errorMessage.sync="cargoInfoContainerData['contSelectedHdgTypeError']"
        validator="text,5"
      />
      <!-- <InputQuery
        :items="cargoInfoData['contDeliveryTypeList']"
        placeholder="Delivery Type"
        itemKey="Delivery Type"
        descKey="Description"
        :isRequired="true"
        :selectedObject.sync="cargoInfoContainerData['contSelectedDelType']"
        :errorMessage.sync="cargoInfoContainerData['contSelectedDelTypeError']"
        validator="text,5"
      />
      <InputQuery
        :items="cargoInfoData['contSendToList']"
        placeholder="Send To"
        itemKey="Destination Code"
        descKey="Company"
        :selectedObject.sync="cargoInfoContainerData['contSelectedSendTo']"
        validator="text,10"
      /> -->
      <!-- <Dropdown
        :items="cargoInfoData['soccocTypeList']"
        itemKey="SOCCOC Type"
        placeholder="SOC/COC"
        :isRequired="true"
        :selectedObject.sync="cargoInfoContainerData['contSocCoc']"
        :errorMessage.sync="cargoInfoContainerData['contSocCocError']"
        validator="text,30"
      /> -->
      <RemarksBox :maxLength="200" style="height: 150px" :text.sync="cargoInfoContainerData['contRemarks']" />
    </PopupV2>
    <!-- #endregion -->

    <!-- #region Save Job Popup -->
    <PopupV2
      :show="showSavePopup"
      :type="savePopupType"
      :title="savePopupTitle"
      :buttons="savePopupButtons"
      @buttonClicked="savePopupButtonClicked"
    >
      <div v-if="saveAlertType == 'required' || saveAlertType == 'requiredCantSave'">
        <div v-if="saveAlertType == 'requiredCantSave'">
          In order to save a job, the field{{ saveErrorList.length > 1 ? 's' : '' }} below
          {{ saveErrorList.length > 1 ? 'are' : 'is' }} required!
        </div>
        <div class="save-content">
          <span v-for="(e, i) in saveErrorList" :key="i">{{ i + 1 }}. {{ e }}<br /></span>
        </div>
        <div v-if="saveAlertType == 'required'">Saving will make this an unconfirm job</div>
        <div v-if="saveAlertType == 'required'" style="margin-bottom: 5px"
          >Do you want to continue to save this job?</div
        >
      </div>
      <div
        v-if="saveAlertType == 'choiceMode' || saveAlertType == 'required'"
        :style="{ textAlign: 'left', minHeight: reasonOpen ? '200px' : '' }"
      >
        <RadioButtonSet
          v-if="saveAlertType == 'choiceMode'"
          title="Save Job as"
          :buttons="saveRadioButton"
          labelKey="label"
          :selected.sync="selectedSaveBy"
          :isRequired="true"
          :isDisabled="viewMode"
          :isHorizontal="true"
          style="margin-bottom: 5px"
        />
        <div v-if="!saveAsConfirm || saveAlertType == 'required'" style="padding-left: 10px">
          <Dropdown
            :isRequired="true"
            placeholder="Reason"
            :items="reasonsList"
            :selectedObject.sync="selectedUnconfirmReason"
            itemKey="Reason"
            @focusChanged="reasonFocusChanged"
            validator="text,50"
          />
          <Input
            :isRequired="selectedUnconfirmReason ? selectedUnconfirmReason['Reason'].includes('OTHERS') : false"
            placeholder="Remarks"
            :inputValue.sync="unconfirmRemarks"
          />
          <div class="popup-error">{{ popupErrorText }}</div>
        </div>
      </div>
    </PopupV2>
    <!-- #endregion -->

    <!--Attachment Window-->
    <div class="attchWin-main" v-if="openPopup">
      <button class="attchWin-close" @click="closePopup()">X</button>
      <div class="attchWin-display" v-if="attachmentList.length > 0">
        <div class="left">
          <div v-if="attachmentList.length > 1" class="leftButton">
            <button class="navButton-left" @click="navAtt('prev')">&lt;</button>
          </div>

          <!-- image container and buttons -->
          <div class="attchWin-attachment">
            <object
              v-if="attachmentIsProjectable()"
              :data="attToView['AttImage']"
              :type="attToView['AttImageType']"
              draggable="false"
              style="width: 100%; height: 100%"
            ></object>
            <div v-else class="attachment-not-projectable-container">
              <span>
                {{ getFileTypeLabel() }} files cannot be previewed.<br />
                Please download the file and view it instead.
              </span>
              <button @click="downloadAttachment">
                <img src="https://dsmlib.logistics.systems/Icons/Download.png" alt="Download icon" />
                Download
              </button>
            </div>
          </div>

          <div v-if="attachmentList.length > 1" class="rightButton">
            <button class="navButton-right" @click="navAtt('next')">></button>
          </div>
        </div>

        <div class="right">
          <div class="count">
            <b>{{ attToView['#'] }}</b> / {{ totalAtt }} Attachments
          </div>
          <div class="attchWin-info">
            <table id="infoHeader">
              <tr>
                <th>Job No</th>
                <td>:{{ attToView['Job No'] }}</td>
              </tr>

              <tr>
                <th>Doc Code</th>
                <td>:{{ selectedViewDocumentList['Doc Code'] }}</td>
              </tr>

              <tr>
                <th>Attachment</th>
                <td>:{{ selectedViewDocumentList['Attachment'] }}</td>
              </tr>
            </table>

            <table id="tableContent">
              <tr>
                <th>ID</th>
                <td>{{ selectedViewDocumentList['ID'] }}</td>
              </tr>

              <tr>
                <th>Remarks</th>
                <td>{{ selectedViewDocumentList['Remarks'] }}</td>
              </tr>

              <tr>
                <th>Status</th>
                <td>{{ selectedViewDocumentList['Status'] }}</td>
              </tr>

              <tr>
                <th>Start Date</th>
                <td>{{ selectedViewDocumentList['Start Date'] }}</td>
              </tr>

              <tr>
                <th>Received Date</th>
                <td>{{ selectedViewDocumentList['Received Date'] }}</td>
              </tr>

              <tr>
                <th>Due Date</th>
                <td>{{ selectedViewDocumentList['Due Date'] }}</td>
              </tr>

              <tr>
                <th>Assign To</th>
                <td>{{ selectedViewDocumentList['Assign To'] }}</td>
              </tr>

              <tr>
                <th>Sets</th>
                <td>{{ selectedViewDocumentList['Sets'] }}</td>
              </tr>

              <tr>
                <th>Page</th>
                <td>{{ selectedViewDocumentList['Page'] }}</td>
              </tr>
            </table>
          </div>
        </div>
      </div>

      <div v-else class="attchWin-display">
        <div style="margin: 0 auto">No Attachments Found</div>
      </div>
    </div>
    <!-- <div v-if="showPrintBLPopup" class="print-popup">
      <div class="title">House B/L</div>
      <DesignerV2
        :show.sync="showPrintBLPopup"
        :viewMode="true"
        :reportData="printBLDetails"
        reportFolder="HBLForm"
        :reportFile="designerV2ReportFile"
        :canShare="true"
      />
    </div> -->
  </div>
</template>

<script>
import StatusPage from '@/pages/Home/StatusPage.vue';
export default {
  components: {
    StatusPage,
  },
  data: function () {
    return {
      pageType: '',
      tableLoading: false,
      createdBy: '',
      updatedBy: '',

      showSavePopup: false,
      savePopupType: 'error',
      savePopupTitle: null,
      savePopupButtons: null,
      saveAlertType: '', // required | option
      saveErrorList: [],
      saveRadioButton: [
        { label: 'Confirm Job', keyValue: '1' },
        { label: 'Unconfirm Job', keyValue: '2' },
      ],
      selectedSaveBy: {
        keyValue: '1',
        label: 'Confirm Job',
      },
      popupErrorText: '',
      reasonOpen: false,
      selectedUnconfirmReason: null,
      unconfirmRemarks: '',

      sharingRadioButton: [
        { label: 'Yes', keyValue: '1' },
        { label: 'No', keyValue: '0' },
      ],
      insuranceRadioButton: [
        { label: 'Yes', keyValue: '1' },
        { label: 'No', keyValue: '0' },
      ],
      urgentRadioButton: [
        { label: 'Yes', keyValue: 'Y' },
        { label: 'No', keyValue: 'N' },
      ],

      menu: [
        {
          title: 'New Booking',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-add-new-50.png',
          enabled: false,
          key: 'MnuNew',
        },
        {
          title: 'Edit Booking',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-edit-64%20(1).png',
          enabled: false,
          key: 'MnuEdit',
        },
        {
          title: 'Delete Booking',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-reduce-50.png',
          enabled: false,
          key: 'MnuDelete',
        },
        { title: 'View Booking', icon: 'https://dsmlib.logistics.systems/Icons/icons8-view-64.png', enabled: false },
        { title: 'Duplicate', icon: 'https://dsmlib.logistics.systems/Icons/DuplicateJob.png', enabled: false },
        {
          title: 'Print Booking Confirmation',
          icon: 'https://dsmlib.logistics.systems/Icons/Print.png',
          enabled: true,
        },
        { title: 'Print House B/L', icon: 'https://dsmlib.logistics.systems/Icons/Print.png', enabled: true },
      ],
      currentTabIndex: 0,
      tabsItems: [
        { title: 'Quick Entry', disabled: false },
        { title: 'Booking Info', disabled: false },
        { title: 'Shipping Info', disabled: false },
        { title: 'Goods Received', disabled: false },
        { title: 'B/L Info', disabled: true },
        { title: 'Documents', disabled: false },
        { title: 'TAS & KPI', disabled: true },
      ],

      mainFilterIsOpen: false,
      dateFilterChecked: true,
      searchFilterChecked: false,
      filterText: '',
      searchFilterItems: [
        { 'Search Criteria': 'Booking #', '#': 1 },
        { 'Search Criteria': 'Job #', '#': 2 },
        { 'Search Criteria': 'BL No #', '#': 3 },
        { 'Search Criteria': 'Cargo Type', '#': 4 },
        { 'Search Criteria': 'Shipper/Consignee', '#': 5 },
        { 'Search Criteria': 'Billing Customer', '#': 6 },
        { 'Search Criteria': 'Project #', '#': 7 },
        { 'Search Criteria': 'Container #', '#': 8 },
        { 'Search Criteria': 'Transport Mode', '#': 9 },
        { 'Search Criteria': 'Customer Ref #', '#': 10 },
        { 'Search Criteria': 'Vessel Name', '#': 11 },
        { 'Search Criteria': 'Mother Vessel Name', '#': 12 },
        { 'Search Criteria': 'Vessel Booking Ref #', '#': 13 },
        { 'Search Criteria': 'User ID', '#': 14 },
        { 'Search Criteria': 'Container Type', '#': 15 },
        { 'Search Criteria': 'Goods Description', '#': 16 },
      ],
      searchCriteriaItems: [{ name: 'EQUALS' }, { name: 'STARTS WITH' }, { name: 'CONTAINS' }, { name: 'ENDS WITH' }],
      selectedCriteria: null,
      selectedSearchBy: null,
      criteriaDisabled: false,
      searchByValue: '',
      searchByError: '',
      searchByValueError: '',

      refreshStart: null,
      refreshEnd: null,
      lastRefreshed: '',

      jobsList: [],
      selectedFreightBooking: [],

      popupTitle: '',
      viewMode: false,
      showPopup: false,
      showDocUpload: false,

      eMessages: [],
      sMessages: [],
      rMessages: [],
      errorMessages: {
        transmode: '',
        cargotype: '',
        deliverytype: '',
        freightterms: '',
        shipper: '',
        bookingtime: '',
        pol: '',
        pod: '',
        ETATime: '',
        polETA: '',
        polETD: '',
        closingDate: '',
        dateofissue: '',
        podETA: '',
        fDestETA: '',
        jobtime: '',
        cargoreadinesstime: '',
        requireddate: '',
        fumigateon: '',
        ventilation: '',
        fumigator: '',
        location: '',
      },

      defaultPopup: {
        show: false,
        type: '',
        title: '',
        content: '',
      },

      deleteLayout: null,
      deleteAllBtn: null,
      deleteSelectedBtn: null,
      deleteMessage: '',
      deleteItems: [],

      editDeleteDoc: [],
      editAddDoc: [],
      editDocumentsList: [],
      documentsToDelete: [],
      popupError: false,

      editDetails: null,

      showShipperAddress: false,
      showConsigneeAddress: false,
      showNPartyAddress: false,

      shipperAddress: null,
      consigneeAddress: null,
      notifyPartyAddress: null,

      detailsEditTitle: '',
      detailsEditObject: null,
      edittingDetails: false,

      addressDetails: {
        postcodeList: [],
        stateList: [],
        countryList: [],
      },

      // Header info data and details
      bookingInfoData: {
        transModeList: [],
        cargoTypeList: [],
        deliveryTypeList: [],
        freightTermsList: [],
        cargoTermsList: [],
        currencyList: [],
        shipper: [],
        consignee: [],
        notifyParty: [],
        quotationNo: [],
        billTo: [],
      },

      selectedSharing: {
        // default 'No' on load
        keyValue: '0',
        label: 'No',
      },
      selectedInsurance: {
        // default 'No' on load
        keyValue: '0',
        label: 'No',
      },
      selectedUrgent: {
        // default 'No' on load
        keyValue: 'N',
        label: 'No',
      },

      // Status task tab
      statusTaskData: {
        statusTaskListing: [],
        statusDocumentListing: [],
        statusJobNo: '',
      },

      selectedBookingInfoData: {
        selectedTransMode: null,
        selectedCargoType: null,
        selectedDeliveryType: null,
        selectedFreightTerms: null,
        selectedCargoTerms: null,
        selectedCurrency: null,
        selectedPaymentTerms: null,
        selectedShipper: null,
        selectedConsignee: null,
        selectedNotifyParty: null,
        custRefNo: '',
        selectedBillTo: null,
        bookingNo: '',
        bookingDateTime: null,
        requestedBy: '',
        remarks: '',
        selectedQuotationNo: null,
        projectNo: '',
      },

      // Shipping info data and details
      shippingInfoData: {
        shipCallNoList: [],
        vesselIDList: [],
        shippingAgentList: [],
        shippingLineList: [],
        fwdgAgentList: [],
        portOperator: [],
        portOfLoading: [],
        portOfDischarge: [],
        finalDestination: [],
      },

      selectedShippingInfoData: {
        bookingRefNo: '',
        originalBLNo: '',
        selectedShipCall: null,
        shipCallNoValue: '',
        voyNo: null,
        selectedVesselID: null,
        selectedVesselName: null,
        vesselNameValue: '',
        selectedShippingAgent: null,
        selectedShippingLine: null,
        selectedPortLoading: null,
        selectedPortDischarge: null,
        selectedFinalDestination: null,
        isTranshipment: null,
        selectedMVesselID: null,
        mVoyNo: '',
        selectedPortDischarge2: null,
        ETATime: null,
        polETA: null,
        polETD: null,
        closingDate: null,
        podETA: null,
        fDestETA: null,
        selectedCarrierAgent: null,
        selectedFwdgAgent: null,
        selectedOverseaAgent: null,
        selectedPortOperator: null,
        selectedContOperator: null,
        selectedCoLoader: null,
        goodsDescription: '',
        shippingMarks: '',
      },
      selectedGoodsReceivedData: {
        jobNo: '',
        jobDateTime: null,
        isFumigation: false,
        selectedFumigator: null,
        selectedLocation: null,
        fumigateTime: null,
        ventilationTime: null,
        phytoMarking: '',
        cargoReadinessTime: null,
        requiredDate: null,
        cargoGoDown: '',
        subPkgList: [],
        selectedSubPkgs: [],
        saveAsJob: false,
      },
      selectedBLInfoData: {
        blNo: '',
        selectedPlaceOfReceipt: null,
        selectedPlaceOfDelivery: null,
        selectedPlaceOfIssue: null,
        dateOfIssue: null,
        saveBL: false,
      },

      // Cargo info data and details
      cargoInfoData: {
        commodityList: [],
        pkgTypeList: [],
      },

      selectedCargoInfoData: {
        gWeight: this.$weightFormat('0'), // default these to 0 because if saved empty it will be 0
        volume: this.$weightFormat('0'),
        chargeable: this.$weightFormat('0'),
        selectedCommodity: null,
        packageCount: null,
        selectedPackageType: null,
        contDetailsList: [],
        selectedContDetails: null,
        deletedContDetails: [],
      },

      cargoInfoContainerData: {
        showContainerPopup: false,
        containerCategoryItems: [{ desc: 'Container Required' }, { desc: 'Cont/Seal #' }],
        selectedCategory: null,
        selectedCategoryError: '',

        contQty: '',
        contQtyError: '',
        contNo: '',
        contNoError: '',
        sealNo: '',

        contSelectedSize: null,
        contSelectedSizeError: '',
        contSelectedType: null,
        contSelectedTypeError: '',

        contSelectedStatus: null,
        contWeight: '',

        contSelectedHdgType: null,
        contSelectedHdgTypeError: '',
        contSelectedDelType: null,
        contSelectedDelTypeError: '',
        contSelectedSendTo: null,
        contSocCoc: null,
        contSocCocError: '',
        contRemarks: '',

        popupButtonList: [{ text: 'OK' }],
        containerEditIndex: -1,
      },

      cargoPopupDetails: {
        cargoAddType: '',
        isEdit: false,
        editIndex: -1,
        editDetails: null,
      },

      // Document info data and details
      documentInfoData: {
        updatePopup: false,
        statusList: [],
        docTypeList: [],
        docStatusList: [],
      },
      selectedDocumentInfoData: {
        docData: [],
        selectedDoc: [],
      },

      excelContResolve: null,
      excelContConfirm: false,
      importContPreview: [],
      importContLoading: false,
      importHasError: false,

      //#endregion

      // input fields - tab array
      inputTabs: [
        { key: 'transmode', tab: 0 },
        { key: 'cargotype', tab: 0 },
        { key: 'deliverytype', tab: 0 },
        { key: 'freightterms', tab: 0 },
        { key: 'shipper', tab: 0 },
        { key: 'bookingtime', tab: 0 },
        { key: 'pol', tab: 0 },
        { key: 'pod', tab: 0 },
        { key: 'packagecount', tab: 1 },
        { key: 'pkgtype', tab: 1 },
        { key: 'billToInput', tab: 1 },
        { key: 'polETD', tab: 2 },
        { key: 'polETA', tab: 2 },
        { key: 'closingDate', tab: 2 },
        { key: 'podETA', tab: 2 },
        { key: 'fDestETA', tab: 2 },
        { key: 'jobtime', tab: 3 },
        { key: 'cargoreadinesstime', tab: 3 },
        { key: 'requireddate', tab: 3 },
        { key: 'fumigateon', tab: 3 },
        { key: 'ventilation', tab: 3 },
        { key: 'fumigator', tab: 3 },
        { key: 'location', tab: 3 },
        { key: 'dateofissue', tab: 4 },
      ],

      viewDocumentButtons: [{ text: 'View', index: 0, width: 60 }],
      attachmentList: [],
      openPopup: false,
      attToView: [],
      totalAtt: '',
      selectedViewDocumentList: '',

      smallDateTimeMinValue: new Date(2000, 0, 1),
      smallDateTimeMaxDate: new Date(2079, 0, 1),
      disableSaveAsJob: false,
      disableSaveBL: false,

      contSummary: '',

      // For vessel schedule sync
      hub: null,
      shipCallNoLoading: false,
      loginDetails: null,
    };
  },
  props: {},
  computed: {
    isSaveAsJobChecked: function () {
      return !this.selectedGoodsReceivedData['saveAsJob'];
    },
    isSaveBLChecked: function () {
      return !this.selectedBLInfoData['saveBL'];
    },
    isFumigationRequired: function () {
      // If Fumigation is checked, set as required fields. If not, clear fields and set not required
      return this.selectedGoodsReceivedData['isFumigation'] == '1';
    },
    isFumigationDisabled: function () {
      // If Fumigation is checked, enable fields
      if (this.viewMode == true) {
        return true;
      } else if (this.selectedGoodsReceivedData['isFumigation'] == '1') {
        return false;
      }
      return true;
    },
    isTranshipmentRequired: function () {
      // If Transhipment is checked, set as required fields. If not, clear fields and set not required
      return this.selectedShippingInfoData['isTranshipment'] == '1';
    },
    isTranshipmentDisabled: function () {
      // If Transhipment is checked, enable fields
      if (this.viewMode == true) {
        return true;
      } else if (this.selectedShippingInfoData['isTranshipment'] == '1') {
        return false;
      }
      return true;
    },
    currencyFormattedValue: function () {
      return this.$currencyFormat(this.selectedBookingInfoData.billToDetails.outstanding.value);
    },
    contPreviewButtons: function () {
      return [
        { text: `Confirm Containers`, disabled: this.importHasError || this.importContPreview.length <= 0 },
        { text: 'Cancel', color: 'red', textColor: 'white' },
      ];
    },
    saveAsConfirm: function () {
      return this.selectedSaveBy?.keyValue === '1';
    },
    safeShipCallNoValue: {
      // Fixes issue where `shipCallNoValue` being undefined caused dropdown to show no items on click.
      get() {
        return this.selectedShippingInfoData['shipCallNoValue'] || '';
      },
      set(val) {
        this.selectedShippingInfoData['shipCallNoValue'] = val;
      },
    },
  },
  methods: {
    menuClicked: async function (item) {
      this.viewMode = false;
      this.mainFilterIsOpen = false;

      if (!item.enabled) {
        // this.$alert.showAlert('Access Denied', `You have no permission for '${item['title']}'`, 'error');
        this.$showAlert('error', 'Access Denied', `You have no permission for '${item['title']}'`);
        return;
      }

      switch (item.title) {
        case 'New Booking': {
          // this.$fullscreenLoading('Getting defaults');
          // const defaults = await this.$apiCallV2(`/FreightForwarding/GetJobDefaults?jobType=${this.pageType[0]}`);

          this.$fullscreenLoading();

          this.showPopup = true;
          this.selectedBookingInfoData['bookingNo'] = '**********';
          this.selectedGoodsReceivedData['jobNo'] = '**********';
          this.selectedBLInfoData['blNo'] = '**********';
          //this.selectedBookingInfoData['jobStatus'] = 'New';

          this.popupTitle = `New Booking`;
          this.createdBy = '';
          this.updatedBy = '';

          this.selectedSharing = this.sharingRadioButton.find((r) => r.keyValue == '0');
          this.selectedInsurance = this.insuranceRadioButton.find((r) => r.keyValue == '0');
          this.selectedUrgent = this.urgentRadioButton.find((r) => r.keyValue == 'N');

          // if (defaults['DefaultTransMode']) {
          //   const selectedTransMode = this.bookingInfoData['transMode'].find(
          //     (item) => item.Description.toLowerCase() === defaults['DefaultTransMode'].toLowerCase(),
          //   );
          //   if (selectedTransMode) {
          //     this.selectedBookingInfoData['selectedTransMode'] = selectedTransMode;
          //   }
          // }

          // if (defaults['DefaultCargoType']) {
          //   this.selectedBookingInfoData['selectedCargoType'] = this.bookingInfoData['cargoType'].getSelected(
          //     defaults['DefaultCargoType'],
          //     'Cargo Type',
          //   );
          // }

          // if (defaults['DefaultCommodity']) {
          //   this.selectedCargoInfoData['selectedCommodity'] = this.cargoInfoData['commodityList'].getSelected(
          //     defaults['DefaultCommodity'],
          //     'Commodity Code',
          //   );
          // }

          // if (defaults['ForwardingAgent']) {
          //   this.selectedShippingInfoData['selectedFwdgAgent'] = this.shippingInfoData['fwdgAgentList'].filter((a) => {
          //     return a['FA Code'].trim() == defaults['ForwardingAgent'].trim();
          //   })[0];
          // }

          // if (defaults['DefaultPort']) {
          //   if (this.pageType == 'Import') {
          //     this.selectedShippingInfoData['selectedPortDischarge'] = this.shippingInfoData[
          //       'portOfDischarge'
          //     ].getSelected(defaults['DefaultPort'], 'Port Code');
          //   } else {
          //     this.selectedShippingInfoData['selectedPortLoading'] = this.shippingInfoData['portOfLoading'].getSelected(
          //       defaults['DefaultPort'],
          //       'Port Code',
          //     );
          //   }
          // }

          break;
        }
        case 'Edit Booking': {
          if (this.selectedFreightBooking.length == 0) {
            this.$showAlert('error', 'No Booking Selected', 'Please select a record to edit');
            return;
          }

          this.popupTitle = `Edit Booking`;

          await this.viewEditClicked('e');
          this.showPopup = true;

          break;
        }
        case 'Delete Booking':
          if (this.selectedFreightBooking.length == 0) {
            this.$showAlert('error', 'No Booking Selected', `Please select a record to delete`);
          } else {
            this.deletePopupButtonClicked();
          }
          break;
        case 'View Booking':
          if (this.selectedFreightBooking.length == 0) {
            this.$showAlert('error', 'No Booking Selected', 'Please select a record to view');
            return;
          }

          this.viewMode = true;

          this.popupTitle = `View Booking`;

          await this.viewEditClicked('v');
          this.showPopup = true;
          break;
        case 'Duplicate':
          if (this.selectedFreightBooking.length == 0) {
            this.$showAlert('error', 'No Booking Selected', 'Please select a record to duplicate');
          } else {
            const duplicateCont = await this.$showAlert(
              'info',
              'Duplicate Containers',
              'Do you want to duplicate the container details as well?',
              [
                { text: 'Duplicate Containers' },
                { text: 'Do not Duplicate Containers', color: 'gray', textColor: 'white' },
              ],
            );

            this.showPopup = true;
            this.popupTitle = `New Booking`;

            this.createdBy = '';
            this.updatedBy = '';

            this.viewEditClicked('e', true, duplicateCont == 'Duplicate Containers');
          }
          break;
        case 'Print Booking Confirmation':
          if (this.selectedFreightBooking.length == 0) {
            const route = this.$router.resolve(
              `/Home/${this.$route.params['user']}/FreightForwarding/Freight%20Booking%20Confirmation?jobtype=E`,
            );
            window.open(route.href, '_blank');
          } else {
            const route = this.$router.resolve(
              `/Home/${this.$route.params['user']}/FreightForwarding/Freight%20Booking%20Confirmation?jobtype=E&bookingno=${this.selectedFreightBooking[0]['Booking No'].trim()}`,
            );
            window.open(route.href, '_blank');
          }
          break;
        case 'Print House B/L':
          if (this.selectedFreightBooking.length == 0) {
            const route = this.$router.resolve(
              `/Home/${this.$route.params['user']}/FreightForwarding/Print%20House%20BL`,
            );
            window.open(route.href, '_blank');
          } else {
            if (this.selectedFreightBooking[0]['Job No'] == '') {
              this.$showAlert(
                'error',
                'Record Invalid',
                'Selected booking does not have a Job Number. \n\n Please save as job under "Goods Received" tab before printing House B/L.',
              );
              return;
            }
            const route = this.$router.resolve(
              `/Home/${this.$route.params['user']}/FreightForwarding/Print%20House%20BL?bookingno=${this.selectedFreightBooking[0]['Booking No'].trim()}`,
            );
            window.open(route.href, '_blank');
          }
          break;
      }
    },

    refreshClicked: async function () {
      this.jobsList = [];
      this.searchByError = '';
      this.searchByValueError = '';
      this.showDocUpload = false;

      if (this.searchFilterChecked && (!this.selectedSearchBy || !this.searchByValue)) {
        this.searchByError = !this.selectedSearchBy ? 'Please select your search criteria' : '';
        this.searchByValueError = !this.searchByValue ? 'Please enter your search value' : '';
        return;
      }

      const dataToPost = {
        stjobdate: this.dateFilterChecked ? new Date(this.refreshStart).formatString('yyyy-MM-dd') : '',
        edjobdate: this.dateFilterChecked ? new Date(this.refreshEnd).formatString('yyyy-MM-dd') : '',
        jobtype: 'E',
        searchfield: this.searchFilterChecked ? this.selectedSearchBy['Search Criteria'] : '',
        searchcriteria: this.searchFilterChecked ? this.selectedCriteria['name'] : '',
        searchvalue: this.searchFilterChecked ? this.searchByValue : '',
      };

      if (this.dateFilterChecked && this.searchFilterChecked) {
        this.filterText = `Filter by ${this.selectedSearchBy['Search Criteria']} ${this.selectedCriteria['name']} ${this.searchByValue}, from ${new Date(this.refreshStart).formatString('yyyy-MM-dd')} to ${new Date(this.refreshEnd).formatString('yyyy-MM-dd')}`;
      } else if (this.dateFilterChecked && !this.searchFilterChecked) {
        this.filterText = `Filter by date from ${new Date(this.refreshStart).formatString('yyyy-MM-dd')} to ${new Date(this.refreshEnd).formatString('yyyy-MM-dd')}`;
      } else if (!this.dateFilterChecked && this.searchFilterChecked) {
        this.filterText = `Filter by ${this.selectedSearchBy['Search Criteria']} ${this.selectedCriteria['name']} ${this.searchByValue}`;
      }

      this.$fullscreenLoading('');
      this.jobsList = await this.$apiCallV2(`/FreightBooking/GetFreightBookingListing`, dataToPost);
      this.$fullscreenLoading();

      this.lastRefreshed = new Date().formatString('yyyy-MM-dd HH:mm');
      this.mainFilterIsOpen = false;
    },
    searchBySelected: function (e) {
      if (e) {
        if (e['Search Criteria'] == 'Required Date') {
          this.selectedCriteria = this.searchCriteriaItems[0];
          this.criteriaDisabled = true;
        } else {
          this.criteriaDisabled = false;
        }
      }
    },
    viewEditClicked: async function (type, duplicate, duplicateCont) {
      this.$fullscreenLoading('Getting Details');
      const getJobDetails = await this.$apiCallV2('/Master/GetDetailsMaster', {
        masterfile: 'FREIGHTBOOKING',
        recordid:
          type == 'save' ?
            this.selectedBookingInfoData['bookingNo']
          : this.selectedFreightBooking[0]['Booking No'].trim(),
      });
      const details = getJobDetails['Booking'];
      const jobDetails = getJobDetails['Booking']['Job'];
      const blDetails = getJobDetails['Booking']['Job']?.['BL'] || null;
      this.editDetails = details;

      this.selectedGoodsReceivedData['jobNo'] = '**********'; // default value to prevent empty. if have value it will be replaced later
      this.selectedBLInfoData['blNo'] = '**********';

      if (!duplicate) {
        // if not duplicating record
        this.popupTitle += ` ${this.selectedFreightBooking[0]['Booking No'].trim()}`;
        this.updatedBy = `${details['User ID'] ? details['User ID'].trim() : ''} ${details['Date Updated'] ? new Date(new Date(details['Date Updated'])).formatString('dd/MM/yyyy HH:mm') : ''}`;
        if (jobDetails) {
          this.popupTitle += `, Job  ${jobDetails['Job No'].trim()}`;
          this.createdBy = `${details['Created By'] ? details['Created By'].trim() : ''} ${details['Created Date'] ? new Date(new Date(details['Created Date'])).formatString('dd/MM/yyyy HH:mm') : ''}`;
        } else {
          this.createdBy = ''; // no job created yet, so no created by
        }
        if (blDetails) {
          this.popupTitle += `, BL  ${blDetails['BL No'].trim()}`;
        }
        this.contSummary = details['Total Cont'] ? details['Total Cont'] : '';

        //documents
        this.$fullscreenLoading('Getting Documents');
        const jobDocuments = await this.$apiCallV2(
          `/FreightForwarding/GetJobDocuments?jobno=${this.selectedFreightBooking[0]['Booking No'].trim()}`,
        );
        this.statusTaskData['statusDocumentListing'] = jobDocuments;
        jobDocuments.forEach((d, index) => {
          // let due = d['Due DateTime'] ? `${d['Due DateTime'].substring(0, 13)}${d['Due DateTime'].substring(13, 15)}` : '';
          const due = d['Due DateTime'] ? d['Due DateTime'] : '';
          // let received = d['Received DateTime'] ? `${d['Received DateTime'].substring(0, 13)}${d['Received DateTime'].substring(13, 15)}` : '';
          const received = d['Received DateTime'] ? d['Received DateTime'] : '';
          const start = d['Start DateTime'] ? d['Start DateTime'] : '';

          this.selectedDocumentInfoData['docData'].push({
            'Doc Code': d['Doc Code'],
            'Doc Ref #': d['Reference No'],
            Attachment: d['AttImageName'],
            Remarks: d['Remarks'],
            Status: d['Status Count'] > 0 ? d['Status Code'] : 'N/A',
            'Start Date': d['Status Count'] > 0 ? start : 'N/A',
            'Received Date': d['Status Count'] > 0 ? received : 'N/A',
            'Due Date': d['Status Count'] > 0 ? due : 'N/A',
            'Assign To': d['Status Count'] > 0 ? d['Assign To'] : 'N/A',
            Sets: d['Status Count'] > 0 ? d['Sets'] : 'N/A',
            Page: d['Status Count'] > 0 ? d['Pages'] : 'N/A',

            documentguid: d['DocumentGUID'],
            statusseqno: '',
            ID: `${d['Doc Code']}-${d['Reference No']}`,
            fileAtt: d['AttImage'],
            filetype: d['AttImageType'],
            Action: '',
            'Row #': index, //id for image viewing
          });
        });
      }

      //#region Booking Info
      //console.log(getJobDetails);
      this.selectedBookingInfoData['selectedTransMode'] =
        details['Transport Mode'] ?
          this.bookingInfoData['transModeList'].getSelected(details['Transport Mode'], 'Trans Mode')
        : null;
      this.selectedBookingInfoData['selectedCargoType'] =
        details['Cargo Type'] ?
          this.bookingInfoData['cargoTypeList'].getSelected(details['Cargo Type'], 'Cargo Type')
        : null;
      this.selectedBookingInfoData['selectedDeliveryType'] =
        details['Delivery Type'] ?
          this.bookingInfoData['deliveryTypeList'].getSelected(details['Delivery Type'], 'Delivery Type')
        : null;
      this.selectedBookingInfoData['selectedFreightTerms'] =
        details['Freight Terms'] ?
          this.bookingInfoData['freightTermsList'].getSelected(details['Freight Terms'], 'Freight Type')
        : null;
      this.selectedBookingInfoData['selectedCargoTerms'] =
        details['Cargo Terms'] ?
          this.bookingInfoData['cargoTermsList'].getSelected(details['Cargo Terms'], 'Origin Code')
        : null;
      this.selectedBookingInfoData['selectedCurrency'] =
        details['Currency'] ?
          this.bookingInfoData['currencyList'].getSelected(details['Currency'], 'Currency Code')
        : null;
      this.selectedBookingInfoData['selectedPaymentTerms'] =
        details['Payment Terms'] ?
          this.bookingInfoData['paymentTermsList'].getSelected(details['Payment Terms'], 'Payment Type')
        : null;

      this.selectedSharing = this.sharingRadioButton.find((r) => r.keyValue == details['Container Sharing']);
      this.selectedInsurance = this.insuranceRadioButton.find((r) => r.keyValue == details['Insurance']);

      this.selectedBookingInfoData['requestedBy'] = details['Requested By'] ? details['Requested By'] : '';
      this.selectedBookingInfoData['remarks'] = details['Remarks'] ? details['Remarks'] : '';

      this.selectedBookingInfoData['selectedShipper'] =
        details['Shipper'] ? this.bookingInfoData['shipper'].getSelected(details['Shipper'], 'Shipper Code') : null;
      this.selectedBookingInfoData['selectedConsignee'] =
        details['Consignee'] ?
          this.bookingInfoData['consignee'].getSelected(details['Consignee'], 'Consignee Code')
        : null;
      this.selectedBookingInfoData['selectedNotifyParty'] =
        details['Notify Party'] ?
          this.bookingInfoData['notifyParty'].getSelected(details['Notify Party'], 'Notify Party Code')
        : null;
      this.selectedBookingInfoData['custRefNo'] = details['Cust Ref No'] ? details['Cust Ref No'].trim() : '';
      this.selectedBookingInfoData['projectNo'] = details['Project #'] ? details['Project #'].trim() : '';
      this.selectedBookingInfoData['selectedQuotationNo'] =
        details['Quotation #'] ?
          this.bookingInfoData['quotationNo'].getSelected(details['Quotation #'], 'Quotation No')
        : null;
      this.selectedBookingInfoData['selectedBillTo'] =
        details['Bill To'] ? this.bookingInfoData['billTo'].getSelected(details['Bill To'], 'Customer Code') : null;

      //if duplicate, different booking no and booking date
      this.selectedBookingInfoData['bookingNo'] =
        duplicate ? '**********'
        : details['Booking No'] ? details['Booking No'].trim()
        : '';
      this.selectedBookingInfoData['bookingDateTime'] =
        duplicate ? new Date()
        : details['Booking Date'] ? new Date(details['Booking Date'])
        : null;

      this.selectedCargoInfoData['gWeight'] =
        details['Gross Weight'] ? this.formatWeightValue(details['Gross Weight']) : this.$weightFormat('0');
      this.selectedCargoInfoData['volume'] =
        details['Volume'] ? this.formatWeightValue(details['Volume']) : this.$weightFormat('0');
      this.selectedCargoInfoData['chargeable'] =
        details['Chargeable'] ? this.formatWeightValue(details['Chargeable']) : this.$weightFormat('0');

      this.selectedCargoInfoData['selectedCommodity'] =
        details['Commodity Code'] ?
          this.cargoInfoData['commodityList'].getSelected(details['Commodity Code'], 'Commodity Code')
        : null;
      this.selectedCargoInfoData['packageCount'] =
        details['Total Packages'] ? String(parseInt(details['Total Packages'])) : '';
      this.selectedCargoInfoData['selectedPackageType'] =
        details['Package Code'] ?
          this.cargoInfoData['pkgTypeList'].getSelected(details['Package Code'], 'Package Code')
        : null;

      if (this.selectedBookingInfoData['selectedBillTo']) {
        this.billToSelected(this.selectedBookingInfoData['selectedBillTo']);
      }
      //#endregion

      //#region Shipping Info
      this.selectedShippingInfoData['bookingRefNo'] = details['Booking Ref No'] ? details['Booking Ref No'].trim() : '';
      this.selectedShippingInfoData['originalBLNo'] =
        details['No. of Original BLs'] ? details['No. of Original BLs'].trim() : '';
      this.selectedShippingInfoData['selectedShipCall'] =
        details['Ship Call No'] ?
          this.shippingInfoData['shipCallNoList'].getSelected(details['Ship Call No'], 'Ship Call No')
        : null;
      if (details['Ship Call No'] && !this.selectedShippingInfoData['selectedShipCall']) {
        this.selectedShippingInfoData['shipCallNoValue'] = details['Ship Call No'];
      }
      this.selectedShippingInfoData['voyNo'] = details['Voyage No'] ? details['Voyage No'] : null;
      this.selectedShippingInfoData['selectedVesselID'] =
        details['Vessel ID'] ?
          this.shippingInfoData['vesselIDList'].getSelected(details['Vessel ID'], 'Vessel ID')
        : null;
      if (details['Vessel Name'] !== '') {
        const vesselMatch = this.shippingInfoData['vesselIDList'].getSelected(details['Vessel Name'], 'Vessel Name');
        this.selectedShippingInfoData['selectedVesselName'] =
          vesselMatch ?
            vesselMatch // If match found, assign object
          : { 'Vessel Name': details['Vessel Name'] }; // If no match, assign free text - wrap in object to match structure
      } else {
        this.selectedShippingInfoData['selectedVesselName'] = null;
      }
      this.selectedShippingInfoData['selectedShippingAgent'] =
        details['Shipping Agent'] ?
          this.shippingInfoData['shippingAgentList'].getSelected(details['Shipping Agent'], 'Agent Code')
        : null;
      this.selectedShippingInfoData['selectedShippingLine'] =
        details['Shipping Line'] ?
          this.shippingInfoData['shippingLineList'].getSelected(details['Shipping Line'], 'Agent Code')
        : null;

      this.selectedShippingInfoData['selectedPortLoading'] =
        details['Port of Loading'] ?
          this.shippingInfoData['portOfLoading'].getSelected(details['Port of Loading'], 'Port Code')
        : null;

      this.selectedShippingInfoData['selectedPortDischarge'] =
        details['Port of Discharge'] ?
          this.shippingInfoData['portOfDischarge'].getSelected(details['Port of Discharge'], 'Port Code')
        : null;
      this.selectedShippingInfoData['selectedFinalDestination'] =
        details['Final Destination'] ?
          this.shippingInfoData['finalDestination'].getSelected(details['Final Destination'], 'Port Code')
        : null;

      this.selectedShippingInfoData['isTranshipment'] = details['Transshipment'] == '1';
      this.selectedShippingInfoData['selectedMVesselID'] =
        details['Mother Vessel ID'] ?
          this.shippingInfoData['vesselIDList'].getSelected(details['Mother Vessel ID'], 'Vessel ID')
        : null;
      this.selectedShippingInfoData['mVoyNo'] = details['Mother Voyage No'] ? details['Mother Voyage No'] : '';
      this.selectedShippingInfoData['selectedPortDischarge2'] =
        details['Transhipment Port'] ?
          this.shippingInfoData['portOfDischarge'].getSelected(details['Transhipment Port'], 'Port Code')
        : null;
      this.selectedShippingInfoData['ETATime'] = details['POT ETD'] ? new Date(details['POT ETD']) : null;

      this.selectedShippingInfoData['polETA'] = details['POL ETA'] ? new Date(details['POL ETA']) : null;
      this.selectedShippingInfoData['polETD'] = details['POL ETD'] ? new Date(details['POL ETD']) : null;
      this.selectedShippingInfoData['closingDate'] = details['Closing Date'] ? new Date(details['Closing Date']) : null;
      this.selectedShippingInfoData['podETA'] = details['POD ETA'] ? new Date(details['POD ETA']) : null;
      this.selectedShippingInfoData['fDestETA'] =
        details['Final Destination ETA'] ? new Date(details['Final Destination ETA']) : null;
      this.selectedShippingInfoData['selectedCarrierAgent'] =
        details['Carrier Agent'] ?
          this.shippingInfoData['coloaderList'].getSelected(details['Carrier Agent'], 'Agent Code')
        : null;

      this.selectedShippingInfoData['selectedFwdgAgent'] =
        details['Forwarding Agent'] ?
          this.shippingInfoData['fwdgAgentList'].getSelected(details['Forwarding Agent'], 'FA Code')
        : null;
      this.selectedShippingInfoData['selectedOverseaAgent'] =
        details['Oversea Agent'] ?
          this.shippingInfoData['overseaAgentList'].getSelected(details['Oversea Agent'], 'Agent Code')
        : null;
      this.selectedShippingInfoData['selectedPortOperator'] =
        details['Port Operator'] ?
          this.shippingInfoData['portOperator'].getSelected(details['Port Operator'], 'Operator Code')
        : null;
      this.selectedShippingInfoData['selectedContOperator'] =
        details['Cont Operator'] ? details['Cont Operator'].trim() : '';
      this.selectedShippingInfoData['selectedCoLoader'] =
        details['Co-loader'] ?
          this.shippingInfoData['coloaderList'].getSelected(details['Co-loader'], 'Agent Code')
        : null;

      this.selectedShippingInfoData['goodsDescription'] =
        details['GoodsDesc'] ? details['GoodsDesc'].map((g) => g['GoodsDescription']).join('\n') : '';
      this.selectedShippingInfoData['shippingMarks'] =
        details['ShipMarks'] ? details['ShipMarks'].map((g) => g['ShipMark']).join('\n') : '';
      //#endregion

      if (!duplicate || duplicateCont) {
        if (details['ContDetail']) {
          details['ContDetail'].forEach((c) => {
            this.selectedCargoInfoData['contDetailsList'].push({
              'Cont #': c['Container No'] ? c['Container No'] : '',
              'Old Cont #': c['Container No'] ? c['Container No'] : '',
              'Seal No': c['Seal No'] ? c['Seal No'] : '',
              'Cont Size': c['Size'] ? c['Size'] : '',
              'Old Cont Size': c['Size'] ? c['Size'] : '',
              'Cont Type': c['Cont Type'] ? c['Cont Type'] : '',
              'Old Cont Type': c['Cont Type'] ? c['Cont Type'] : '',
              'Cont Weight': c['Cont Weight'] ? this.$weightFormat(c['Cont Weight']) : '',
              Status:
                c['Cont Status'] ?
                  (this.cargoInfoData['contStatusList'].getSelected(c['Cont Status'], '#')?.['Cont Status'] ?? '')
                : '',
              'Handling Type': c['Handling Type'] ? c['Handling Type'] : '',
              'Old Handling Type': c['Handling Type'] ? c['Handling Type'] : '',
              'Delivery Type': c['Delivery Type'] ? c['Delivery Type'] : '',
              'Old Delivery Type': c['Delivery Type'] ? c['Delivery Type'] : '',
              'SOC/COC': c['Cont Soc Coc'] ? c['Cont Soc Coc'] : '',
              'Send To': c['Send To'] ? c['Send To'] : '',
              Remarks: c['Remarks'] ? c['Remarks'] : '',
              'ROT No': c['ROT No'] ? c['ROT No'] : '',
              ContCharges: c['ContCharges'] ? c['ContCharges'] : '',
              Invoices: c['Invoices'] ? c['Invoices'] : '',
              item_action: '',
            });
          });
        }
      }

      if (details['Shipper']) {
        this.shipperAddress = {};
        this.shipperAddress['name'] = details['Shipper Name1'];
        this.shipperAddress['add1'] = details['Shipper Add1'];
        this.shipperAddress['add2'] = details['Shipper Add2'];
        this.shipperAddress['add3'] = details['Shipper Add3'];
        this.shipperAddress['add4'] = details['Shipper Add4'];
        this.shipperAddress['postcode'] = details['Shipper PostCode'];
        this.shipperAddress['city'] = details['Shipper City'];
        this.shipperAddress['state'] = details['Shipper State'];
        this.shipperAddress['country'] = details['Shipper Country'];
        this.shipperAddress['attn'] = details['Shipper Attn'];
        this.shipperAddress['tel'] = details['Shipper Tel'];
        this.shipperAddress['fax'] = details['Shipper Fax'];
        this.shipperAddress['gst'] = details['Shipper GSTNo'];
      } else {
        this.shipperAddress = null;
      }

      if (details['Consignee']) {
        this.consigneeAddress = {};
        this.consigneeAddress['name'] = details['Consignee Name1'];
        this.consigneeAddress['add1'] = details['Consignee Add1'];
        this.consigneeAddress['add2'] = details['Consignee Add2'];
        this.consigneeAddress['add3'] = details['Consignee Add3'];
        this.consigneeAddress['add4'] = details['Consignee Add4'];
        this.consigneeAddress['postcode'] = details['Consignee PostCode'];
        this.consigneeAddress['city'] = details['Consignee City'];
        this.consigneeAddress['state'] = details['Consignee State'];
        this.consigneeAddress['country'] = details['Consignee Country'];
        this.consigneeAddress['attn'] = details['Consignee Attn'];
        this.consigneeAddress['tel'] = details['Consignee Tel'];
        this.consigneeAddress['fax'] = details['Consignee Fax'];
        this.consigneeAddress['gst'] = details['Consignee GSTNo'];
      } else {
        this.consigneeAddress = null;
      }

      if (details['Notify Party']) {
        this.notifyPartyAddress = {};
        this.notifyPartyAddress['name'] = details['NParty Name1'];
        this.notifyPartyAddress['add1'] = details['NParty Add1'];
        this.notifyPartyAddress['add2'] = details['NParty Add2'];
        this.notifyPartyAddress['add3'] = details['NParty Add3'];
        this.notifyPartyAddress['add4'] = details['NParty Add4'];
        this.notifyPartyAddress['postcode'] = details['NParty PostCode'];
        this.notifyPartyAddress['city'] = details['NParty City'];
        this.notifyPartyAddress['state'] = details['NParty State'];
        this.notifyPartyAddress['country'] = details['NParty Country'];
        this.notifyPartyAddress['attn'] = details['NParty Attn'];
        this.notifyPartyAddress['tel'] = details['NParty Tel'];
        this.notifyPartyAddress['fax'] = details['NParty Fax'];
        this.notifyPartyAddress['gst'] = details['NParty GSTNo'];
      } else {
        this.notifyPartyAddress = null;
      }
      // Goods Received
      //console.log(jobDetails);
      if (jobDetails) {
        this.disableSaveAsJob = true; // disable checkbox if job details are available (already saved as job)
        this.selectedGoodsReceivedData['saveAsJob'] = true;
        this.selectedGoodsReceivedData['jobNo'] =
          duplicate ? '**********'
          : jobDetails['Job No'] ? jobDetails['Job No']
          : '';
        this.selectedUrgent = this.urgentRadioButton.find((r) => r.keyValue == jobDetails['Urgent']);
        this.selectedGoodsReceivedData['jobDateTime'] =
          jobDetails['Job Date'] ? new Date(jobDetails['Job Date']) : null;
        this.selectedGoodsReceivedData['cargoReadinessTime'] =
          jobDetails['Cargo Readiness Time'] ? new Date(jobDetails['Cargo Readiness Time']) : null;
        this.selectedGoodsReceivedData['requiredDate'] =
          jobDetails['Required Date'] ? new Date(jobDetails['Required Date']) : null;
        this.selectedGoodsReceivedData['cargoGoDown'] = jobDetails['Cargo Go Down'] ? jobDetails['Cargo Go Down'] : '';
        this.selectedGoodsReceivedData['isFumigation'] = jobDetails['Fumigation'] == '1';
        if (jobDetails['Fumigation'] == '1') {
          this.selectedGoodsReceivedData['selectedFumigator'] = this.shippingInfoData['fumigatorList'].getSelected(
            jobDetails['Fumigator'],
            'Supplier Code',
          );
          this.selectedGoodsReceivedData['selectedLocation'] = this.shippingInfoData[
            'fumigatorLocationList'
          ].getSelected(jobDetails['Fumigate At'], 'Destination Code');
          this.selectedGoodsReceivedData['fumigateTime'] = new Date(jobDetails['Fumigation Time']);
          this.selectedGoodsReceivedData['ventilationTime'] = new Date(jobDetails['Ventilation Time']);
          // unsure why it was split before, removed the time.
          // const [year1, month1, day1] = jobDetails['Fumigation Time'].split('-').map((datePart) => parseInt(datePart));
          // this.selectedGoodsReceivedData['fumigateTime'] = new Date(year1, month1 - 1, day1);
          // const [year2, month2, day2] = jobDetails['Ventilation Time'].split('-').map((datePart) => parseInt(datePart));
          // this.selectedGoodsReceivedData['ventilationTime'] = new Date(year2, month2 - 1, day2);
          this.selectedGoodsReceivedData['phytoMarking'] = jobDetails['Phytomarking'] ? jobDetails['Phytomarking'] : '';
        } else {
          this.selectedShippingInfoData['fumigateTime'] = null;
          this.selectedShippingInfoData['ventilationTime'] = null;
        }

        if (!duplicate) {
          if (jobDetails['PackingList']) {
            let packages = [];
            if (Array.isArray(jobDetails['PackingList'])) {
              // Has multiple sub package
              packages = jobDetails['PackingList'];
            } else {
              // Has one sub package
              packages = [jobDetails['PackingList']];
            }

            packages.forEach((p) => {
              const TotalGrossKG =
                p['Total Gross KG'] ? this.$weightFormat(p['Total Gross KG']) : this.$weightFormat('0');
              const PerPkgKG = p['Per Pkg KG'] ? this.$weightFormat(p['Per Pkg KG']) : this.$weightFormat('0');
              const TotalNettKG = p['Total Nett KG'] ? this.$weightFormat(p['Total Nett KG']) : this.$weightFormat('0');
              const PerPkgNettKG =
                p['Per Pkg Nett KG'] ? this.$weightFormat(p['Per Pkg Nett KG']) : this.$weightFormat('0');
              const TotalVolM3 = p['Total Vol M3'] ? this.$weightFormat(p['Total Vol M3']) : this.$weightFormat('0');
              const PerPkgM3 = p['Per Pkg M3'] ? this.$weightFormat(p['Per Pkg M3']) : this.$weightFormat('0');

              const Length = p['Length'] ? this.$weightFormat(p['Length']) : this.$weightFormat('0');
              const Width = p['Width'] ? this.$weightFormat(p['Width']) : this.$weightFormat('0');
              const Height = p['Height'] ? this.$weightFormat(p['Height']) : this.$weightFormat('0');

              this.selectedGoodsReceivedData['subPkgList'].push({
                'Package Quantity': p['SubPackages Qty'],
                'Package Type': p['SubPackage Code'] ? p['SubPackage Code'] : '',
                Dimensions: `${Length}m X ${Width}m X ${Height}m`,
                'Gross Weight': `${TotalGrossKG}KG (${PerPkgKG}KG)`,
                'Net Weight': `${TotalNettKG}KG (${PerPkgNettKG}KG)`,
                Volume: `${TotalVolM3}M³ (${PerPkgM3}M³)`,
                'Item Code': p['Item Code'] ? p['Item Code'] : '',
                'Packing List #': p['Packing List No'] || '',
                'Invoice #': p['Invoice No'] || '',
                'Container #': p['Container No'] || '',
                Remarks: p['Remarks'] || '',
                item_action: '',
                seq_no: p['SeqNo'] || '',
              });
            });
          }
          const statusTaskList = (
            await this.$apiCallV2(
              `/FreightForwarding/GetStatusTaskList?jobno=${this.selectedGoodsReceivedData['jobNo'].trim()}`,
            )
          ).filter((s) => s['#'] != '0');
          // same issue with JobPage.vue
          // const statusTaskList = await this.$apiCallV2(
          //   `/FreightForwarding/GetStatusTaskList?jobno=${this.selectedGoodsReceivedData['jobNo'].trim()}`,
          // );
          //console.log(statusTaskList);
          this.statusTaskData['statusTaskListing'] = this.appendStatusIcon(statusTaskList);
          //console.log(this.statusTaskData['statusTaskListing']);
          this.statusTaskData['statusJobNo'] = this.selectedGoodsReceivedData['jobNo'].trim();
          if (type == 'v') {
            this.tabsItems[6]['disabled'] = false;
          }
        }
      } else {
        this.selectedGoodsReceivedData['saveAsJob'] = false;
      }

      if (blDetails) {
        this.disableSaveBL = true;
        this.selectedBLInfoData['saveBL'] = true;
        this.selectedBLInfoData['blNo'] =
          duplicate ? '**********'
          : blDetails['BL No'] ? blDetails['BL No']
          : '';

        this.selectedBLInfoData['selectedPlaceOfReceipt'] = this.shippingInfoData['portOfLoading'].getSelected(
          blDetails['Place Of Receipt'],
          'Port Code',
        );

        this.selectedBLInfoData['selectedPlaceOfDelivery'] = this.shippingInfoData['portOfLoading'].getSelected(
          blDetails['Place Of Delivery'],
          'Port Code',
        );

        this.selectedBLInfoData['selectedPlaceOfIssue'] = this.shippingInfoData['portOfLoading'].getSelected(
          blDetails['Place Of Issue'],
          'Port Code',
        );

        this.selectedBLInfoData['dateOfIssue'] =
          blDetails['Date Of Issue'] ? new Date(blDetails['Date Of Issue']) : null;
      } else {
        this.selectedBLInfoData['saveBL'] = false;
      }

      this.$fullscreenLoading();
    },
    appendStatusIcon: function (list) {
      const allStatus = list.sort((a, b) => {
        return a['#'] < b['#'] ? 1 : -1;
      });
      const statusWithIcons = allStatus.map((s) => {
        switch (s['Progress']) {
          case 'Completed':
            return Object.assign({ '': 'https://dsmlib.logistics.systems/Icons/StatusComplete.png' }, s);
          case 'Due':
            return Object.assign({ '': 'https://dsmlib.logistics.systems/Icons/StatusDue.png' }, s);
          case 'Due In 2 days':
            return Object.assign({ '': 'https://dsmlib.logistics.systems/Icons/StatusDueIn2Days.png' }, s);
          case 'Pending':
            return Object.assign({ '': 'https://dsmlib.logistics.systems/Icons/StatusPending.png' }, s);
          case 'Cancelled':
            return Object.assign({ '': 'https://dsmlib.logistics.systems/Icons/StatusCancelled.png' }, s);
          default:
            return Object.assign({ '': '' }, s);
        }
      });

      return statusWithIcons;
    },
    savePopupButtonClicked: function (e) {
      switch (e) {
        case 'Yes': //
          if (!this.selectedUnconfirmReason) {
            this.popupErrorText = 'Please select your reason';
          } else if (
            this.selectedUnconfirmReason['Reason'].includes('OTHERS') &&
            (!this.unconfirmRemarks || !this.unconfirmRemarks.trim())
          ) {
            this.popupErrorText = 'Please state your remarks';
          } else {
            this.saveClicked(true, true, false);
            this.showSavePopup = false;
          }
          break;
        case 'Save': // User choose to save as confirm or unconfirm job
          if (!this.saveAsConfirm) {
            if (!this.selectedUnconfirmReason) {
              this.popupErrorText = 'Please select your reason';
            } else if (this.selectedUnconfirmReason['Reason'].includes('OTHERS') && !this.unconfirmRemarks) {
              this.popupErrorText = 'Please state your remarks';
            } else {
              this.saveClicked(true, true, false);
              this.showSavePopup = false;
            }
          } else {
            this.saveClicked(true, true, true);
            this.showSavePopup = false;
          }
          break;
        default:
          this.showSavePopup = false;
          this.selectedUnconfirmReason = null;
          this.unconfirmRemarks = '';
          this.selectedSaveBy = { keyValue: '1', label: 'Confirm Job' };
          break;
      }
    },
    reasonFocusChanged: function (e) {
      this.reasonOpen = e;
    },
    formatWeightValue: function (value) {
      // to format value to weight format with decimals and remove the separator to avoid issues
      if (value) {
        if (typeof value === 'number') {
          value = value.toString();
        }
        value = value.replaceAll(',', '');
        return this.$weightFormat(value).replaceAll(',', '');
      }
      return this.$weightFormat('0');
    },
    deletePopupButtonClicked: async function () {
      const bookingNo = this.selectedFreightBooking[0]['Booking No'];
      const jobNo = this.selectedFreightBooking[0]['Job No'];
      const blNo = this.selectedFreightBooking[0]['BL No'];

      let alertText = `Are you sure you want to delete ${bookingNo}?`;

      if (jobNo) {
        alertText += `\nThis will also delete Job ${jobNo}`;
      }

      if (blNo) {
        alertText += `\nThis will also delete BL ${blNo}`;
      }
      const confirmDelete = await this.$showAlert('warning', 'Delete Booking', alertText, [
        { text: 'Delete', color: 'red', textColor: 'white' },
        { text: 'Cancel', color: 'gray', textColor: 'white' },
      ]);
      if (confirmDelete === 'Delete') {
        const postData = {
          action: 'D', // delete
          bookingno: this.selectedFreightBooking[0]['Booking No'].trim(),
          jobno: this.selectedFreightBooking[0]['Job No'].trim(),
          blNo: this.selectedFreightBooking[0]['BL No'].trim(),
        };
        const job = await this.$apiCallV2(`/FreightBooking/SaveFreightBooking`, postData);
        if (job[0]['Results'] == 0) {
          this.refreshClicked(true);
          const parts = [`Booking ${bookingNo}`];
          if (jobNo) parts.push(`Job ${jobNo}`);
          if (blNo) parts.push(`BL ${blNo}`);

          let joinedParts;
          if (parts.length > 2) {
            joinedParts = parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1];
          } else {
            joinedParts = parts.join(' and ');
          }

          const message = `${joinedParts} ${parts.length > 1 ? 'were' : 'was'} successfully deleted.`;

          this.$showAlert('success', 'Deleted!', message);
        } else {
          this.$showAlert(
            'error',
            'Not Deleted',
            `There was an error occurred while deleting your job. Please try again.`,
          );
        }
      }
    },

    clearPopupData: function () {
      this.viewMode = false;

      Object.keys(this.selectedBookingInfoData).forEach((k) => {
        this.selectedBookingInfoData[k] = null;
      });
      Object.keys(this.selectedShippingInfoData).forEach((k) => {
        this.selectedShippingInfoData[k] = null;
      });
      Object.keys(this.selectedGoodsReceivedData).forEach((k) => {
        this.selectedGoodsReceivedData[k] = null;
      });
      Object.keys(this.selectedBLInfoData).forEach((k) => {
        this.selectedBLInfoData[k] = null;
      });
      //#region Selected Cargo Info Data
      this.selectedCargoInfoData['gWeight'] = this.$weightFormat('0'); // default these to 0 because if saved empty it will be 0
      this.selectedCargoInfoData['volume'] = this.$weightFormat('0');
      this.selectedCargoInfoData['chargeable'] = this.$weightFormat('0');

      this.selectedCargoInfoData['selectedCommodity'] = null;
      this.selectedCargoInfoData['selectedPMaterial'] = null;
      this.selectedCargoInfoData['packageCount'] = null;
      this.selectedCargoInfoData['selectedPackageType'] = null;

      // this.selectedCargoInfoData['subPkgList'] = [];
      // this.selectedCargoInfoData['selectedSubPkgs'] = null;
      // this.selectedCargoInfoData['deletedSubPkg'] = [];
      this.selectedCargoInfoData['contDetailsList'] = [];
      this.selectedCargoInfoData['selectedContDetails'] = null;
      this.selectedCargoInfoData['deletedContDetails'] = [];
      //#endregion

      this.selectedDocumentInfoData['docData'] = [];
      this.selectedDocumentInfoData['selectedDoc'] = [];

      Object.keys(this.errorMessages).forEach((k) => {
        this.errorMessages[k] = '';
      });

      this.sMessages = [];
      this.eMessages = [];
      this.rMessages = [];

      this.documentsToDelete = [];
      this.editDeleteDoc = [];
      this.editAddDoc = [];

      this.statusTaskListing = [];

      this.editDetails = null;

      this.currentTabIndex = 0;

      this.showShipperAddress = false;
      this.showConsigneeAddress = false;
      this.showNPartyAddress = false;

      this.shipperAddress = null;
      this.consigneeAddress = null;
      this.notifyPartyAddress = null;

      this.edittingDetails = false;

      this.documentInfoData['updatePopup'] = false;

      this.$apiCallV2('/FreightForwarding/GetQuotationListing', {}).then((result) => {
        this.bookingInfoData['quotationNo'] = result; // reset the listing
      });

      this.disableSaveAsJob = false;
      this.disableSaveBL = false;
      this.contSummary = '';
      this.tabsItems[6]['disabled'] = true;

      this.selectedUnconfirmReason = null;
      this.unconfirmRemarks = '';
    },

    billToSelected: async function (e) {
      if (!e) {
        this.$fullscreenLoading('Loading');

        this.selectedBookingInfoData['billToDetails'] = null;
        await this.$apiCallV2('/FreightForwarding/GetQuotationListing', {}).then((result) => {
          this.bookingInfoData['quotationNo'] = result; // reset the listing

          this.$fullscreenLoading();
        });
        return;
      }
      this.$fullscreenLoading('Loading');

      const getOutstanding = await this.$apiCallV2(
        `/FreightForwarding/GetAROutstanding?cust=${encodeURIComponent(e['Customer Code'].trim())}&jobdate=${this.selectedBookingInfoData['bookingDateTime']?.formatString('yyyy-MM-dd') ?? ''}`,
      );
      const details = getOutstanding[0];

      this.selectedBookingInfoData['billToDetails'] = {};
      this.selectedBookingInfoData['billToDetails']['creditLimit'] = details['CreditLimit'];
      this.selectedBookingInfoData['billToDetails']['creditTerms'] =
        `${details['CreditTerm']} ${details['CreditTypeDesc'] ? details['CreditTypeDesc'].trim() : ''}`;
      this.selectedBookingInfoData['billToDetails']['status'] = {
        text: details['CusStatus'],
        color: details['CusStatusColor'],
      };
      this.selectedBookingInfoData['billToDetails']['outstanding'] = {
        perc: details['PerOutstanding'],
        color: details['PerOutstandingColor'],
        value: details['Outstanding'],
      };
      this.selectedBookingInfoData['billToDetails']['currency'] = details['Currency'] ? details['Currency'].trim() : '';

      if (details['errMsg']) {
        await this.$showAlert(
          'error',
          'Credit Outstanding',
          `<div style="text-align: left">${details['errMsg']}</div>`,
        );
        this.selectedBookingInfoData['selectedBillTo'] = null;
        this.selectedBookingInfoData['billToDetails'] = null;
        this.$refs['billToInput'].setFocus();
      }
      await this.$apiCallV2('/FreightForwarding/GetQuotationListing', {
        billingcustno: e['Customer Code'],
        custno:
          this.pageType == 'Import' ?
            this.selectedBookingInfoData['selectedConsignee']?.['Consignee Code'] || null
          : this.selectedBookingInfoData['selectedShipper']?.['Shipper Code'] || null,
      }).then((result) => {
        this.bookingInfoData['quotationNo'] = result; // get listing based on billing cust no
      });

      this.$fullscreenLoading();
    },
    addPackageClicked: function (edit) {
      if (edit) {
        if (this.selectedGoodsReceivedData['selectedSubPkgs'].length == 0) {
          this.$showAlert('error', 'No Details Selected', `No Package details selected to edit`);
          return;
        } else if (this.selectedGoodsReceivedData['selectedSubPkgs'].length > 1) {
          this.$showAlert('error', 'Multiple Details Selected', `Please select only 1 Package details to edit`);
          return;
        }
      }

      this.cargoPopupDetails['cargoAddType'] = 'sub';
      this.cargoPopupDetails['isEdit'] = Boolean(edit);
      this.cargoPopupDetails['editIndex'] = -1;
      this.cargoPopupDetails['editDetails'] = edit ? this.selectedGoodsReceivedData['selectedSubPkgs'][0] : null;
      if (edit) {
        const selectedSubPkg = this.selectedGoodsReceivedData['selectedSubPkgs'][0];
        const compareColumns = ['Package Quantity', 'Package Type', 'Gross Weight'];

        this.cargoPopupDetails['editIndex'] = this.selectedGoodsReceivedData['subPkgList'].findIndex((s) =>
          compareColumns.every((column) => s[column] === selectedSubPkg[column]),
        );
      }
    },
    cargoInfoPopupDeleteDetails: function () {
      if (!this.selectedGoodsReceivedData['selectedSubPkgs']) {
        this.$showAlert('error', 'Error', `Please select at least 1 sub package below to remove`);
        return;
      }

      if (this.selectedGoodsReceivedData['selectedSubPkgs'].length == 0) {
        //this.$alert.showAlert('Error', `Please select at least 1 ${type == 'sub' ? 'sub package' : 'container detail'} below to remove`, 'error');
        this.$showAlert('error', 'Error', `Please select at least 1 sub package below to remove`);
        return;
      }

      const temp = this.selectedGoodsReceivedData['subPkgList'];
      this.selectedGoodsReceivedData['subPkgList'] = [];

      // this.selectedCargoInfoData[selectedKey].forEach((s) => {
      //   let index = temp.findIndex(t => JSON.stringify(t) == JSON.stringify(s));

      //   let itemAction = temp[index]['item_action'];
      //   if (itemAction != 'N') { this.selectedCargoInfoData[deleteKey].push(temp[index]) }

      //   temp.splice(index, 1);
      // });

      this.selectedGoodsReceivedData['selectedSubPkgs'].forEach((s) => {
        const compareColumns = ['Package Quantity', 'Package Type', 'Gross Weight'];
        const index = temp.findIndex((t) => {
          return compareColumns.every((column) => t[column] === s[column]);
        });
        if (index !== -1) {
          const itemAction = temp[index]['item_action'];
          if (itemAction != 'N') {
            this.selectedGoodsReceivedData['deletedSubPkg'].push(temp[index]);
          }
          temp.splice(index, 1);
        }
      });

      this.$nextTick(() => {
        this.selectedGoodsReceivedData['subPkgList'] = temp;
        this.selectedGoodsReceivedData['selectedSubPkgs'] = [];
      });
    },

    //#region Cargo Info Tab Methods
    addContainerClicked: function () {
      this.clearContainerPopupData();
      this.clearContainerPopupError();
      this.cargoInfoContainerData['containerEditIndex'] = -1;

      this.cargoInfoContainerData['popupButtonList'] = [
        { text: 'Add Container' },
        { text: 'Cancel', textColor: 'white', color: 'gray' },
      ];
      this.cargoInfoContainerData['showContainerPopup'] = true;
      this.$nextTick(() => {
        this.cargoInfoContainerData['selectedCategory'] = { desc: 'Container Required' };
      });
    },
    editContainerClicked: function () {
      if (this.selectedCargoInfoData['selectedContDetails'].length == 0) {
        this.$showAlert('error', 'No Container Selected', 'Please select at least 1 container to edit');
      } else {
        this.clearContainerPopupData();
        this.clearContainerPopupError();

        const selected = JSON.parse(JSON.stringify(this.selectedCargoInfoData['selectedContDetails'][0]));
        //delete selected['_rowid']; //object have rowid which need to be deleted for comparing below
        // console.log(selected) // weishen 25/10/2024 - removed above code because its causing error. contDetailsList have '_rowid' as well. no need to delete selected['_rowid'].
        // console.log(this.selectedCargoInfoData['contDetailsList'])
        this.cargoInfoContainerData['containerEditIndex'] = this.selectedCargoInfoData['contDetailsList'].findIndex(
          (cd) => JSON.stringify(cd) == JSON.stringify(selected),
        );
        // console.log(this.cargoInfoContainerData['containerEditIndex'])
        this.cargoInfoContainerData['popupButtonList'] = [
          { text: 'Edit Container' },
          { text: 'Cancel', textColor: 'white', color: 'gray' },
        ];
        this.cargoInfoContainerData['showContainerPopup'] = true;

        this.cargoInfoContainerData['selectedCategory'] = { desc: 'Cont/Seal #' };
        this.cargoInfoContainerData['contQty'] = '';
        this.cargoInfoContainerData['contNo'] = this.selectedCargoInfoData['selectedContDetails'][0]['Cont #'] || '';
        this.cargoInfoContainerData['oldcontNo'] =
          this.selectedCargoInfoData['selectedContDetails'][0]['Old Cont #'] || '';
        this.cargoInfoContainerData['sealNo'] = this.selectedCargoInfoData['selectedContDetails'][0]['Seal No'] || '';
        this.cargoInfoContainerData['oldsealNo'] =
          this.selectedCargoInfoData['selectedContDetails'][0]['Old Seal No'] || '';
        this.cargoInfoContainerData['contSelectedSize'] = this.cargoInfoData['contSizeList'].getSelected(
          this.selectedCargoInfoData['selectedContDetails'][0]['Cont Size'],
          'Size',
        );
        this.cargoInfoContainerData['contoldSelectedSize'] = this.cargoInfoData['contSizeList'].getSelected(
          this.selectedCargoInfoData['selectedContDetails'][0]['Old Cont Size'],
          'Size',
        );
        this.cargoInfoContainerData['contSelectedType'] = this.cargoInfoData['contTypeList'].getSelected(
          this.selectedCargoInfoData['selectedContDetails'][0]['Cont Type'],
          'Cont Type',
        );
        this.cargoInfoContainerData['contoldSelectedType'] = this.cargoInfoData['contTypeList'].getSelected(
          this.selectedCargoInfoData['selectedContDetails'][0]['Old Cont Type'],
          'Cont Type',
        );
        this.cargoInfoContainerData['contSelectedStatus'] = this.cargoInfoData['contStatusList'].getSelected(
          this.selectedCargoInfoData['selectedContDetails'][0]['Status'],
          'Cont Status',
        );
        this.cargoInfoContainerData['contWeight'] =
          this.selectedCargoInfoData['selectedContDetails'][0]['Cont Weight'] ?
            this.selectedCargoInfoData['selectedContDetails'][0]['Cont Weight'].replaceAll(',', '')
          : this.$weightFormat('0');
        this.cargoInfoContainerData['contSelectedHdgType'] = this.cargoInfoData['contHandlingTypeList'].getSelected(
          this.selectedCargoInfoData['selectedContDetails'][0]['Handling Type'],
          'Handling Type',
        );
        this.cargoInfoContainerData['contoldSelectedHdgType'] = this.cargoInfoData['contHandlingTypeList'].getSelected(
          this.selectedCargoInfoData['selectedContDetails'][0]['Old Handling Type'],
          'Handling Type',
        );
        this.cargoInfoContainerData['contSelectedDelType'] = this.cargoInfoData['contDeliveryTypeList'].getSelected(
          this.selectedCargoInfoData['selectedContDetails'][0]['Delivery Type'],
          'Delivery Type',
        );
        // this.cargoInfoContainerData['contoldSelectedDelType'] = this.cargoInfoData['contDeliveryTypeList'].getSelected(
        //   this.selectedCargoInfoData['selectedContDetails'][0]['Old Delivery Type'],
        //   'Delivery Type',
        // );
        // this.cargoInfoContainerData['contSelectedSendTo'] = this.cargoInfoData['contSendToList'].getSelected(
        //   this.selectedCargoInfoData['selectedContDetails'][0]['Send To'],
        //   'Destination Code',
        // );
        // this.cargoInfoContainerData['contSocCoc'] = this.cargoInfoData['soccocTypeList'].getSelected(
        //   this.selectedCargoInfoData['selectedContDetails'][0]['SOC/COC'],
        //   'SOCCOC Type',
        // );
        this.cargoInfoContainerData['contRemarks'] = this.selectedCargoInfoData['selectedContDetails'][0]['Remarks'];
        this.cargoInfoContainerData['rotno'] = this.selectedCargoInfoData['selectedContDetails'][0]['ROT No'];
        this.cargoInfoContainerData['contcharges'] =
          this.selectedCargoInfoData['selectedContDetails'][0]['ContCharges'];
        this.cargoInfoContainerData['invoices'] = this.selectedCargoInfoData['selectedContDetails'][0]['Invoices'];
        this.cargoInfoContainerData['updateType'] = this.selectedCargoInfoData['selectedContDetails'][0]['UpdateType'];
      }
    },
    addEditContainerClicked: async function (e) {
      if (e == 'Cancel') {
        this.cargoInfoContainerData['showContainerPopup'] = false;
        return;
      }

      //#region Compulsory field check
      let errorCount = 0;
      if (!this.cargoInfoContainerData['selectedCategory']) {
        this.cargoInfoContainerData['selectedCategoryError'] = 'Cateogry is required!';
        errorCount++;
      }
      if (
        !this.cargoInfoContainerData['contQty'] && this.cargoInfoContainerData['selectedCategory'] ?
          this.cargoInfoContainerData['selectedCategory']['desc'] == 'Container Required'
        : false
      ) {
        this.cargoInfoContainerData['contQtyError'] = 'Container Quantity is required!';
        errorCount++;
      }
      if (
        !this.cargoInfoContainerData['contNo'] && this.cargoInfoContainerData['selectedCategory'] ?
          this.cargoInfoContainerData['selectedCategory']['desc'] == 'Cont/Seal #'
        : false
      ) {
        this.cargoInfoContainerData['contNoError'] = 'Container No is required!';
        errorCount++;
      }
      if (!this.cargoInfoContainerData['contSelectedSize']) {
        this.cargoInfoContainerData['contSelectedSizeError'] = 'Container Size is required!';
        errorCount++;
      }
      if (!this.cargoInfoContainerData['contSelectedType']) {
        this.cargoInfoContainerData['contSelectedTypeError'] = 'Container Type is required!';
        errorCount++;
      }
      if (!this.cargoInfoContainerData['contSelectedHdgType']) {
        this.cargoInfoContainerData['contSelectedHdgTypeError'] = 'Handling Type is required!';
        errorCount++;
      }
      // if (!this.cargoInfoContainerData['contSelectedDelType']) {
      //   this.cargoInfoContainerData['contSelectedDelTypeError'] = 'Delivery Type is required!';
      //   errorCount++;
      // }
      // if (!this.cargoInfoContainerData['contSocCoc']) {
      //   this.cargoInfoContainerData['contSocCocError'] = 'SOC/COC is required!';
      //   errorCount++;
      // }
      //#endregion

      if (errorCount > 0) {
        return;
      }

      const newDataList = [];
      const newData = {
        'Cont #': this.cargoInfoContainerData['contNo'] || '',
        'Old Cont #': e == 'Add Container' ? '' : this.cargoInfoContainerData['oldcontNo'] || '',
        'Seal No': this.cargoInfoContainerData['sealNo'] || '',
        'Old Seal No': e == 'Add Container' ? '' : this.cargoInfoContainerData['oldsealNo'] || '',
        'Cont Size':
          this.cargoInfoContainerData['contSelectedSize'] ?
            this.cargoInfoContainerData['contSelectedSize']['Size']
          : '',
        'Old Cont Size':
          e == 'Add Container' ? ''
          : this.cargoInfoContainerData['contoldSelectedSize'] ?
            this.cargoInfoContainerData['contoldSelectedSize']['Size']
          : '',
        'Cont Type':
          this.cargoInfoContainerData['contSelectedType'] ?
            this.cargoInfoContainerData['contSelectedType']['Cont Type']
          : '',
        'Old Cont Type':
          e == 'Add Container' ? ''
          : this.cargoInfoContainerData['contoldSelectedType'] ?
            this.cargoInfoContainerData['contoldSelectedType']['Cont Type']
          : '',
        'Cont Weight':
          this.cargoInfoContainerData['contWeight'] ?
            this.$weightFormat(this.cargoInfoContainerData['contWeight'])
          : this.$weightFormat('0'),
        Status:
          this.cargoInfoContainerData['contSelectedStatus'] ?
            this.cargoInfoContainerData['contSelectedStatus']['Cont Status']
          : '',
        'Handling Type':
          this.cargoInfoContainerData['contSelectedHdgType'] ?
            this.cargoInfoContainerData['contSelectedHdgType']['Handling Type']
          : '',
        'Old Handling Type':
          this.cargoInfoContainerData['contoldSelectedHdgType'] ?
            this.cargoInfoContainerData['contoldSelectedHdgType']['Handling Type']
          : '',
        'Delivery Type':
          this.cargoInfoContainerData['contSelectedDelType'] ?
            this.cargoInfoContainerData['contSelectedDelType']['Delivery Type']
          : '',
        'Old Delivery Type':
          this.cargoInfoContainerData['contoldSelectedDelType'] ?
            this.cargoInfoContainerData['contoldSelectedDelType']['Delivery Type']
          : '',
        'Send To':
          this.cargoInfoContainerData['contSelectedSendTo'] ?
            this.cargoInfoContainerData['contSelectedSendTo']['Destination Code']
          : '',
        'SOC/COC':
          this.cargoInfoContainerData['contSocCoc'] ? this.cargoInfoContainerData['contSocCoc']['SOCCOC Type'] : '',
        Remarks: this.cargoInfoContainerData['contRemarks'] || '',
        'ROT No': this.cargoInfoContainerData['rotno'] || '',
        ContCharges: this.cargoInfoContainerData['contcharges'] || '',
        Invoices: this.cargoInfoContainerData['Invoices'] || '',
        UpdateType: '',
      };

      const temp = this.selectedCargoInfoData['contDetailsList'];

      if (e == 'Add Container') {
        const loopCount =
          this.cargoInfoContainerData['selectedCategory']['desc'] == 'Container Required' ?
            Number(this.cargoInfoContainerData['contQty'])
          : 1;
        for (let x = 0; x < loopCount; x++) {
          newDataList.push(JSON.parse(JSON.stringify(newData)));
        }
        newDataList.forEach((d) => (d['item_action'] = 'N'));
      } else if (e == 'Edit Container') {
        //check for container #, size, seal # (FMS Update Container only check container #, seal #, size)

        //#region UpdateType
        //checking on if updatetype is 1 or 2 or 3.
        //1 = update container #, seal #, size only.
        //2 = update size, type, delivery, handling only. Charges involved
        //3 = update all. Charges Invloved.
        let containerUpdateType = 0; //no cont update
        if (this.cargoInfoContainerData['contNo'] != this.cargoInfoContainerData['oldcontNo']) containerUpdateType = 1;
        if (this.cargoInfoContainerData['sealNo'] != this.cargoInfoContainerData['oldsealNo']) containerUpdateType = 1;
        const sizeDiff = newData['Cont Size'] != newData['Old Cont Size'];
        if (sizeDiff)
          //check if true or false
          containerUpdateType = 1;
        const typeDiff = newData['Cont Type'] != newData['Old Cont Type'];
        if (typeDiff)
          //check if true or false
          containerUpdateType = containerUpdateType == 1 ? 3 : 2;
        const deliveryDiff = newData['Delivery Type'] != newData['Old Delivery Type'];
        if (deliveryDiff)
          //check if true or false
          containerUpdateType = containerUpdateType == 1 ? 3 : 2;
        const handlingDiff = newData['Handling Type'] != newData['Old Handling Type'];
        if (handlingDiff)
          //check if true or false
          containerUpdateType = containerUpdateType == 1 ? 3 : 2;
        //#endregion

        if (containerUpdateType != 0) {
          if (this.cargoInfoContainerData['rotno']) {
            const confirmEdit = await this.$showAlert(
              'warning',
              'Edit Container',
              `Container No already associated with a CMR NO(${this.cargoInfoContainerData['rotno']}). Do you want to continue edit?`,
              [
                { text: 'Edit', color: 'green', textColor: 'white' },
                { text: 'Cancel', color: 'gray', textColor: 'white' },
              ],
            );

            if (confirmEdit == 'Cancel') {
              return;
            }
          }

          newData['UpdateType'] = containerUpdateType.toString();
        }
        // console.log(this.cargoInfoContainerData['containerEditIndex']);
        // console.log(temp)
        const itemAction = temp[this.cargoInfoContainerData['containerEditIndex']].item_action;
        if (!itemAction) {
          newData['item_action'] = 'E';
        } else if (itemAction == 'N') {
          newData['item_action'] = 'N';
        }

        this.selectedCargoInfoData['contDetailsList'] = [];
        this.selectedCargoInfoData['selectedContDetails'] = [];
        temp.splice(this.cargoInfoContainerData['containerEditIndex'], 1, newData);
      }

      this.cargoInfoContainerData['showContainerPopup'] = false;
      this.selectedCargoInfoData['contDetailsList'] = [];
      this.$nextTick(() => (this.selectedCargoInfoData['contDetailsList'] = temp.concat(newDataList)));
    },
    clearContainerPopupData: function () {
      this.cargoInfoContainerData['selectedCategory'] = null;
      this.cargoInfoContainerData['contQty'] = '';
      this.cargoInfoContainerData['contNo'] = '';
      this.cargoInfoContainerData['sealNo'] = '';
      this.cargoInfoContainerData['contSelectedSize'] = null;
      this.cargoInfoContainerData['contSelectedType'] = null;
      this.cargoInfoContainerData['contSelectedStatus'] = null;
      this.cargoInfoContainerData['contWeight'] = this.$weightFormat('0');
      this.cargoInfoContainerData['contSelectedHdgType'] = null;
      this.cargoInfoContainerData['contSelectedDelType'] = null;
      this.cargoInfoContainerData['contSelectedSendTo'] = null;
      this.cargoInfoContainerData['contSocCoc'] = null;
      this.cargoInfoContainerData['contRemarks'] = '';
    },
    clearContainerPopupError: function () {
      this.cargoInfoContainerData['selectedCategoryError'] = '';
      this.cargoInfoContainerData['contQtyError'] = '';
      this.cargoInfoContainerData['contNoError'] = '';
      this.cargoInfoContainerData['contSelectedSizeError'] = '';
      this.cargoInfoContainerData['contSelectedTypeError'] = '';
      this.cargoInfoContainerData['contSelectedHdgTypeError'] = '';
      this.cargoInfoContainerData['contSelectedDelTypeError'] = '';
      this.cargoInfoContainerData['contSocCocError'] = '';
    },
    deleteContainerClicked: async function () {
      // console.log(this.selectedCargoInfoData['selectedContDetails']);
      // console.log(this.selectedCargoInfoData['selectedContDetails'].length);
      if (this.selectedCargoInfoData['selectedContDetails'].length == 0) {
        this.$showAlert('error', 'No Container Selected', 'Please select at least 1 container to delete');
        return;
      }

      const confirmDelete = await this.$showAlert(
        'warning',
        'Delete Container',
        'Are you sure you want to delete the selected container?',
        [
          { text: 'Delete', color: 'red', textColor: 'white' },
          { text: 'Cancel', color: 'gray', textColor: 'white' },
        ],
      );
      if (confirmDelete == 'Delete') {
        const temp = this.selectedCargoInfoData['contDetailsList'];
        this.selectedCargoInfoData['contDetailsList'] = [];

        this.selectedCargoInfoData['selectedContDetails'].forEach((s) => {
          const selected = JSON.parse(JSON.stringify(s));
          //delete selected['_rowid'];

          const index = temp.findIndex((t) => JSON.stringify(t) == JSON.stringify(selected));
          const itemAction = temp[index]['item_action'];
          if (itemAction != 'N') {
            this.selectedCargoInfoData['deletedContDetails'].push(temp[index]);
          }

          temp.splice(index, 1);
        });

        this.$nextTick(() => {
          this.selectedCargoInfoData['contDetailsList'] = temp;
          this.selectedCargoInfoData['selectedContDetails'] = [];
        });
      }
    },
    //#endregion

    convertNumber: function (value) {
      if (value) {
        const converted = Number(value);
        const formatted = value ? this.$weightFormat(converted) : this.$weightFormat('0');
        return formatted;
      }
    },
    cargoInfoPopupAddDetails: function (sub, details) {
      this.cargoPopupDetails['cargoAddType'] = '';

      if (sub == 'sub') {
        const temp = this.selectedGoodsReceivedData['subPkgList'];
        this.selectedGoodsReceivedData['subPkgList'] = [];
        const grossWeight = `${this.convertNumber(details.totalGross)}KG (${this.convertNumber(details.grossPerPkg)}KG)`;
        const netWeight = `${this.convertNumber(details.totalNet)}KG (${this.convertNumber(details.netPerPkg)}KG)`;
        const volume = `${this.convertNumber(details.totalVol)}M³ (${this.convertNumber(details.volPerPkg)}M³)`;

        const Length = details.dimLength ? this.convertNumber(details.dimLength) : this.$weightFormat('0');
        const Width = details.dimWidth ? this.convertNumber(details.dimWidth) : this.$weightFormat('0');
        const Height = details.dimHeight ? this.convertNumber(details.dimHeight) : this.$weightFormat('0');

        const addEditDetails = {
          'Package Quantity': details.pkgQty,
          'Package Type': details.selectedPackageType ? details.selectedPackageType['Package Code'] : '',
          Dimensions: `${Length}m X ${Width}m X ${Height}m`,
          'Gross Weight': grossWeight,
          'Net Weight': netWeight,
          Volume: volume,
          'Item Code': details.selectedItemCode ? details.selectedItemCode['Product Code'] : '',
          'Packing List #': details.packingListNo,
          'Invoice #': details.invoiceNo,
          'Container #': details.containerNo,
          Remarks: details.remarks,
          seq_no: '',
        };

        if (this.cargoPopupDetails['editIndex'] >= 0) {
          const itemAction = temp[this.cargoPopupDetails['editIndex']]['item_action'];
          if (!itemAction) {
            addEditDetails['item_action'] = 'E';
          } else if (itemAction == 'N') {
            addEditDetails['item_action'] = 'N';
          }

          addEditDetails['seq_no'] = temp[this.cargoPopupDetails['editIndex']]['seq_no'];

          temp.splice(this.cargoPopupDetails['editIndex'], 1, addEditDetails);
          this.selectedGoodsReceivedData['selectedSubPkgs'] = [];
        } else {
          addEditDetails['item_action'] = 'N';
          temp.push(addEditDetails);
        }

        this.$nextTick(() => (this.selectedGoodsReceivedData['subPkgList'] = temp));
      } else {
        const detailsToUse = Array.isArray(details) ? details : [details];
        const data = detailsToUse.map((d) => {
          return {
            'Cont #': d.contNo || '',
            'Old Cont #': d.oldcontNo || '',
            'Seal No': d.sealNo || '',
            'Old Seal No': d.oldsealNo || '',
            'Cont Size':
              d.contSelectedSize ?
                d.contSelectedSize['Size'] ?
                  d.contSelectedSize['Size'].trim()
                : ''
              : '',
            'Old Cont Size':
              d.contoldSelectedSize ?
                d.contoldSelectedSize['Size'] ?
                  d.contoldSelectedSize['Size'].trim()
                : ''
              : '',
            'Cont Type':
              d.contSelectedType ?
                d.contSelectedType['Cont Type'] ?
                  d.contSelectedType['Cont Type'].trim()
                : ''
              : '',
            'Old Cont Type':
              d.contoldSelectedType ?
                d.contoldSelectedType['Cont Type'] ?
                  d.contoldSelectedType['Cont Type'].trim()
                : ''
              : '',
            'Cont Weight': d.contWeight ? this.$weightFormat(d.contWeight) : this.$weightFormat('0'),
            Status:
              d.contSelectedStatus ?
                d.contSelectedStatus['Cont Status'] ?
                  d.contSelectedStatus['Cont Status'].trim()
                : ''
              : '',
            'Handling Type':
              d.contSelectedHdgType ?
                d.contSelectedHdgType['Handling Type'] ?
                  d.contSelectedHdgType['Handling Type'].trim()
                : ''
              : '',
            'Old Handling Type':
              d.contoldSelectedHdgType ?
                d.contoldSelectedHdgType['Handling Type'] ?
                  d.contoldSelectedHdgType['Handling Type'].trim()
                : ''
              : '',
            'Delivery Type':
              d.contSelectedDelType ?
                d.contSelectedDelType['Delivery Type'] ?
                  d.contSelectedDelType['Delivery Type'].trim()
                : ''
              : '',
            'Old Delivery Type':
              d.contoldSelectedDelType ?
                d.contoldSelectedDelType['Delivery Type'] ?
                  d.contoldSelectedDelType['Delivery Type'].trim()
                : ''
              : '',
            'Send To':
              d.contSelectedSendTo ?
                d.contSelectedSendTo['Destination Code'] ?
                  d.contSelectedSendTo['Destination Code'].trim()
                : ''
              : '',
            'SOC/COC': d.contSocCoc['SOCCOC Type'],
            Remarks: d.contRemarks || '',
            'ROT No': d.rotno || '',
            'Cont Charges': d.contcharges || '',
          };
        });

        let temp = this.selectedCargoInfoData['contDetailsList'];
        this.selectedCargoInfoData['contDetailsList'] = [];

        if (this.cargoPopupDetails['editIndex'] >= 0) {
          const itemAction = temp[this.cargoPopupDetails['editIndex']]['item_action'];
          if (!itemAction) {
            data[0]['item_action'] = 'E';
          } else if (itemAction == 'N') {
            data[0]['item_action'] = 'N';
          }

          temp.splice(this.cargoPopupDetails['editIndex'], 1, data[0]);
          this.selectedCargoInfoData['selectedContDetails'] = [];
        } else {
          data.forEach((d) => (d['item_action'] = 'N'));
          temp = temp.concat(data);
        }

        this.$nextTick(() => (this.selectedCargoInfoData['contDetailsList'] = temp));
      }
    },
    cargoPopupCancel: function () {
      this.cargoPopupDetails['cargoAddType'] = '';
      this.cargoPopupDetails['isEdit'] = false;
    },
    importFromExcel: async function () {
      this.importContPreview = [];
      this.importHasError = false;
      this.$refs['excelCont'].value = '';
      this.$refs['excelCont'].click();
      const excelFile = await new Promise((r) => (this.excelContResolve = r)); // returns the selected file

      if (typeof excelFile == 'object') {
        // If the file picker is not dismissed by pressing cancel
        this.excelContConfirm = true;

        const converted = await this.$excelToJson(excelFile);
        this.importContLoading = true;
        setTimeout(async () => {
          this.importContLoading = false;
          if (converted['success']) {
            // Successfully converted the excel to JSON
            const excelContent = converted['data'];

            const allSheet = excelContent.map((c) => c['sheet']);
            if (allSheet.includes('Containers')) {
              // Excel has the 'Containers' sheet
              const warn =
                '<img src="https://dsmlib.logistics.systems/Icons/warning.png" height="20" title="Value does not exist within the list">';
              const error =
                '<img src="https://dsmlib.logistics.systems/Icons/error.png" height="20" title="Value is required">';

              let duplicate = false;
              const currentContainers = this.selectedCargoInfoData['contDetailsList'].map((c) => c['Cont #']);
              const excelData = this.$deepTrimJsonV2(excelContent.find((c) => c['sheet'] == 'Containers')['data']);
              excelData.forEach((d) => {
                if (!d['Container No']) {
                  d['Container No'] = error;
                  this.importHasError = true;
                } else if (currentContainers.includes(d['Container No'])) {
                  duplicate = true;
                }

                if (!d['Size']) {
                  d['Size'] = error;
                  this.importHasError = true;
                } else if (
                  !this.cargoInfoData['contSizeList'].find(
                    (c) => c['Size'].toString().toLowerCase() == d['Size'].toString().toLowerCase(),
                  )
                ) {
                  d['Size'] += warn;
                  this.importHasError = true;
                }
                if (!d['Type']) {
                  d['Type'] = error;
                  this.importHasError = true;
                } else if (
                  !this.cargoInfoData['contTypeList'].find(
                    (c) => c['Cont Type'].toString().toLowerCase() == d['Type'].toString().toLowerCase(),
                  )
                ) {
                  d['Type'] += warn;
                  this.importHasError = true;
                }
                if (!d['Handling Type']) {
                  d['Handling Type'] = error;
                  this.importHasError = true;
                } else if (
                  !this.cargoInfoData['contHandlingTypeList'].find(
                    (c) => c['Handling Type'].toString().toLowerCase() == d['Handling Type'].toString().toLowerCase(),
                  )
                ) {
                  d['Handling Type'] += warn;
                  this.importHasError = true;
                }
                if (!d['Delivery Type']) {
                  d['Delivery Type'] = error;
                  this.importHasError = true;
                } else if (
                  !this.cargoInfoData['contDeliveryTypeList'].find(
                    (c) => c['Delivery Type'].toString().toLowerCase() == d['Delivery Type'].toString().toLowerCase(),
                  )
                ) {
                  d['Delivery Type'] += warn;
                  this.importHasError = true;
                }
              });

              if (duplicate) {
                // Got duplicate
                this.$showAlert(
                  'error',
                  'Container Has Duplicates',
                  `The excel file you've chosen might already been uploaded or there are 1 or more container number is duplicated.<br>Please fix the duplicate container in your excel and re-upload.`,
                );
                this.excelContConfirm = false;
              } else {
                // No duplications
                this.importContPreview = excelData;

                const confirmCont = await new Promise((r) => (this.excelContResolve = r));
                this.excelContConfirm = false;

                if (confirmCont.includes('Confirm')) {
                  // User confirm the container list
                  const fromExcel = this.importContPreview.map((c) => {
                    return {
                      'Cont #': c['Container No'] ? c['Container No'] : '',
                      'Seal No': c['Seal No'] ? c['Seal No'] : '',
                      'Cont Size': c['Size'] ? c['Size'] : '',
                      'Cont Type': c['Type'] ? c['Type'] : '',
                      'Cont Weight': c['Weight (KG)'] ? c['Weight (KG)'] : '',
                      Status: '',
                      'Handling Type': c['Handling Type'] ? c['Handling Type'] : '',
                      'Delivery Type': c['Delivery Type'] ? c['Delivery Type'] : '',
                      Remarks: c['Remarks'] ? c['Remarks'] : '',
                      'SOC/COC': c['SOC/COC'] ? c['SOC/COC'] : '',
                      'From API': false,
                    };
                  });

                  const temp = this.selectedCargoInfoData['contDetailsList'];
                  this.selectedCargoInfoData['contDetailsList'] = [];
                  this.$nextTick(() => {
                    this.selectedCargoInfoData['contDetailsList'] = temp.concat(fromExcel);
                    this.importContPreview = [];
                  });
                }
              }
            } else {
              // Excel has no 'Containers' sheet
              this.$showAlert(
                'error',
                'Wrong Excel Format',
                `Please make sure that you're using the correct Excel format to import the list of containers.<br><br>If you don't have one, you may download it from <a href="https://dsmlib.logistics.systems/Files/Template/DataTemplate_Containers.xlsx">here</a>.`,
              );
              this.excelContConfirm = false;
            }
          } else {
            // Error converting the Excel to JSON
            this.$showAlert('error', 'Error Reading File', converted['data']);
            this.excelContConfirm = false;
          }
        }, 1000);
      }
    },
    excelContClicked: function (btn) {
      this.excelContResolve(btn);
    },
    excelContChange: function (e) {
      const files = e.target.files;
      if (files.length > 0) {
        this.excelContResolve(e.target.files[0]);
      } else {
        this.excelContResolve('cancel');
      }
    },

    //#region API Calls
    getBookingInfoDetails: function () {
      const bookingInfoDetailsPromises = [];

      bookingInfoDetailsPromises.push(
        // transport mode
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'transmode' }).then((result) => {
          this.bookingInfoData['transModeList'] = result;
        }),
      );

      bookingInfoDetailsPromises.push(
        // cargo type
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'cargotype' }).then((result) => {
          this.bookingInfoData['cargoTypeList'] = result;
        }),
      );

      bookingInfoDetailsPromises.push(
        // delivery type
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'FREIGHTDELIVERYTYPE' }).then((result) => {
          this.bookingInfoData['deliveryTypeList'] = result;
        }),
      );

      bookingInfoDetailsPromises.push(
        // freight terms
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'FREIGHTTYPE', jobtype: this.pageType[0] }).then(
          (result) => {
            this.bookingInfoData['freightTermsList'] = result;
          },
        ),
      );

      bookingInfoDetailsPromises.push(
        // Cargo Terms
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CARGOTERMS' }).then((result) => {
          this.bookingInfoData['cargoTermsList'] = result.map((cargoTerms) => ({
            ...cargoTerms,
            'Code - Desc': `${cargoTerms['Origin Code']} - ${cargoTerms['Description']}`,
          }));
        }),
      );

      bookingInfoDetailsPromises.push(
        // Currency
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CURRENCY' }).then((result) => {
          this.bookingInfoData['currencyList'] = result;
        }),
      );

      bookingInfoDetailsPromises.push(
        // Payment Terms (is it payment type?)
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'PAYMENTTYPE' }).then((result) => {
          this.bookingInfoData['paymentTermsList'] = result;
        }),
      );

      bookingInfoDetailsPromises.push(
        //Shipper
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'shipper' }).then((result) => {
          this.bookingInfoData['shipper'] = result.map((shipper) => ({
            ...shipper,
            'Code - Name': `${shipper['Shipper Code']} - ${shipper['Shipper Name']}`,
          }));
        }),
      );

      bookingInfoDetailsPromises.push(
        //Consignee
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'consignee' }).then((result) => {
          this.bookingInfoData['consignee'] = result.map((consignee) => ({
            ...consignee,
            'Code - Name': `${consignee['Consignee Code']} - ${consignee['Consignee Name']}`,
          }));
        }),
      );

      bookingInfoDetailsPromises.push(
        //Notify Party
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'notifyparty' }).then((result) => {
          this.bookingInfoData['notifyParty'] = result.map((notifyParty) => ({
            ...notifyParty,
            'Code - Name': `${notifyParty['Notify Party Code']} - ${notifyParty['Notify Party Name']}`,
          }));
        }),
      );

      bookingInfoDetailsPromises.push(
        //Bill To
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'billcust' }).then((result) => {
          this.bookingInfoData['billTo'] = result.map((billTo) => ({
            ...billTo,
            'Code - Name': `${billTo['Customer Code']} - ${billTo['Customer Name']}`,
          }));
        }),
      );

      bookingInfoDetailsPromises.push(
        //Quotation No
        this.$apiCallV2('/FreightForwarding/GetQuotationListing', {}).then((result) => {
          this.bookingInfoData['quotationNo'] = result;
        }),
      );

      //address form
      bookingInfoDetailsPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'COUNTRY' }).then((result) => {
          this.addressDetails['countryList'] = result;
        }),
      );
      bookingInfoDetailsPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'POSTCODE' }).then((result) => {
          this.addressDetails['postcodeList'] = result;
        }),
      );
      bookingInfoDetailsPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'STATE' }).then((result) => {
          this.addressDetails['stateList'] = result;
        }),
      );

      return bookingInfoDetailsPromises;
    },

    getShippingInfoTabDetails: function () {
      const shippingInfoTabDetailsPromises = [];

      shippingInfoTabDetailsPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'vesselid' }).then((result) => {
          this.shippingInfoData['vesselIDList'] = result.map((vesselid) => ({
            ...vesselid,
            'ID - Name': `${vesselid['Vessel ID']} - ${vesselid['Vessel Name']}`,
          }));
        }),
      );

      shippingInfoTabDetailsPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'portoperator' }).then((result) => {
          this.shippingInfoData['portOperator'] = result.map((portOperator) => ({
            ...portOperator,
            'Code - Name': `${portOperator['Operator Code']} - ${portOperator['Operator Name']}`,
          }));
        }),
      );

      shippingInfoTabDetailsPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'fumigator' }).then((result) => {
          this.shippingInfoData['fumigatorList'] = result;
        }),
      );

      return shippingInfoTabDetailsPromises;
    },
    getShippingInfoTabDetailsByTransMode: function () {
      const shippingInfoTabDetailsByTransModePromises = [];

      shippingInfoTabDetailsByTransModePromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'shippingagent' }).then((result) => {
          this.shippingInfoData['shippingAgentList'] = result.map((shippingagent) => ({
            ...shippingagent,
            'Code - Name': `${shippingagent['Agent Code']} - ${shippingagent['Agent Name']}`,
          }));
        }),
      );

      shippingInfoTabDetailsByTransModePromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'shippingline' }).then((result) => {
          this.shippingInfoData['shippingLineList'] = result.map((shippingline) => ({
            ...shippingline,
            'Code - Name': `${shippingline['Agent Code']} - ${shippingline['Agent Name']}`,
          }));
        }),
      );

      shippingInfoTabDetailsByTransModePromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'frtforwarder' }).then((result) => {
          this.shippingInfoData['frtForwarderList'] = result.map((frtforwarder) => ({
            ...frtforwarder,
            'Code - Name': `${frtforwarder['Agent Code']} - ${frtforwarder['Agent Name']}`,
          }));
        }),
      );

      shippingInfoTabDetailsByTransModePromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'FORWARDINGAGENT' }).then((result) => {
          this.shippingInfoData['fwdgAgentList'] = result.map((fwdgAgent) => ({
            ...fwdgAgent,
            'Code - Name': `${fwdgAgent['FA Code']} - ${fwdgAgent['FA Name']}`,
          }));
        }),
      );

      shippingInfoTabDetailsByTransModePromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'FOREIGNFORWARDER' }).then((result) => {
          this.shippingInfoData['overseaAgentList'] = result.map((fwdgAgent) => ({
            ...fwdgAgent,
            'Code - Name': `${fwdgAgent['Agent Code']} - ${fwdgAgent['Agent Name']}`,
          }));
        }),
      );

      shippingInfoTabDetailsByTransModePromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'AGENT' }).then((result) => {
          this.shippingInfoData['coloaderList'] = result.map((fwdgAgent) => ({
            ...fwdgAgent,
            'Code - Name': `${fwdgAgent['Agent Code']} - ${fwdgAgent['Agent Name']}`,
          }));
        }),
      );

      return shippingInfoTabDetailsByTransModePromises;
    },
    getCargoInfoTabDetails: function () {
      const shippingInfoTabDetailsPromises = [];

      shippingInfoTabDetailsPromises.push(
        // Commodity
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'commodity' }).then((result) => {
          this.cargoInfoData['commodityList'] = result.map((commodity) => ({
            ...commodity,
            'Code - Desc': `${commodity['Commodity Code']} - ${commodity['Description']}`,
          }));
        }),
      );

      shippingInfoTabDetailsPromises.push(
        // Package Type
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'packagecode' }).then((result) => {
          this.cargoInfoData['pkgTypeList'] = result.map((pkgType) => ({
            ...pkgType,
            'Code - Desc': `${pkgType['Package Code']} - ${pkgType['Description']}`,
          }));
        }),
      );

      shippingInfoTabDetailsPromises.push(
        // Cont Size
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CONTSIZE', module: 'FRE' }).then((result) => {
          this.cargoInfoData['contSizeList'] = result;
        }),
      );

      shippingInfoTabDetailsPromises.push(
        // Cont Type
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CONTTYPE', module: 'FRE' }).then((result) => {
          this.cargoInfoData['contTypeList'] = result.map((pkgType) => ({
            ...pkgType,
            'Code - Desc': `${pkgType['Cont Type']} - ${pkgType['Description']}`,
          }));
        }),
      );

      shippingInfoTabDetailsPromises.push(
        // Handling Type
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CONTHANDLINGTYPE', module: 'FRE' }).then((result) => {
          this.cargoInfoData['contHandlingTypeList'] = result;
        }),
      );

      shippingInfoTabDetailsPromises.push(
        // Cargo Delivery Type
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CONTDELIVERYTYPE', module: 'FRE' }).then((result) => {
          this.cargoInfoData['contDeliveryTypeList'] = result;
        }),
      );

      shippingInfoTabDetailsPromises.push(
        // SOCCOC
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'SOCCOC' }).then((result) => {
          this.cargoInfoData['soccocTypeList'] = result;
        }),
      );

      shippingInfoTabDetailsPromises.push(
        // Cont Status
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CONTSTATUS', module: 'FRE' }).then((result) => {
          this.cargoInfoData['contStatusList'] = result;
        }),
      );

      shippingInfoTabDetailsPromises.push(
        // Pkg Type
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'whproduct', module: 'FRE' }).then((result) => {
          this.cargoInfoData['itemCode'] = result;
        }),
      );

      return shippingInfoTabDetailsPromises;
    },
    getLargeData: function () {
      const largeDataPromises = [];
      largeDataPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'port' }).then((result) => {
          const portList = result.map((item) => {
            return {
              ...item,
              'Code - Desc': `${item['Port Code']} - ${item['Port Location']}`,
            };
          });
          this.shippingInfoData['portOfLoading'] = portList;
          this.shippingInfoData['portOfDischarge'] = portList;
          this.shippingInfoData['finalDestination'] = portList;
        }),
      );

      largeDataPromises.push(
        this.$apiCallV2('/Master/GetListMaster?save=true', { masterfile: 'shipcallno' }).then((result) => {
          this.shippingInfoData['shipCallNoList'] = result;
        }),
      );

      largeDataPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'destination' }).then((result) => {
          this.shippingInfoData['fumigatorLocationList'] = result;
          //this.cargoInfoData['contSendToList'] = result;
        }),
      );

      largeDataPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'job_unconfirm_reason' }).then((result) => {
          this.reasonsList = result;
        }),
      );

      return largeDataPromises;
    },
    //#endregion

    //#region InputQuery Refresh Clicked Handlers
    consigneeInputQueryRefreshClicked: async function (placeholder) {
      this.bookingInfoData['consignee'] = [];
      this.$fullscreenLoading(`Getting Latest ${placeholder} List`);
      await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'consignee' }).then((result) => {
        this.bookingInfoData['consignee'] = result.map((consignee) => ({
          ...consignee,
          'Code - Name': `${consignee['Consignee Code']} - ${consignee['Consignee Name']}`,
        }));
      });
      this.$fullscreenLoading();
    },
    billToInputQueryRefreshClicked: async function (placeholder) {
      this.bookingInfoData['billTo'] = [];
      this.$fullscreenLoading(`Getting Latest ${placeholder} List`);
      await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'billcust' }).then((result) => {
        this.bookingInfoData['billTo'] = result.map((billTo) => ({
          ...billTo,
          'Code - Name': `${billTo['Customer Code']} - ${billTo['Customer Name']}`,
        }));
      });
      this.$fullscreenLoading();
    },
    vesselIDInputQueryRefreshClicked: async function (placeholder) {
      this.shippingInfoData['vesselIDList'] = [];
      this.$fullscreenLoading(`Getting Latest ${placeholder} List`);
      await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'vesselid' }).then((result) => {
        this.shippingInfoData['vesselIDList'] = result.map((vesselid) => ({
          ...vesselid,
          'ID - Name': `${vesselid['Vessel ID']} - ${vesselid['Vessel Name']}`,
        }));
      });
      this.$fullscreenLoading();
    },
    portOfLoadingInputQueryRefreshClicked: async function (placeholder) {
      this.shippingInfoData['portOfLoading'] = [];
      this.$fullscreenLoading(`Getting Latest ${placeholder} List`);
      await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'port' }).then((result) => {
        const portList = result.map((item) => {
          return {
            ...item,
            'Code - Desc': `${item['Port Code']} - ${item['Port Location']}`,
          };
        });
        this.shippingInfoData['portOfLoading'] = portList;
        //this.shippingInfoData['portOfDischarge'] = portList;
        //this.shippingInfoData['finalDestination'] = portList;
      });
      this.$fullscreenLoading();
    },
    portOfDischargeInputQueryRefreshClicked: async function (placeholder) {
      this.shippingInfoData['portOfDischarge'] = [];
      this.$fullscreenLoading(`Getting Latest ${placeholder} List`);
      await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'port' }).then((result) => {
        const portList = result.map((item) => {
          return {
            ...item,
            'Code - Desc': `${item['Port Code']} - ${item['Port Location']}`,
          };
        });
        //this.shippingInfoData['portOfLoading'] = portList;
        this.shippingInfoData['portOfDischarge'] = portList;
        //this.shippingInfoData['finalDestination'] = portList;
      });
      this.$fullscreenLoading();
    },
    finalDestinationInputQueryRefreshClicked: async function (placeholder) {
      this.shippingInfoData['finalDestination'] = [];
      this.$fullscreenLoading(`Getting Latest ${placeholder} List`);
      await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'port' }).then((result) => {
        const portList = result.map((item) => {
          return {
            ...item,
            'Code - Desc': `${item['Port Code']} - ${item['Port Location']}`,
          };
        });
        //this.shippingInfoData['portOfLoading'] = portList;
        //this.shippingInfoData['portOfDischarge'] = portList;
        this.shippingInfoData['finalDestination'] = portList;
      });
      this.$fullscreenLoading();
    },
    shipperInputQueryRefreshClicked: async function (placeholder) {
      this.bookingInfoData['shipper'] = [];
      this.$fullscreenLoading(`Getting Latest ${placeholder} List`);
      await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'shipper' }).then((result) => {
        this.bookingInfoData['shipper'] = result.map((shipper) => ({
          ...shipper,
          'Code - Name': `${shipper['Shipper Code']} - ${shipper['Shipper Name']}`,
        }));
      });
      this.$fullscreenLoading();
    },
    shippingAgentInputQueryRefreshClicked: async function (placeholder) {
      this.shippingInfoData['shippingAgentList'] = [];
      this.$fullscreenLoading(`Getting Latest ${placeholder} List`);
      await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'shippingagent' }).then((result) => {
        this.shippingInfoData['shippingAgentList'] = result.map((shippingagent) => ({
          ...shippingagent,
          'Code - Name': `${shippingagent['Agent Code']} - ${shippingagent['Agent Name']}`,
        }));
      });
      this.$fullscreenLoading();
    },
    //#endregion

    formatCargoPkgList: function () {
      //console.log(this.selectedGoodsReceivedData['subPkgList'])
      if (this.selectedGoodsReceivedData['subPkgList']?.length < 1) {
        return [];
      }
      let toSend = this.selectedGoodsReceivedData['subPkgList'].filter((s) => s['item_action']);
      toSend = toSend.concat(this.selectedGoodsReceivedData['deletedSubPkg'] ?? []);
      const formatted = toSend.map((p) => {
        const dim = p['Dimensions'].split('X');
        const gWeight = p['Gross Weight'].replaceAll(',', '').split('KG');
        const nWeight = p['Net Weight'].replaceAll(',', '').split('KG');
        const uom = p['Volume'].replaceAll(',', '').split('M³');
        return {
          seq_no: p['seq_no'],
          packageqty: p['Package Quantity'],
          packageheight: dim[2].replaceAll('m', '').replaceAll(',', '').trim(),
          packagewidth: dim[1].replaceAll('m', '').replaceAll(',', '').trim(),
          packagelength: dim[0].replaceAll('m', '').replaceAll(',', '').trim(),
          packageweight: gWeight[1].replace('(', '').trim(),
          packageuom: uom[1].replace('(', '').trim(),
          remarks: p['Remarks'],
          actionflag: p['item_action'] || 'D',
          prod_code: p['Item Code'],
          balquantity: '',
          balweight: '',
          baluom: '',
          packagenweight: nWeight[1].replace('(', '').trim(),
          totalweight: gWeight[0].trim(),
          totaluom: uom[0].trim(),
          totalnweight: nWeight[0].trim(),
          itemvalue: '',
          packinglistno: p['Packing List #'],
          invoiceno: p['Invoice #'],
          cont_no: p['Container #'],
          package_code: p['Package Type'],
        };
      });

      return formatted;
    },
    formatCargoContList: function () {
      if (this.selectedCargoInfoData['contDetailsList']?.length < 1) {
        return [];
      }
      // if no action, set as 'E' to update demurragedate & dententiondate to each containerdetails records
      this.selectedCargoInfoData['contDetailsList'].forEach((item) => {
        if (!item['item_action']) {
          item['item_action'] = 'E';
        }
      });
      let toSend = this.selectedCargoInfoData['contDetailsList'].filter((s) => s['item_action']);
      toSend = toSend.concat(this.selectedCargoInfoData['deletedContDetails'] ?? []);
      const formatted = toSend.map((c) => {
        return {
          cont_no: c['Cont #'],
          oldcont_no: c['Old Cont #'],
          seal_no: c['Seal No'],
          size: c['Cont Size'],
          oldsize: c['Old Cont Size'],
          cont_type: c['Cont Type'],
          oldcont_type: c['Old Cont Type'],
          deliverytype: c['Delivery Type'],
          olddeliverytype: c['Old Delivery Type'],
          handlingtype: c['Handling Type'],
          oldhandlingtype: c['Old Handling Type'],
          remarks: c['Remarks'],
          shipmentstatus:
            c['Status'] ? this.cargoInfoData['contStatusList'].getSelected(c['Status'], 'Cont Status')['#'] : '0', //use number value instead of text
          sendto: c['Send To'],
          weight: c['Cont Weight'].replaceAll(',', ''),
          description: c['SOC/COC'],
          demurragedate:
            this.selectedShippingInfoData['demurageTime'] ?
              new Date(this.selectedShippingInfoData['demurageTime']).formatString('yyyy-MM-dd HH:mm:ss')
            : '',
          dententiondate:
            this.selectedShippingInfoData['detention'] ?
              new Date(this.selectedShippingInfoData['detention']).formatString('yyyy-MM-dd HH:mm:ss')
            : '',
          // 'demurragedate': '',
          // 'detentiondate': '',
          updatetype: c['UpdateType'],
          actionflag: c['item_action'] || 'D',
          docno: '',
          rotno: c['ROT No'],
        };
      });

      return formatted;
    },
    formatDocuments: function () {
      const adding = this.selectedDocumentInfoData['docData'].filter((d) => d['Action']);
      const deleting = this.documentsToDelete;

      let addingStr = JSON.stringify(adding.concat(deleting));
      addingStr = addingStr.replace(/"N\/A"/g, '""'); //replace all key values that is "N/A" to empty string instead of below code which replace any N/A
      // addingStr = addingStr.split('N/A').join('');

      const formatted = JSON.parse(addingStr);
      formatted.forEach((f) => {
        f['Start Date'] = f['Start Date'].split(':').join('');
        f['Received Date'] = f['Received Date'].split(':').join('');
        f['Due Date'] = f['Due Date'].split(':').join('');
      });
      return formatted;
    },
    saveClicked: async function (noload, bypassCompulsory, saveAs) {
      this.sMessages = [];
      this.eMessages = [];
      this.rMessages = [];

      this.popupError = false;

      const action = this.popupTitle[0] == 'D' ? 'N' : this.popupTitle[0];

      const isInvalid = this.checkPopupEntryFields();
      if (isInvalid) {
        return;
      }
      if (!this.isSaveAsJobChecked) {
        if ((await this.checkPopupEntryFieldsJob()) && !bypassCompulsory) {
          return;
        }
      }
      if (!(await this.validateDates())) {
        return;
      }
      const postData = {
        //booking
        action: action,
        dateupdate: '',
        jobstatus: 'N',
        jobtype: 'E', // Freight Booking - Export only
        bookingno: action == 'N' ? '' : this.selectedBookingInfoData['bookingNo'],
        bookingdate:
          this.selectedBookingInfoData['bookingDateTime'] ?
            new Date(this.selectedBookingInfoData['bookingDateTime']).formatString('yyyy-MM-dd')
          : '',
        bookingtime:
          this.selectedBookingInfoData['bookingDateTime'] ?
            new Date(this.selectedBookingInfoData['bookingDateTime']).formatString('HHmm')
          : '',
        shipmentby:
          this.selectedBookingInfoData['selectedTransMode'] ?
            this.selectedBookingInfoData['selectedTransMode']['Trans Mode']
          : '',
        cargotype:
          this.selectedBookingInfoData['selectedCargoType'] ?
            this.selectedBookingInfoData['selectedCargoType']['Cargo Type']
          : '',
        deliverytype:
          this.selectedBookingInfoData['selectedDeliveryType'] ?
            this.selectedBookingInfoData['selectedDeliveryType']['Delivery Type']
          : '',
        freightpayable:
          this.selectedBookingInfoData['selectedFreightTerms'] ?
            this.selectedBookingInfoData['selectedFreightTerms']['Freight Type']
          : '',
        cargoterms:
          this.selectedBookingInfoData['selectedCargoTerms'] ?
            this.selectedBookingInfoData['selectedCargoTerms']['Origin Code']
          : '',
        currency:
          this.selectedBookingInfoData['selectedCurrency'] ?
            this.selectedBookingInfoData['selectedCurrency']['Currency Code']
          : '',
        paymenttype:
          this.selectedBookingInfoData['selectedPaymentTerms'] ?
            this.selectedBookingInfoData['selectedPaymentTerms']['Payment Type']
          : '',
        // Export only. Cu number is the shipper.
        cunumber:
          this.selectedBookingInfoData['selectedShipper'] ?
            this.selectedBookingInfoData['selectedShipper']['Shipper Code']
          : '',
        shipper:
          this.selectedBookingInfoData['selectedShipper'] ?
            this.selectedBookingInfoData['selectedShipper']['Shipper Code']
          : '',
        consignee:
          this.selectedBookingInfoData['selectedConsignee'] ?
            this.selectedBookingInfoData['selectedConsignee']['Consignee Code']
          : '',
        notifyparty:
          this.selectedBookingInfoData['selectedNotifyParty'] ?
            this.selectedBookingInfoData['selectedNotifyParty']['Notify Party Code']
          : '',
        custrefno: this.selectedBookingInfoData['custRefNo'],
        billto:
          this.selectedBookingInfoData['selectedBillTo'] ?
            this.selectedBookingInfoData['selectedBillTo']['Customer Code']
          : '',
        requestedby: this.selectedBookingInfoData['requestedBy'],
        remarks: this.selectedBookingInfoData['remarks'],
        quotationno:
          this.selectedBookingInfoData['selectedQuotationNo'] ?
            this.selectedBookingInfoData['selectedQuotationNo']['Quotation No']
          : '',
        projectno: this.selectedBookingInfoData['projectNo'],
        sharecontainer: this.selectedSharing ? this.selectedSharing['keyValue'] : '',
        insurance: this.selectedInsurance ? this.selectedInsurance['keyValue'] : '',

        //cargo
        gweight: this.selectedCargoInfoData['gWeight'],
        measurement: this.selectedCargoInfoData['volume'],
        nweight: this.selectedCargoInfoData['chargeable'],
        commodity:
          this.selectedCargoInfoData['selectedCommodity'] ?
            this.selectedCargoInfoData['selectedCommodity']['Commodity Code']
          : '',
        noofpkg: this.selectedCargoInfoData['packageCount'],
        packagecode:
          this.selectedCargoInfoData['selectedPackageType'] ?
            this.selectedCargoInfoData['selectedPackageType']['Package Code']
          : '',

        //shipping
        bookingrefno: this.selectedShippingInfoData['bookingRefNo'],
        nooriginalbl: this.selectedShippingInfoData['originalBLNo'],
        voyno: this.selectedShippingInfoData['voyNo'],
        shipcallno:
          this.selectedShippingInfoData['selectedShipCall'] ?
            this.selectedShippingInfoData['selectedShipCall']['Ship Call No']
          : this.selectedShippingInfoData['shipCallNoValue'],
        vesselid:
          this.selectedShippingInfoData['selectedVesselID'] ?
            this.selectedShippingInfoData['selectedVesselID']['Vessel ID']
          : '',
        vesselname:
          this.selectedShippingInfoData['selectedVesselName'] ?
            this.selectedShippingInfoData['selectedVesselName']['Vessel Name']
          : this.selectedShippingInfoData['vesselNameValue'],
        shippingagent:
          this.selectedShippingInfoData['selectedShippingAgent'] ?
            this.selectedShippingInfoData['selectedShippingAgent']['Agent Code']
          : '',
        shippingline:
          this.selectedShippingInfoData['selectedShippingLine'] ?
            this.selectedShippingInfoData['selectedShippingLine']['Agent Code']
          : '',
        shipline:
          this.selectedShippingInfoData['selectedShippingLine'] ?
            this.selectedShippingInfoData['selectedShippingLine']['Agent Name']
          : '',
        loadport:
          this.selectedShippingInfoData['selectedPortLoading'] ?
            this.selectedShippingInfoData['selectedPortLoading']['Port Code']
          : '',
        dischargeport:
          this.selectedShippingInfoData['selectedPortDischarge'] ?
            this.selectedShippingInfoData['selectedPortDischarge']['Port Code']
          : '',
        finaldestination:
          this.selectedShippingInfoData['selectedFinalDestination'] ?
            this.selectedShippingInfoData['selectedFinalDestination']['Port Code']
          : '',
        transhipment: this.selectedShippingInfoData['isTranshipment'] ? '1' : '0',
        mothervname:
          this.selectedShippingInfoData['selectedMVesselID'] ?
            this.selectedShippingInfoData['selectedMVesselID']['Vessel ID']
          : '',
        mothervno: this.selectedShippingInfoData['mVoyNo'],
        trans_port:
          this.selectedShippingInfoData['selectedPortDischarge2'] ?
            this.selectedShippingInfoData['selectedPortDischarge2']['Port Code']
          : '',
        datetranship:
          this.selectedShippingInfoData['ETATime'] ?
            new Date(this.selectedShippingInfoData['ETATime']).formatString('yyyy-MM-dd')
          : '',
        timetranship:
          this.selectedShippingInfoData['ETATime'] ?
            new Date(this.selectedShippingInfoData['ETATime']).formatString('HHmm')
          : '',
        polETA_date:
          this.selectedShippingInfoData['polETA'] ?
            new Date(this.selectedShippingInfoData['polETA']).formatString('yyyy-MM-dd')
          : '',
        polETA_time:
          this.selectedShippingInfoData['polETA'] ?
            new Date(this.selectedShippingInfoData['polETA']).formatString('HHmm')
          : '',
        polETD_date:
          this.selectedShippingInfoData['polETD'] ?
            new Date(this.selectedShippingInfoData['polETD']).formatString('yyyy-MM-dd')
          : '',
        closing_date:
          this.selectedShippingInfoData['closingDate'] ?
            new Date(this.selectedShippingInfoData['closingDate']).formatString('yyyy-MM-dd')
          : '',
        polETD_time:
          this.selectedShippingInfoData['polETD'] ?
            new Date(this.selectedShippingInfoData['polETD']).formatString('HHmm')
          : '',
        closing_time:
          this.selectedShippingInfoData['closingDate'] ?
            new Date(this.selectedShippingInfoData['closingDate']).formatString('HHmm')
          : '',
        podETA_date:
          this.selectedShippingInfoData['podETA'] ?
            new Date(this.selectedShippingInfoData['podETA']).formatString('yyyy-MM-dd')
          : '',
        podETA_time:
          this.selectedShippingInfoData['podETA'] ?
            new Date(this.selectedShippingInfoData['podETA']).formatString('HHmm')
          : '',
        fDestETA_date:
          this.selectedShippingInfoData['fDestETA'] ?
            new Date(this.selectedShippingInfoData['fDestETA']).formatString('yyyy-MM-dd')
          : '',
        fDestETA_time:
          this.selectedShippingInfoData['fDestETA'] ?
            new Date(this.selectedShippingInfoData['fDestETA']).formatString('HHmm')
          : '',
        carrieragent:
          this.selectedShippingInfoData['selectedCarrierAgent'] ?
            this.selectedShippingInfoData['selectedCarrierAgent']['Agent Code']
          : '',
        forwardingagent:
          this.selectedShippingInfoData['selectedFwdgAgent'] ?
            this.selectedShippingInfoData['selectedFwdgAgent']['FA Code']
          : '',
        overseaagent:
          this.selectedShippingInfoData['selectedOverseaAgent'] ?
            this.selectedShippingInfoData['selectedOverseaAgent']['Agent Code']
          : '',
        portoperator:
          this.selectedShippingInfoData['selectedPortOperator'] ?
            this.selectedShippingInfoData['selectedPortOperator']['Operator Code']
          : '',
        contoperator:
          this.selectedShippingInfoData['selectedContOperator'] ?
            this.selectedShippingInfoData['selectedContOperator']
          : '',
        coloader:
          this.selectedShippingInfoData['selectedCoLoader'] ?
            this.selectedShippingInfoData['selectedCoLoader']['Agent Code']
          : '',

        imp_name: this.consigneeAddress ? this.consigneeAddress['name'] : '', // consignee
        imp_code:
          this.selectedBookingInfoData['selectedConsignee'] ?
            this.selectedBookingInfoData['selectedConsignee']['Consignee Code']
          : '',
        imp_attnto: this.consigneeAddress ? this.consigneeAddress['attn'] : '',
        imp_addr1: this.consigneeAddress ? this.consigneeAddress['add1'] : '',
        imp_addr2: this.consigneeAddress ? this.consigneeAddress['add2'] : '',
        imp_addr3: this.consigneeAddress ? this.consigneeAddress['add3'] : '',
        imp_addr4: this.consigneeAddress ? this.consigneeAddress['add4'] : '',
        imp_postcode: this.consigneeAddress ? this.consigneeAddress['postcode'] : '',
        imp_city: this.consigneeAddress ? this.consigneeAddress['city'] : '',
        imp_state: this.consigneeAddress ? this.consigneeAddress['state'] : '',
        imp_country: this.consigneeAddress ? this.consigneeAddress['country'] : '',
        imp_phoneno: this.consigneeAddress ? this.consigneeAddress['tel'] : '',
        imp_faxno: this.consigneeAddress ? this.consigneeAddress['fax'] : '',
        exp_name: this.shipperAddress ? this.shipperAddress['name'] : '', // shipper
        exp_code:
          this.selectedBookingInfoData['selectedShipper'] ?
            this.selectedBookingInfoData['selectedShipper']['Shipper Code']
          : '',
        exp_attnto: this.shipperAddress ? this.shipperAddress['attn'] : '',
        exp_addr1: this.shipperAddress ? this.shipperAddress['add1'] : '',
        exp_addr2: this.shipperAddress ? this.shipperAddress['add2'] : '',
        exp_addr3: this.shipperAddress ? this.shipperAddress['add3'] : '',
        exp_addr4: this.shipperAddress ? this.shipperAddress['add4'] : '',
        exp_postcode: this.shipperAddress ? this.shipperAddress['postcode'] : '',
        exp_city: this.shipperAddress ? this.shipperAddress['city'] : '',
        exp_state: this.shipperAddress ? this.shipperAddress['state'] : '',
        exp_country: this.shipperAddress ? this.shipperAddress['country'] : '',
        exp_phoneno: this.shipperAddress ? this.shipperAddress['tel'] : '',
        exp_faxno: this.shipperAddress ? this.shipperAddress['fax'] : '',
        nparty_name: this.notifyPartyAddress ? this.notifyPartyAddress['name'] : '',
        nparty_code:
          this.selectedBookingInfoData['selectedNotifyParty'] ?
            this.selectedBookingInfoData['selectedNotifyParty']['Notify Party Code']
          : '',
        nparty_attnto: this.notifyPartyAddress ? this.notifyPartyAddress['attn'] : '',
        nparty_addr1: this.notifyPartyAddress ? this.notifyPartyAddress['add1'] : '',
        nparty_addr2: this.notifyPartyAddress ? this.notifyPartyAddress['add2'] : '',
        nparty_addr3: this.notifyPartyAddress ? this.notifyPartyAddress['add3'] : '',
        nparty_addr4: this.notifyPartyAddress ? this.notifyPartyAddress['add4'] : '',
        nparty_postcode: this.notifyPartyAddress ? this.notifyPartyAddress['postcode'] : '',
        nparty_city: this.notifyPartyAddress ? this.notifyPartyAddress['city'] : '',
        nparty_state: this.notifyPartyAddress ? this.notifyPartyAddress['state'] : '',
        nparty_country: this.notifyPartyAddress ? this.notifyPartyAddress['country'] : '',
        nparty_phoneno: this.notifyPartyAddress ? this.notifyPartyAddress['tel'] : '',
        nparty_faxno: this.notifyPartyAddress ? this.notifyPartyAddress['fax'] : '',
        imp_gst2license: this.consigneeAddress ? this.consigneeAddress['gst'] : '',
        exp_gst2license: this.shipperAddress ? this.shipperAddress['gst'] : '',
        actionflag: '',

        goodsdescription: this.selectedShippingInfoData['goodsDescription'],
        shipmark: this.selectedShippingInfoData['shippingMarks'],

        containerdetail: [],
        documents: [],

        //Goods Received
        jobconfirm: saveAs === undefined || saveAs === null ? false : saveAs,
        indicator: this.selectedUnconfirmReason ? this.selectedUnconfirmReason['Reason'] : '',
        jobnoconfirmremarks: this.unconfirmRemarks,

        saveasjob: this.selectedGoodsReceivedData['saveAsJob'] ? '1' : '0',
        jobno:
          this.selectedGoodsReceivedData['jobNo'] && this.selectedGoodsReceivedData['jobNo'] !== '**********' ?
            this.selectedGoodsReceivedData['jobNo']
          : '',
        urgent: this.selectedUrgent.keyValue,
        jobdate:
          this.selectedGoodsReceivedData['jobDateTime'] ?
            new Date(this.selectedGoodsReceivedData['jobDateTime']).formatString('yyyy-MM-dd')
          : '',
        jobtime:
          this.selectedGoodsReceivedData['jobDateTime'] ?
            new Date(this.selectedGoodsReceivedData['jobDateTime']).formatString('HHmm')
          : '',
        cargoreadinessdate:
          this.selectedGoodsReceivedData['cargoReadinessTime'] ?
            new Date(this.selectedGoodsReceivedData['cargoReadinessTime']).formatString('yyyy-MM-dd')
          : '',
        cargoreadinesstime:
          this.selectedGoodsReceivedData['cargoReadinessTime'] ?
            new Date(this.selectedGoodsReceivedData['cargoReadinessTime']).formatString('HHmm')
          : '',
        requireddate:
          this.selectedGoodsReceivedData['requiredDate'] ?
            new Date(this.selectedGoodsReceivedData['requiredDate']).formatString('yyyy-MM-dd HH:mm:ss')
          : '',
        cargogodown: this.selectedGoodsReceivedData['cargoGoDown'] ? this.selectedGoodsReceivedData['cargoGoDown'] : '',
        fumigation: this.selectedGoodsReceivedData['isFumigation'] ? '1' : '0',
        fumigatorcode:
          this.selectedGoodsReceivedData['selectedFumigator'] ?
            this.selectedGoodsReceivedData['selectedFumigator']['Supplier Code']
          : '',
        fumigationdate:
          this.selectedGoodsReceivedData['fumigateTime'] ?
            new Date(this.selectedGoodsReceivedData['fumigateTime']).formatString('yyyy-MM-dd')
          : '',
        fumigationtime:
          this.selectedGoodsReceivedData['fumigateTime'] ?
            new Date(this.selectedGoodsReceivedData['fumigateTime']).formatString('HHmm')
          : '',
        ventilationdate:
          this.selectedGoodsReceivedData['ventilationTime'] ?
            new Date(this.selectedGoodsReceivedData['ventilationTime']).formatString('yyyy-MM-dd')
          : '',
        ventilationtime:
          this.selectedGoodsReceivedData['ventilationTime'] ?
            new Date(this.selectedGoodsReceivedData['ventilationTime']).formatString('HHmm')
          : '',
        phytomarking: this.selectedGoodsReceivedData['phytoMarking'],
        fumigateat:
          this.selectedGoodsReceivedData['selectedLocation'] ?
            this.selectedGoodsReceivedData['selectedLocation']['Destination Code']
          : '',
        status: '',

        // BL Info
        savebl: this.selectedBLInfoData['saveBL'] ? '1' : '0',
        blNo: this.selectedBLInfoData['blNo'],
        placeofreceipt:
          this.selectedBLInfoData['selectedPlaceOfReceipt'] ?
            this.selectedBLInfoData['selectedPlaceOfReceipt']['Port Code']
          : '',
        placeofdelivery:
          this.selectedBLInfoData['selectedPlaceOfDelivery'] ?
            this.selectedBLInfoData['selectedPlaceOfDelivery']['Port Code']
          : '',
        placeofissue:
          this.selectedBLInfoData['selectedPlaceOfIssue'] ?
            this.selectedBLInfoData['selectedPlaceOfIssue']['Port Code']
          : '',
        dateofissue:
          this.selectedBLInfoData['dateOfIssue'] ?
            new Date(this.selectedBLInfoData['dateOfIssue']).formatString('yyyy-MM-dd')
          : '',
        timeofissue:
          this.selectedBLInfoData['dateOfIssue'] ?
            new Date(this.selectedBLInfoData['dateOfIssue']).formatString('HHmm')
          : '',
      };
      postData['packinglist'] = this.formatCargoPkgList();
      postData['containerdetail'] = this.formatCargoContList();
      postData['documents'] = await this.formatDocuments();
      //console.log(postData);

      if (!noload) {
        // Only show the loading if noload is null or undefined
        this.$fullscreenLoading('Check Compulsory Fields');
      }

      this.$fullscreenLoading('Saving Job');
      const saveResult = await this.$apiCallV2('/FreightBooking/SaveFreightBooking', postData);
      this.$fullscreenLoading();

      this.sMessages = [];
      this.eMessages = [];
      this.rMessages = [];
      //(saveResult);

      if (saveResult[0]['Results'] == 0) {
        if (postData['action'] == 'N') {
          this.sMessages.push('Successfully saved job!');

          const result = saveResult[0];
          let message = `Your booking number is ${result['BookingNo']}`;

          if (result['JobNo']) {
            message += `. Your job number is ${result['JobNo']}`;
            if (result['BLNo']) {
              message += `. Your BL number is ${result['BLNo']}`;
            }
          }

          this.sMessages.push(message);
          this.selectedBookingInfoData['bookingNo'] = saveResult[0]['BookingNo'];
        } else {
          const result = saveResult[0];

          this.sMessages.push(
            `Successfully edited booking for Booking No ${this.selectedBookingInfoData['bookingNo']}!`,
          );

          if (result['JobNo']) {
            this.sMessages.push(`Successfully edited job for Job No ${result['JobNo']}!`);
          }

          if (result['BLNo']) {
            this.sMessages.push(`Successfully edited bl for BL No ${result['BLNo']}!`);
          }
        }

        this.popupTitle = `View Booking`;
        this.viewMode = true;

        this.documentsToDelete = [];

        this.eJobFullDetails = null;
        this.eJobAttachments = null;
        this.selectedeJob = [];

        this.$nextTick(async () => {
          // To refresh Job Details after save
          this.selectedCargoInfoData['contDetailsList'] = [];
          this.selectedGoodsReceivedData['subPkgList'] = [];
          this.selectedDocumentInfoData['docData'] = [];
          this.statusTaskData['statusTaskListing'] = [];
          this.selectedFreightBooking = [{ 'Booking No': this.selectedBookingInfoData['bookingNo'] }];
          await this.viewEditClicked('v');
        });
        this.refreshClicked(false);
      } else {
        this.eMessages.push(saveResult[0]['ErrMsg']);
      }
    },
    checkPopupEntryFieldsJob: function () {
      // save as job is checked, check required job fields, decide save as confirm or unconfirm
      if (!this.selectedCargoInfoData['packageCount']) {
        this.rMessages.push({ Key: 'packagecount', Value: 'No. of Pkg is Required' });
      }
      if (!this.selectedCargoInfoData['selectedPackageType']) {
        this.rMessages.push({ Key: 'pkgtype', Value: 'Package Type is Required' });
      }
      if (!this.selectedBookingInfoData['selectedBillTo']) {
        this.rMessages.push({ Key: 'billToInput', Value: 'Bill To is Required' });
      }
      if (!this.selectedShippingInfoData['closingDate']) {
        this.rMessages.push({ Key: 'closingDate', Value: 'Closing Date is Required' });
      }
      if (!this.selectedShippingInfoData['polETA']) {
        this.rMessages.push({ Key: 'polETA', Value: 'POL ETA is Required' });
      }
      if (!this.selectedShippingInfoData['polETD']) {
        this.rMessages.push({ Key: 'polETD', Value: 'POL ETD is Required' });
      }
      if (!this.selectedShippingInfoData['podETA']) {
        this.rMessages.push({ Key: 'podETA', Value: 'POD ETA is Required' });
      }

      this.saveErrorList = this.rMessages.map((r) => {
        return `${r['Value'].replace(' is Required', '').trim()}`;
      });

      if (this.saveErrorList.length > 0) {
        this.popupError = true;
        this.saveAlertType = 'required';
        this.savePopupType = 'warning';
        this.savePopupTitle = 'The below fields are required but is empty or invalid';
        this.savePopupButtons = [{ text: 'Yes' }, { text: 'No', color: 'lightgray' }];
        this.showSavePopup = true;

        return true;
      }

      this.popupError = false;
      this.saveAlertType = 'choiceMode';
      this.savePopupType = 'warning';
      this.savePopupTitle = 'Saving Job';
      this.savePopupButtons = [{ text: 'Save' }, { text: 'Cancel', color: 'lightgray' }];
      this.showSavePopup = true;

      return true;
    },
    checkPopupEntryFields: function () {
      // check min and max date
      if (
        this.selectedBookingInfoData['bookingDateTime'] != null &&
        this.selectedBookingInfoData['bookingDateTime'] != ''
      ) {
        if (this.checkDateTime('bookingtime', this.selectedBookingInfoData['bookingDateTime'])) {
          this.rMessages.push({ Key: 'bookingtime', Value: this.errorMessages['bookingtime'] });
        }
      }
      if (this.selectedShippingInfoData['ETATime'] != null && this.selectedShippingInfoData['ETATime'] != '') {
        if (this.checkDateTime('ETATime', this.selectedShippingInfoData['ETATime'])) {
          this.rMessages.push({ Key: 'ETATime', Value: this.errorMessages['ETATime'] });
        }
      }
      if (this.selectedShippingInfoData['polETD'] != null && this.selectedShippingInfoData['polETD'] != '') {
        if (this.checkDateTime('polETD', this.selectedShippingInfoData['polETD'])) {
          this.rMessages.push({ Key: 'polETD', Value: this.errorMessages['polETD'] });
        }
      }
      if (this.selectedShippingInfoData['closingDate'] != null && this.selectedShippingInfoData['closingDate'] != '') {
        if (this.checkDateTime('closingDate', this.selectedShippingInfoData['closingDate'])) {
          this.rMessages.push({ Key: 'closingDate', Value: this.errorMessages['closingDate'] });
        }
      }
      if (this.selectedShippingInfoData['polETA'] != null && this.selectedShippingInfoData['polETA'] != '') {
        if (this.checkDateTime('polETA', this.selectedShippingInfoData['polETA'])) {
          this.rMessages.push({ Key: 'polETA', Value: this.errorMessages['polETA'] });
        }
      }
      if (this.selectedShippingInfoData['podETA'] != null && this.selectedShippingInfoData['podETA'] != '') {
        if (this.checkDateTime('podETA', this.selectedShippingInfoData['podETA'])) {
          this.rMessages.push({ Key: 'podETA', Value: this.errorMessages['podETA'] });
        }
      }
      if (this.selectedShippingInfoData['fDestETA'] != null && this.selectedShippingInfoData['fDestETA'] != '') {
        if (this.checkDateTime('fDestETA', this.selectedShippingInfoData['fDestETA'])) {
          this.rMessages.push({ Key: 'fDestETA', Value: this.errorMessages['fDestETA'] });
        }
      }
      if (
        this.selectedGoodsReceivedData['jobDateTime'] != null &&
        this.selectedGoodsReceivedData['jobDateTime'] != ''
      ) {
        if (this.checkDateTime('jobtime', this.selectedGoodsReceivedData['jobDateTime'])) {
          this.rMessages.push({ Key: 'jobtime', Value: this.errorMessages['jobtime'] });
        }
      }
      if (
        this.selectedGoodsReceivedData['cargoReadinessTime'] != null &&
        this.selectedGoodsReceivedData['cargoReadinessTime'] != ''
      ) {
        if (this.checkDateTime('cargoreadinesstime', this.selectedGoodsReceivedData['cargoReadinessTime'])) {
          this.rMessages.push({ Key: 'cargoreadinesstime', Value: this.errorMessages['cargoreadinesstime'] });
        }
      }
      if (
        this.selectedGoodsReceivedData['requiredDate'] != null &&
        this.selectedGoodsReceivedData['requiredDate'] != ''
      ) {
        if (this.checkDateTime('requireddate', this.selectedGoodsReceivedData['requiredDate'])) {
          this.rMessages.push({ Key: 'requireddate', Value: this.errorMessages['requireddate'] });
        }
      }
      if (
        this.selectedGoodsReceivedData['fumigateTime'] != null &&
        this.selectedGoodsReceivedData['fumigateTime'] != ''
      ) {
        if (this.checkDateTime('fumigateon', this.selectedGoodsReceivedData['fumigateTime'])) {
          this.rMessages.push({ Key: 'fumigateon', Value: this.errorMessages['fumigateon'] });
        }
      }
      if (
        this.selectedGoodsReceivedData['ventilationTime'] != null &&
        this.selectedGoodsReceivedData['ventilationTime'] != ''
      ) {
        if (this.checkDateTime('ventilation', this.selectedGoodsReceivedData['ventilationTime'])) {
          this.rMessages.push({ Key: 'ventilation', Value: this.errorMessages['ventilation'] });
        }
      }
      if (this.selectedBLInfoData['dateOfIssue'] != null && this.selectedBLInfoData['dateOfIssue'] != '') {
        if (this.checkDateTime('dateofissue', this.selectedBLInfoData['dateOfIssue'])) {
          this.rMessages.push({ Key: 'dateofissue', Value: this.errorMessages['dateofissue'] });
        }
      }
      if (!this.selectedBookingInfoData['selectedTransMode']) {
        this.rMessages.push({ Key: 'transmode', Value: 'Trans Mode is Required' });
      }
      if (!this.selectedBookingInfoData['selectedShipper']) {
        this.rMessages.push({ Key: 'shipper', Value: 'Shipper is Required' });
      }
      if (!this.selectedBookingInfoData['selectedCargoType']) {
        this.rMessages.push({ Key: 'cargotype', Value: 'Cargo Type is Required' });
      }
      if (!this.selectedBookingInfoData['selectedDeliveryType']) {
        this.rMessages.push({ Key: 'deliverytype', Value: 'Delivery Type is Required' });
      }
      if (!this.selectedBookingInfoData['selectedFreightTerms']) {
        this.rMessages.push({ Key: 'freightterms', Value: 'Freight Terms is Required' });
      }
      if (!this.selectedBookingInfoData['bookingDateTime']) {
        this.rMessages.push({ Key: 'bookingtime', Value: 'Booking Time is Required' });
      }
      if (!this.selectedShippingInfoData['selectedPortLoading']) {
        this.rMessages.push({ Key: 'pol', Value: 'Port of Loading is Required' });
      }
      if (!this.selectedShippingInfoData['selectedPortDischarge']) {
        this.rMessages.push({ Key: 'pod', Value: 'Port of Discharge is Required' });
      }

      if (!this.isSaveAsJobChecked) {
        // check if save as job is checked
        if (!this.selectedGoodsReceivedData['jobDateTime']) {
          this.rMessages.push({ Key: 'jobtime', Value: 'Job Time is Required' });
        }
        if (this.isFumigationRequired) {
          if (!this.selectedGoodsReceivedData['selectedFumigator']) {
            this.rMessages.push({ Key: 'fumigator', Value: 'Fumigator is Required' });
          }
          if (!this.selectedGoodsReceivedData['selectedLocation']) {
            this.rMessages.push({ Key: 'location', Value: 'Location is Required' });
          }
          if (!this.selectedGoodsReceivedData['fumigateTime']) {
            this.rMessages.push({ Key: 'fumigateon', Value: 'Fumigate on is Required' });
          }
          if (!this.selectedGoodsReceivedData['ventilationTime']) {
            this.rMessages.push({ Key: 'ventilation', Value: 'Ventilation Time is Required' });
          }
        }
      }

      if (this.rMessages.length > 0) {
        this.rMessages.forEach((r) => {
          this.errorMessages[r['Key']] = r['Value'];
        });
        return true;
      }

      return false;
    },
    validateDateOrder: async function (dateField1, dateField2, alertMessage, focusField) {
      if (
        this.selectedShippingInfoData[dateField1] &&
        this.selectedShippingInfoData[dateField2] &&
        this.selectedShippingInfoData[dateField1] > this.selectedShippingInfoData[dateField2]
      ) {
        const invalidDate = await this.$showAlert('warning', 'Invalid Date selection', alertMessage, [{ text: 'OK' }]);
        if (invalidDate === 'OK') {
          this.requiredClicked(focusField);
          return false;
        }
      }
      return true;
    },
    validateDates: async function () {
      return (
        (await this.validateDateOrder('polETA', 'podETA', 'POD ETA cannot be earlier than POL ETA!', 'polETA')) &&
        (await this.validateDateOrder(
          'polETA',
          'fDestETA',
          'Final Dest. ETA cannot be earlier than POL ETA!',
          'fDestETA',
        )) &&
        (await this.validateDateOrder(
          'podETA',
          'fDestETA',
          'Final Dest. ETA cannot be earlier than POD ETA!',
          'fDestETA',
        ))
      );
    },
    requiredClicked: function (e) {
      const inputField = this.inputTabs.find((field) => field.key === e);
      if (inputField) {
        this.currentTabIndex = inputField.tab;
        this.$nextTick(() => {
          this.$refs[e]?.setFocus();
        });
      }
    },
    addDoc: function () {
      this.documentInfoData['updatePopup'] = true;
    },

    documentUpdate: async function (e) {
      const editDetails = this.editDocumentsList;
      const details = e['details'];
      const hasStatus = details['statuses'];

      const allData = [];

      if (hasStatus == 'N/A') {
        // Doc without status
        const att = Array.from(details['attachment']);

        if (editDetails.length > 0) {
          // For edit
          if (details['docRef'] != editDetails[0]['Doc Ref #']) {
            // Changes in docref#
            editDetails.forEach((ed) => {
              // let index = this.selectedDocumentInfoData['docData'].indexOf(ed);
              const index = this.selectedDocumentInfoData['docData'].findIndex(
                (d) => JSON.stringify(d) == JSON.stringify(ed),
              );
              ed['Action'] = ed['Action'] == 'N' ? ed['Action'] : 'E';
              ed['Doc Ref #'] = details['docRef'];
              ed['ID'] = `${details['docCode']}-${details['docRef']}`;

              this.selectedDocumentInfoData['docData'].splice(index, 1, ed);
            });
          }
        }

        const fixedData = {
          'Doc Code': details['docCode'],
          'Doc Ref #': details['docRef'],
          Attachment: '',
          Remarks: '',
          Status: 'N/A',
          'Start Date': 'N/A',
          'Received Date': 'N/A',
          'Due Date': 'N/A',
          'Assign To': 'N/A',
          Sets: 'N/A',
          Page: 'N/A',

          documentguid: '',
          statusseqno: '',
          ID: `${details['docCode']}-${details['docRef']}`,
          fileAtt: '',
          filetype: '',
          Action: 'N',
        };

        if (att.length > 0) {
          // if has attachment
          for (const a of att) {
            const attBase64 = await this.$convertFile.toBase64(a);
            allData.push({
              'Doc Code': details['docCode'],
              'Doc Ref #': details['docRef'],
              Attachment: a.name,
              Remarks: '',
              Status: 'N/A',
              'Start Date': 'N/A',
              'Received Date': 'N/A',
              'Due Date': 'N/A',
              'Assign To': 'N/A',
              Sets: 'N/A',
              Page: 'N/A',

              documentguid: '',
              statusseqno: '',
              ID: `${details['docCode']}-${details['docRef']}`,
              fileAtt: attBase64,
              filetype: a.type,
              Action: 'N',
            });
          }
        } else if (editDetails.length == 0 && details) {
          // without attachment and not edit ?? !editDetails
          allData.push(fixedData);
        }

        const temp = this.selectedDocumentInfoData['docData'];
        this.selectedDocumentInfoData['docData'] = [];
        this.$nextTick(() => {
          this.selectedDocumentInfoData['docData'] = temp.concat(allData);
        });
      } else {
        // Doc with status
        const statusChecked = hasStatus.filter((s) => s['checked']);
        for (const s of statusChecked) {
          allData.push({
            'Doc Code': details['docCode'],
            'Doc Ref #': details['docRef'],
            Attachment: s['attachment'] ? s['attachment'][0].name : '',
            Remarks: s['remarks'],
            Status: s['docStatus'],
            'Start Date': s['start'] ? new Date(s['start']).formatString('yyyy-MM-dd HH:mm') : '',
            'Received Date': s['received'] ? new Date(s['received']).formatString('yyyy-MM-dd HH:mm') : '',
            'Due Date': s['due'] ? new Date(s['due']).formatString('yyyy-MM-dd HH:mm') : '',
            'Assign To': s['assignTo'],
            Sets: s['sets'],
            Page: s['page'],

            documentguid: '',
            statusseqno: '',
            ID: `${details['docCode']}-${details['docRef']}`,
            fileAtt: s['attachment'] ? await this.$convertFile.toBase64(s['attachment'][0]) : '',
            filetype: s['attachment'] ? s['attachment'][0].type : '',
            Action: 'N',
          });
        }

        if (editDetails.length == 0) {
          // For new
          const temp = this.selectedDocumentInfoData['docData'];
          this.selectedDocumentInfoData['docData'] = [];
          //if no status check then add a blank record to allData.
          if (allData.length == 0) {
            const fixedData = {
              'Doc Code': details['docCode'],
              'Doc Ref #': details['docRef'],
              Attachment: '',
              Remarks: '',
              Status: '',
              'Start Date': '',
              'Received Date': '',
              'Due Date': '',
              'Assign To': '',
              Sets: '',
              Page: '0',

              documentguid: '',
              statusseqno: '',
              ID: `${details['docCode']}-${details['docRef']}`,
              fileAtt: '',
              filetype: '',
              Action: 'N',
            };
            allData.push(fixedData);
          }

          this.$nextTick(() => {
            this.selectedDocumentInfoData['docData'] = temp.concat(allData);
          });
        } else {
          // For edit
          //find if there is any records that have empty status. If yes then add that record into allData
          const emptyStatus = editDetails.find((f) => f['Status'] === '');
          if (emptyStatus) {
            const fixedData = {
              'Doc Code': emptyStatus['docCode'],
              'Doc Ref #': emptyStatus['docRef'],
              Attachment: '',
              Remarks: '',
              Status: '',
              'Start Date': '',
              'Received Date': '',
              'Due Date': '',
              'Assign To': '',
              Sets: '',
              Page: '0',

              documentguid: '',
              statusseqno: '',
              ID: `${emptyStatus['docCode']}-${emptyStatus['docRef']}`,
              fileAtt: '',
              filetype: '',
              Action: 'N',
            };
            allData.push(fixedData);
          }

          const oldStatus = editDetails.map((ed) => ed['Status']);
          const newStatus = allData.map((ad) => ad['Status']);

          const toDelete = oldStatus.filter((o) => !newStatus.includes(o));
          const toAdd = newStatus.filter((n) => !oldStatus.includes(n));
          const toCheck = newStatus.filter((n) => oldStatus.includes(n));

          toDelete.forEach((d) => {
            const deleting = editDetails.find((ed) => ed['Status'] == d);
            // let index = this.selectedDocumentInfoData['docData'].indexOf(deleting);
            const index = this.selectedDocumentInfoData['docData'].findIndex(
              (d) => JSON.stringify(d) == JSON.stringify(deleting),
            );

            this.selectedDocumentInfoData['docData'].splice(index, 1);
            deleting['Action'] = 'D';
            this.documentsToDelete.push(deleting);
          });
          toAdd.forEach((a) => {
            const adding = allData.find((ad) => ad['Status'] == a);
            this.selectedDocumentInfoData['docData'].push(adding);
          });
          toCheck.forEach((c) => {
            const oldData = editDetails.find((ed) => ed['Status'] == c);
            const newData = allData.find((ed) => ed['Status'] == c);
            // let index = this.selectedDocumentInfoData['docData'].indexOf(oldData);
            const index = this.selectedDocumentInfoData['docData'].findIndex(
              (d) => JSON.stringify(d) == JSON.stringify(oldData),
            );

            Object.keys(oldData).forEach((k) => {
              if (oldData[k] == null) {
                oldData[k] = '';
              }
            });

            if (oldData['Sets'] == 0) {
              oldData['Sets'] = '';
            }
            if (oldData['Page'] == 0) {
              oldData['Page'] = '';
            }

            let hasChanges = false;

            if (newData['Attachment']) {
              hasChanges = true;
            }

            if (
              newData['Start Date'] != oldData['Start Date'] ||
              newData['Received Date'] != oldData['Received Date'] ||
              newData['Due Date'] != oldData['Due Date'] ||
              newData['Remarks'] != oldData['Remarks'] ||
              newData['Assign To'] != oldData['Assign To'] ||
              newData['Sets'] != oldData['Sets'] ||
              newData['Page'] != oldData['Page']
            ) {
              oldData['Start Date'] = newData['Start Date'];
              oldData['Received Date'] = newData['Received Date'];
              oldData['Due Date'] = newData['Due Date'];
              oldData['Remarks'] = newData['Remarks'];
              oldData['Assign To'] = newData['Assign To'];
              oldData['Sets'] = newData['Sets'];
              oldData['Page'] = newData['Page'];

              hasChanges = true;
            }

            if (hasChanges) {
              oldData['Action'] = oldData['Action'] == 'N' ? oldData['Action'] : 'E';
              this.selectedDocumentInfoData['docData'].splice(index, 1, oldData);
            }

            if (details['docRef'] != editDetails[0]['Doc Ref #']) {
              // Changes in docref#
              editDetails.forEach((ed) => {
                // let index = this.selectedDocumentInfoData['docData'].indexOf(ed);
                const index = this.selectedDocumentInfoData['docData'].findIndex(
                  (d) => JSON.stringify(d) == JSON.stringify(ed),
                );
                ed['Action'] = ed['Action'] == 'N' ? ed['Action'] : 'E';
                ed['Doc Ref #'] = details['docRef'];
                ed['ID'] = `${details['docCode']}-${details['docRef']}`;

                this.selectedDocumentInfoData['docData'].splice(index, 1, ed);
              });
            }

            oldData['Attachment'] = newData['Attachment'];
            oldData['fileAtt'] = newData['fileAtt'];
            oldData['filetype'] = newData['filetype'];
          });

          const temp = this.selectedDocumentInfoData['docData'];
          this.selectedDocumentInfoData['docData'] = [];
          this.$nextTick(() => {
            this.selectedDocumentInfoData['docData'] = temp;
          });
        }
      }

      this.selectedDocumentInfoData['selectedDoc'] = [];
    },
    deleteDoc: async function () {
      const deleteAll = [];
      const uniqueSelected = [
        ...new Set(
          this.selectedDocumentInfoData['selectedDoc'].map((s) => {
            return s['ID'];
          }),
        ),
      ];
      if (uniqueSelected.length == 0) {
        this.showDefaultPopup('error', 'No document selected', 'Please select at least one document to delete');
        return;
      }

      uniqueSelected.forEach((u) => {
        const selectedCount = this.selectedDocumentInfoData['selectedDoc'].filter((s) => {
          return s['ID'] == u;
        });
        const allCount = this.selectedDocumentInfoData['docData'].filter((s) => {
          return s['ID'] == u;
        });

        const deleteData = {
          docCode: selectedCount[0]['Doc Code'],
          refNo: selectedCount[0]['Doc Ref #'],
          allItems: allCount,
          selectedItems: selectedCount,
          allSelected: selectedCount.length == allCount.length,
        };
        deleteAll.push(deleteData);
      });

      if (deleteAll.length > 0) {
        for (const d of deleteAll) {
          this.deleteItems = d;
          this.deleteSelectedBtn.style.display = 'unset';

          if (d['allSelected']) {
            this.deleteMessage.innerHTML = `You are about to delete ${d['allItems'].length} of ${d['allItems'].length} attachments for<br />Doc Code ${d['docCode']}<br />(Doc Ref #: ${d['refNo']})`;
            this.deleteAllBtn.innerHTML = `Delete All Attachments for<br />Doc Code ${d['docCode']}<br />(Doc Ref #: ${d['refNo']})`;
            this.deleteSelectedBtn.style.display = 'none';
          } else {
            const selectedNames = d['selectedItems']
              .map((s) => {
                return s['Attachment'];
              })
              .join(`<br />`);
            this.deleteMessage.innerHTML = `You are about to delete ${d['selectedItems'].length} of ${d['allItems'].length} attachments for
              <br />Doc Code ${d['docCode']}
              <br />(Doc Ref #: ${d['refNo']})
              <br /><br />Selected Files:
              <br />${selectedNames}`;
            this.deleteAllBtn.innerHTML = `Delete All Attachments for<br />Doc Code ${d['docCode']}<br />(Doc Ref #: ${d['refNo']})`;
            this.deleteSelectedBtn.innerHTML = `Delete ${d['selectedItems'].length} of ${d['allItems'].length} Attachments for<br />Doc Code ${d['docCode']}<br />(Doc Ref #: ${d['refNo']})`;
          }
          // eslint-disable-next-line no-undef -- Sweet Alert should should be replaced with <PopupV2> in the future
          await Swal.fire({
            type: 'warning',
            title: 'Deleting Doc Code',
            html: this.deleteLayout,
            showConfirmButton: false,
            showCancelButton: false,
          });
        }
      }
    },
    deleteButtonClicked: function (e) {
      const btn = e.target.innerHTML;
      // eslint-disable-next-line no-undef -- Sweet Alert should should be replaced with <PopupV2> in the future
      Swal.close();

      if (btn.includes('Delete All')) {
        this.deleteItems['allItems'].forEach((d) => {
          // var index = this.selectedDocumentInfoData['docData'].indexOf(d);
          const index = this.selectedDocumentInfoData['docData'].findIndex(
            (dd) => JSON.stringify(dd) == JSON.stringify(d),
          );

          if (d['documentguid']) {
            // To send to API for delete
            d['Action'] = 'D';
            this.documentsToDelete.push(d);
          }

          this.selectedDocumentInfoData['docData'].splice(index, 1);
        });
      } else {
        this.deleteItems['selectedItems'].forEach((d) => {
          // var index = this.selectedDocumentInfoData['docData'].indexOf(d);
          const index = this.selectedDocumentInfoData['docData'].findIndex(
            (dd) => JSON.stringify(dd) == JSON.stringify(d),
          );

          if (d['documentguid']) {
            // To send to API for delete
            d['Action'] = 'D';
            this.documentsToDelete.push(d);
          }

          this.selectedDocumentInfoData['docData'].splice(index, 1);
        });
      }

      const temp = this.selectedDocumentInfoData['docData'];
      this.selectedDocumentInfoData['docData'] = [];
      this.$nextTick(() => {
        this.selectedDocumentInfoData['docData'] = temp;
      });

      this.selectedDocumentInfoData['selectedDoc'] = [];
    },
    editDoc: function () {
      if (this.selectedDocumentInfoData['selectedDoc'].length == 0) {
        this.showDefaultPopup('error', 'No document selected', 'Please select at least one document to edit');
      } else {
        const uniqueID = [...new Set(this.selectedDocumentInfoData['selectedDoc'].map((s) => s['ID']))];
        if (uniqueID.length > 1) {
          this.showDefaultPopup(
            'error',
            'Multiple Document Selected',
            'Please select at most one unique document to edit',
          );
        } else {
          this.editDocumentsList = this.selectedDocumentInfoData['docData'].filter((d) => d['ID'] == uniqueID[0]);
          this.documentInfoData['updatePopup'] = true;
        }
      }
    },

    showDefaultPopup: function (type, title, content) {
      this.defaultPopup['show'] = true;
      this.defaultPopup['type'] = type;
      this.defaultPopup['title'] = title;
      this.defaultPopup['content'] = content;
    },
    defaultPopupButtonClicked: function (e) {
      if (e == 'OK') {
        this.defaultPopup['show'] = false;
      }
    },

    showHideAddress: function (type) {
      switch (type) {
        case 'shipper':
          this.showShipperAddress = !this.showShipperAddress;
          break;
        case 'consignee':
          this.showConsigneeAddress = !this.showConsigneeAddress;
          break;
        case 'nparty':
          this.showNPartyAddress = !this.showNPartyAddress;
          break;
      }
    },
    editDetailsPopup: function (type) {
      this.edittingDetails = true;
      this.detailsEditObject = {};

      let postcode = '';
      let state = '';
      let country = '';

      switch (type) {
        case 'shipper':
          this.detailsEditTitle = 'Shipper';
          this.detailsEditObject = {
            name: this.shipperAddress['name'],
            addressLine1: this.shipperAddress['add1'],
            addressLine2: this.shipperAddress['add2'],
            addressLine3: this.shipperAddress['add3'],
            addressLine4: this.shipperAddress['add4'],
            postcodeSearch: '',
            postcodeKey: 'PostCode',
            city: this.shipperAddress['city'],
            stateSearch: '',
            stateKey: 'State',
            countrySearch: '',
            countryKey: 'Country',
            attn: this.shipperAddress['attn'],
            tel: this.shipperAddress['tel'],
            fax: this.shipperAddress['fax'],
            gst: this.shipperAddress['gst'],
            postcode: this.shipperAddress['postcode'], //added for v2
            state: this.shipperAddress['state'], //added for v2
            country: this.shipperAddress['country'], //added for v2
          };

          postcode = this.shipperAddress['postcode'];
          state = this.shipperAddress['state'];
          country = this.shipperAddress['country'];
          break;
        case 'consignee':
          this.detailsEditTitle = 'Consignee';
          this.detailsEditObject = {
            name: this.consigneeAddress?.name || '',
            addressLine1: this.consigneeAddress?.add1 || '',
            addressLine2: this.consigneeAddress?.add2 || '',
            addressLine3: this.consigneeAddress?.add3 || '',
            addressLine4: this.consigneeAddress?.add4 || '',
            postcodeSearch: '',
            postcodeKey: 'PostCode',
            city: this.consigneeAddress?.city || '',
            stateSearch: '',
            stateKey: 'State',
            countrySearch: '',
            countryKey: 'Country',
            attn: this.consigneeAddress?.attn || '',
            tel: this.consigneeAddress?.tel || '',
            fax: this.consigneeAddress?.fax || '',
            gst: this.consigneeAddress?.gst || '',
            postcode: this.consigneeAddress?.['postcode'] || '', //added for v2
            state: this.consigneeAddress?.['state'] || '', //added for v2
            country: this.consigneeAddress?.['country'] || '', //added for v2
          };

          postcode = this.consigneeAddress?.postcode || '';
          state = this.consigneeAddress?.state || '';
          country = this.consigneeAddress?.country || '';
          break;
        case 'nparty':
          this.detailsEditTitle = 'Notify Party';
          this.detailsEditObject = {
            name: this.notifyPartyAddress?.name || '',
            addressLine1: this.notifyPartyAddress?.add1 || '',
            addressLine2: this.notifyPartyAddress?.add2 || '',
            addressLine3: this.notifyPartyAddress?.add3 || '',
            addressLine4: this.notifyPartyAddress?.add4 || '',
            postcodeSearch: '',
            postcodeKey: 'PostCode',
            city: this.notifyPartyAddress?.city || '',
            stateSearch: '',
            stateKey: 'State',
            countrySearch: '',
            countryKey: 'Country',
            attn: this.notifyPartyAddress?.attn || '',
            tel: this.notifyPartyAddress?.tel || '',
            fax: this.notifyPartyAddress?.fax || '',
            gst: this.notifyPartyAddress?.gst || '',
            postcode: this.notifyPartyAddress?.['postcode'] || '', //added for v2
            state: this.notifyPartyAddress?.['state'] || '', //added for v2
            country: this.notifyPartyAddress?.['country'] || '', //added for v2
          };

          postcode = this.notifyPartyAddress?.postcode || '';
          state = this.notifyPartyAddress?.state || '';
          country = this.notifyPartyAddress?.country || '';
          break;
      }

      this.detailsEditObject['postcodeItems'] = this.addressDetails['postcodeList'];
      this.detailsEditObject['stateItems'] = this.addressDetails['stateList'];
      this.detailsEditObject['countryItems'] = this.addressDetails['countryList'];

      this.$nextTick(() => {
        this.detailsEditObject['postcodeSearch'] = postcode;
        this.detailsEditObject['stateSearch'] = state;
        this.detailsEditObject['countrySearch'] = country;
      });
    },
    detailsEditSave: function (type) {
      if (type == 'save') {
        switch (this.detailsEditTitle) {
          case 'Shipper':
            this.shipperAddress['name'] = this.detailsEditObject['name'];
            this.shipperAddress['add1'] = this.detailsEditObject['addressLine1'];
            this.shipperAddress['add2'] = this.detailsEditObject['addressLine2'];
            this.shipperAddress['add3'] = this.detailsEditObject['addressLine3'];
            this.shipperAddress['add4'] = this.detailsEditObject['addressLine4'];
            this.shipperAddress['postcode'] = this.detailsEditObject['postcode'];
            this.shipperAddress['city'] = this.detailsEditObject['city'];
            this.shipperAddress['state'] = this.detailsEditObject['state'];
            this.shipperAddress['country'] = this.detailsEditObject['country'];
            this.shipperAddress['attn'] = this.detailsEditObject['attn'];
            this.shipperAddress['tel'] = this.detailsEditObject['tel'];
            this.shipperAddress['fax'] = this.detailsEditObject['fax'];
            this.shipperAddress['gst'] = this.detailsEditObject['gst'];
            this.showShipperAddress = true;
            break;
          case 'Consignee':
            if (!this.consigneeAddress) {
              this.consigneeAddress = {};
            }
            this.consigneeAddress['name'] = this.detailsEditObject['name'];
            this.consigneeAddress['add1'] = this.detailsEditObject['addressLine1'];
            this.consigneeAddress['add2'] = this.detailsEditObject['addressLine2'];
            this.consigneeAddress['add3'] = this.detailsEditObject['addressLine3'];
            this.consigneeAddress['add4'] = this.detailsEditObject['addressLine4'];
            this.consigneeAddress['postcode'] = this.detailsEditObject['postcode'];
            this.consigneeAddress['city'] = this.detailsEditObject['city'];
            this.consigneeAddress['state'] = this.detailsEditObject['state'];
            this.consigneeAddress['country'] = this.detailsEditObject['country'];
            this.consigneeAddress['attn'] = this.detailsEditObject['attn'];
            this.consigneeAddress['tel'] = this.detailsEditObject['tel'];
            this.consigneeAddress['fax'] = this.detailsEditObject['fax'];
            this.consigneeAddress['gst'] = this.detailsEditObject['gst'];
            this.showConsigneeAddress = true;
            break;
          case 'Notify Party':
            if (!this.notifyPartyAddress) {
              this.notifyPartyAddress = {};
            }
            this.notifyPartyAddress['name'] = this.detailsEditObject['name'];
            this.notifyPartyAddress['add1'] = this.detailsEditObject['addressLine1'];
            this.notifyPartyAddress['add2'] = this.detailsEditObject['addressLine2'];
            this.notifyPartyAddress['add3'] = this.detailsEditObject['addressLine3'];
            this.notifyPartyAddress['add4'] = this.detailsEditObject['addressLine4'];
            this.notifyPartyAddress['postcode'] = this.detailsEditObject['postcode'];
            this.notifyPartyAddress['city'] = this.detailsEditObject['city'];
            this.notifyPartyAddress['state'] = this.detailsEditObject['state'];
            this.notifyPartyAddress['country'] = this.detailsEditObject['country'];
            this.notifyPartyAddress['attn'] = this.detailsEditObject['attn'];
            this.notifyPartyAddress['tel'] = this.detailsEditObject['tel'];
            this.notifyPartyAddress['fax'] = this.detailsEditObject['fax'];
            this.notifyPartyAddress['gst'] = this.detailsEditObject['gst'];
            this.showNPartyAddress = true;
            break;
        }
      }

      this.edittingDetails = false;
      this.detailsEditObject = null;
    },

    shipperSelected: function (e) {
      if (e) {
        this.shipperAddress = {};
        this.shipperAddress['name'] = e['Shipper Name'];
        this.shipperAddress['add1'] = e['Shipper Add1'];
        this.shipperAddress['add2'] = e['Shipper Add2'];
        this.shipperAddress['add3'] = e['Shipper Add3'];
        this.shipperAddress['add4'] = e['Shipper Add4'];
        this.shipperAddress['postcode'] = e['Shipper PostCode'];
        this.shipperAddress['city'] = e['Shipper City'];
        this.shipperAddress['state'] = e['Shipper State'];
        this.shipperAddress['country'] = e['Shipper Country'];
        this.shipperAddress['attn'] = e['Shipper Attn'];
        this.shipperAddress['tel'] = e['Shipper Tel'];
        this.shipperAddress['fax'] = e['Shipper Fax'];
        this.shipperAddress['gst'] = e['Shipper GSTNo'];

        this.showShipperAddress = true;

        if (this.pageType == 'Export') {
          this.selectedBookingInfoData['selectedBillTo'] = this.bookingInfoData['billTo'].find(
            (b) => b['Customer Code'] == e['Bill To'],
          );
          this.billToSelected(this.selectedBookingInfoData['selectedBillTo']);
        }
      } else {
        this.shipperAddress = null;
        this.showShipperAddress = false;
      }
    },
    consigneeSelected: function (e) {
      if (e) {
        this.consigneeAddress = {};
        this.consigneeAddress['name'] = e['Consignee Name'];
        this.consigneeAddress['add1'] = e['Consignee Add1'];
        this.consigneeAddress['add2'] = e['Consignee Add2'];
        this.consigneeAddress['add3'] = e['Consignee Add3'];
        this.consigneeAddress['add4'] = e['Consignee Add4'];
        this.consigneeAddress['postcode'] = e['Consignee PostCode'];
        this.consigneeAddress['city'] = e['Consignee City'];
        this.consigneeAddress['state'] = e['Consignee State'];
        this.consigneeAddress['country'] = e['Consignee Country'];
        this.consigneeAddress['attn'] = e['Consignee Attn'];
        this.consigneeAddress['tel'] = e['Consignee Tel'];
        this.consigneeAddress['fax'] = e['Consignee Fax'];
        this.consigneeAddress['gst'] = e['Consignee GSTNo'];

        this.showConsigneeAddress = true;

        if (this.pageType == 'Import') {
          this.selectedBookingInfoData['selectedBillTo'] = this.bookingInfoData['billTo'].find(
            (b) => b['Customer Code'] == e['Bill To'],
          );
          this.billToSelected(this.selectedBookingInfoData['selectedBillTo']);
        }
      } else {
        this.consigneeAddress = null;
        this.showConsigneeAddress = false;
      }
    },
    npartySelected: function (e) {
      if (e) {
        this.notifyPartyAddress = {};
        this.notifyPartyAddress['name'] = e['Notify Party Name'];
        this.notifyPartyAddress['add1'] = e['Notify Party Add1'];
        this.notifyPartyAddress['add2'] = e['Notify Party Add2'];
        this.notifyPartyAddress['add3'] = e['Notify Party Add3'];
        this.notifyPartyAddress['add4'] = e['Notify Party Add4'];
        this.notifyPartyAddress['postcode'] = e['Notify Party PostCode'];
        this.notifyPartyAddress['city'] = e['Notify Party City'];
        this.notifyPartyAddress['state'] = e['Notify Party State'];
        this.notifyPartyAddress['country'] = e['Notify Party Country'];
        this.notifyPartyAddress['attn'] = e['Notify Party Attn'];
        this.notifyPartyAddress['tel'] = e['Notify Party Tel'];
        this.notifyPartyAddress['fax'] = e['Notify Party Fax'];
        this.notifyPartyAddress['gst'] = e['Notify Party GSTNo'];

        this.showNPartyAddress = true;
      } else {
        this.notifyPartyAddress = null;
        this.showNPartyAddress = false;
      }
    },
    attachmentIsProjectable: function () {
      if (!this.attToView?.AttImageName || !this.attToView?.AttImageType) {
        return false;
      }
      const filenameSplit = this.attToView.AttImageName.trim().split('.');
      const fileExtension = filenameSplit[filenameSplit.length - 1].toLowerCase();
      const projectableExtensions = ['pdf', 'txt', 'json', 'mp3', 'wav', 'mp4', 'webm', 'ogg'];
      return this.attToView.AttImageType.startsWith('image/') || projectableExtensions.includes(fileExtension);
    },
    getFileTypeLabel() {
      if (this.attToView.AttImageName) {
        const split = this.attToView.AttImageName.split('.');
        return split[split.length - 1].toUpperCase();
      }
      return '';
    },
    downloadAttachment: function () {
      const anchor = document.createElement('a');
      anchor['href'] = this.attToView['AttImage'];
      anchor['download'] = this.attToView?.AttImageName || 'download';
      anchor.click();
    },
    //#endregion
    viewDocumentClicked: async function (_a, row) {
      this.$fullscreenLoading('Getting Attachments');
      this.attachmentList = [];
      this.attachmentList = await this.$apiCallV2(
        `/FreightForwarding/GetJobDocuments?jobno=${this.selectedFreightBooking[0]['Booking No']}`,
      );
      this.selectedViewDocumentList = [];
      this.selectedViewDocumentList = row;
      //if no attachments, show "no attachments"
      if (this.attachmentList?.length) {
        this.convertImage(this.attachmentList);
        this.attToView = this.attachmentList[row['Row #']];
        //console.log(this.attToView) // selected attachment to view
        this.totalAtt = this.attachmentList.length;

        for (let i = 0; i < this.attachmentList.length; i++) {
          this.attachmentList[i]['AttImageType'] = this.attachmentList[i]['AttImageType'].trim();

          for (const key in this.attachmentList[i]) {
            // if (this.attachmentList[i].hasOwnProperty(key)) {
            if (Object.prototype.hasOwnProperty.call(this.attachmentList[i], key)) {
              if (
                this.attachmentList[i][key] === '' ||
                this.attachmentList[i][key] == null ||
                !this.attachmentList[i][key]
              ) {
                this.attachmentList[i][key] = 'null';
              }
            }
          }
        }
      }
      this.$fullscreenLoading();
      if (this.attToView == undefined || this.attToView.length == 0) {
        this.$showAlert('warning', 'Unsaved Document', 'Please save the job before viewing the document.');
        return;
      }
      this.openPopup = true;
      this.loading = false;
    },
    convertImage: function (data) {
      //loop to get and convert all attImg
      for (let j = 0; j < data.length; j++) {
        let type = '';
        if (data[j]['AttImageType'] == null) {
          data[j]['AttImageType'] = 'n/a';
        } else {
          type = data[j]['AttImageType'].trim();
        }

        //if empty
        if (data[j]['AttImage'] == null || data[j]['AttImage'].length == 0) {
          data[j]['AttImage'] = 'https://i.ibb.co/kyZfpjx/no-image.png';

          //if value = pdf file
        } else if (type === '.pdf' || type === 'application/pdf') {
          //qyvoon 20210520
          const b64Data = data[j]['AttImage'];
          const byteCharacters = atob(b64Data);
          const byteArrays = [];
          const sliceSize = 512;
          for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
              byteNumbers[i] = slice.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
          }
          const blob = new Blob(byteArrays, { type: 'application/pdf' });
          const fileurl = URL.createObjectURL(blob);
          data[j]['AttImage'] = fileurl;
        } else if (type === '.xlsx') {
          data[j]['AttImage'] =
            'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,' +
            data[j]['AttImage'] +
            '==';
        } else if (type === '.ogg') {
          data[j]['AttImage'] = 'data:audio/ogg;base64,' + data[j]['AttImage'];
        } else if (type === '.pptx') {
          data[j]['AttImage'] =
            'data:application/vnd.openxmlformats-officedocument.presentationml.presentation;base64,' +
            data[j]['AttImage'].substring(0, data[j]['AttImage'].length - 4);
        } else if (type === '.docx') {
          data[j]['AttImage'] =
            'data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,' +
            data[j]['AttImage'];
        } else if (type === '.msg') {
          data[j]['AttImage'] = 'data:application/vnd.msoutlook;base64,' + data[j]['AttImage'];
        } else if (
          type === '.jpeg' ||
          type === '.jpg' ||
          type === 'image/Jpeg' ||
          type === '.jpeg' ||
          type === 'image/jpeg' ||
          type === '.png' ||
          type === '.gif'
        ) {
          data[j]['AttImage'] = 'data:image/png;base64,' + data[j]['AttImage'];
        } else if (type === '.webm' || type === 'video/webm') {
          data[j]['AttImage'] = 'data:video/webm;base64,' + data[j]['AttImage'];
        } else {
          data[j]['AttImage'] = 'data:application/octet-stream;base64,' + data[j]['AttImage'];
        }
      }
    },
    closePopup: function () {
      this.attachmentList = '';
      this.openPopup = false;
    },
    navAtt: function (clicked) {
      const getIndex = this.attToView;
      const inArray = this.attachmentList.indexOf(getIndex);

      // const curr = getIndex["#"] - 1;
      let next;
      const length = this.attachmentList.length;
      const end = length - 1;

      if (clicked == 'prev') {
        if (inArray == 0) {
          next = end;
        } else {
          next = inArray - 1;
        }
      } else if (inArray == end) {
        next = 0;
      } else {
        next = inArray + 1;
      }
      this.selectedViewDocumentList = this.selectedDocumentInfoData['docData'][next];
      this.attToView = this.attachmentList[next];
    },
    vesselIDselected: function (e) {
      this.selectedShippingInfoData['selectedVesselName'] =
        e['Vessel ID'] ?
          this.shippingInfoData['vesselIDList'].filter((t) => {
            return t['Vessel ID'].trim() == e['Vessel ID'].trim();
          })[0]
        : null;
    },
    vesselNameselected: function (e) {
      if (!e) return;
      this.selectedShippingInfoData['selectedVesselID'] =
        e['Vessel Name'] ?
          this.shippingInfoData['vesselIDList'].filter((t) => {
            return t['Vessel Name'].trim() == e['Vessel Name'].trim();
          })[0]
        : null;
    },
    checkDateTime: function (field, date) {
      //console.log(date)
      //console.log(field)
      if (date != null && date != '') {
        if (date > this.smallDateTimeMaxDate) {
          const errMsg = `Please select date earlier than ${this.smallDateTimeMaxDate.formatString('yyyy-MM-dd')}`;
          if (field == 'bookingtime') {
            this.errorMessages['bookingtime'] = errMsg;
          }
          if (field == 'ETATime') {
            this.errorMessages['ETATime'] = errMsg;
          }
          if (field == 'polETA') {
            this.errorMessages['polETA'] = errMsg;
          }
          if (field == 'polETD') {
            this.errorMessages['polETD'] = errMsg;
          }
          if (field == 'closingDate') {
            this.errorMessages['closingDate'] = errMsg;
          }
          if (field == 'dateofissue') {
            this.errorMessages['dateofissue'] = errMsg;
          }
          if (field == 'podETA') {
            this.errorMessages['podETA'] = errMsg;
          }
          if (field == 'fDestETA') {
            this.errorMessages['fDestETA'] = errMsg;
          }
          if (field == 'jobtime') {
            this.errorMessages['jobtime'] = errMsg;
          }
          if (field == 'cargoreadinesstime') {
            this.errorMessages['cargoreadinesstime'] = errMsg;
          }
          if (field == 'requireddate') {
            this.errorMessages['requireddate'] = errMsg;
          }
          if (field == 'fumigateon') {
            this.errorMessages['fumigateon'] = errMsg;
          }
          if (field == 'ventilation') {
            this.errorMessages['ventilation'] = errMsg;
          }
          return true;
        } else if (date < this.smallDateTimeMinValue) {
          const errMsg = `Please select date later than ${this.smallDateTimeMinValue.formatString('yyyy-MM-dd')}`;
          if (field == 'bookingtime') {
            this.errorMessages['bookingtime'] = errMsg;
          }
          if (field == 'ETATime') {
            this.errorMessages['ETATime'] = errMsg;
          }
          if (field == 'polETA') {
            this.errorMessages['polETA'] = errMsg;
          }
          if (field == 'polETD') {
            this.errorMessages['polETD'] = errMsg;
          }
          if (field == 'closingDate') {
            this.errorMessages['closingDate'] = errMsg;
          }
          if (field == 'dateofissue') {
            this.errorMessages['dateofissue'] = errMsg;
          }
          if (field == 'podETA') {
            this.errorMessages['podETA'] = errMsg;
          }
          if (field == 'fDestETA') {
            this.errorMessages['fDestETA'] = errMsg;
          }
          if (field == 'jobtime') {
            this.errorMessages['jobtime'] = errMsg;
          }
          if (field == 'cargoreadinesstime') {
            this.errorMessages['cargoreadinesstime'] = errMsg;
          }
          if (field == 'requireddate') {
            this.errorMessages['requireddate'] = errMsg;
          }
          if (field == 'fumigateon') {
            this.errorMessages['fumigateon'] = errMsg;
          }
          if (field == 'ventilation') {
            this.errorMessages['ventilation'] = errMsg;
          }
          return true;
        }
      }
    },
    //#region Query notifications
    vesselScheduleUpdated: async function (data) {
      if (data['configcode'].includes(this.loginDetails['ConfigCode']) && data['userid'] == this.loginDetails['LoginUserId']) {
        this.shipCallNoLoading = true;
        await this.refreshVesselSchedule();
      }
    },
    refreshVesselSchedule: async function () {
      this.shippingInfoData['shipCallNoList'] = await this.$apiCallV2('/Master/GetListMaster?save=true', { 
        masterfile: 'shipcallno' 
      });
      this.shipCallNoLoading = false;
    },
    //#endregion
  },
  async mounted() {
    this.hub = await this.$signal.get('notifications');
    this.hub.on('VesselScheduleUpdate', this.vesselScheduleUpdated);

    let mountedPromises = [];
    let promisesFulfilled = 0;

    // const query = this.$route.query;
    // let queryEdit = false;
    // if (query['date'] && query['job']) {
    //   this.prePopulate = true;
    //   this.preJob = query['job'];
    //   this.preDate = query['date'];
    //   queryEdit = query['edit'] ? query['edit'] == 'Y' : false;
    // }

    this.refreshStart = new Date(new Date().setDate(new Date().getDate() - 3));
    this.refreshEnd = new Date();

    if (!this.selectedSearchBy) {
      this.selectedSearchBy = this.searchFilterItems[0];
    }

    if (!this.selectedCriteria) {
      this.selectedCriteria = this.searchCriteriaItems[2];
    }

    this.loginDetails = await this.$apiCallV2('/Login/GetDetails');

    this.refreshClicked(true);

    const getBookingInfoDetailsPromises = this.getBookingInfoDetails().map((promise) => {
      return promise.then(() => {
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      });
    });
    mountedPromises = mountedPromises.concat(getBookingInfoDetailsPromises);

    const getShippingInfoTabDetailsPromises = this.getShippingInfoTabDetails().map((promise) => {
      return promise.then(() => {
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      });
    });
    mountedPromises = mountedPromises.concat(getShippingInfoTabDetailsPromises);

    const getShippingInfoTabDetailsByTransModePromises = this.getShippingInfoTabDetailsByTransMode().map((promise) => {
      return promise.then(() => {
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      });
    });
    mountedPromises = mountedPromises.concat(getShippingInfoTabDetailsByTransModePromises);

    const getCargoInfoTabDetailsPromises = this.getCargoInfoTabDetails().map((promise) => {
      return promise.then(() => {
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      });
    });
    mountedPromises = mountedPromises.concat(getCargoInfoTabDetailsPromises);

    const getLargeDataPromises = this.getLargeData().map((promise) => {
      return promise.then(() => {
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      });
    });
    mountedPromises = mountedPromises.concat(getLargeDataPromises);
    let sasControl = [];
    sasControl = await this.$apiCallV2(`/SASControl/GetSASControlAccess?system=FMS&page=FreightBooking`);
    await Promise.allSettled(mountedPromises).then((_promiseOutcomes) => {
      this.menu = this.menu.map((menu) => ({
        ...menu,
        enabled: true,
      }));

      sasControl.forEach((s) => {
        const menu = this.menu.find((m) => m['key'] == s['Control']);
        if (menu) {
          menu[s['Properties'].toLowerCase()] = s['PropertiesValue'] !== 'False';
          menu['message'] = 'Access Denied';
        }
      });
    });

    // For delete popup
    this.deleteLayout = document.createElement('div');
    this.deleteLayout.style.fontSize = '0.9em';

    this.deleteMessage = document.createElement('span');

    this.deleteAllBtn = document.createElement('button');
    this.deleteAllBtn.innerHTML = '';
    this.deleteAllBtn.className = 'swal2-confirm swal2-styled';
    this.deleteAllBtn.style.backgroundColor = 'red';
    this.deleteAllBtn.style.marginTop = '15px';
    this.deleteAllBtn.onclick = this.deleteButtonClicked;

    this.deleteSelectedBtn = document.createElement('button');
    this.deleteSelectedBtn.innerHTML = '';
    this.deleteSelectedBtn.className = 'swal2-confirm swal2-styled';
    this.deleteSelectedBtn.style.backgroundColor = '#ffa73a';
    this.deleteSelectedBtn.onclick = this.deleteButtonClicked;

    const cancel = document.createElement('button');
    cancel.innerHTML = 'Cancel';
    cancel.className = 'swal2-cancel swal2-styled';
    cancel.onclick = function () {
      // eslint-disable-next-line no-undef -- Sweet Alert should should be replaced with <PopupV2> in the future
      Swal.close();
    };

    this.deleteLayout.appendChild(this.deleteMessage);
    this.deleteLayout.appendChild(this.deleteSelectedBtn);
    this.deleteLayout.appendChild(this.deleteAllBtn);
    this.deleteLayout.appendChild(cancel);
  },
  watch: {
    showPopup: function (val) {
      if (!val) {
        this.clearPopupData();
      }
    },
    currentTabIndex: function (val) {
      if (val == 1) {
        // Repopulating the tableboxes for cargo info tab
        this.selectedCargoInfoData['selectedContDetails'] = [];

        if (this.selectedCargoInfoData['contDetailsList'].length > 0) {
          const temp = this.selectedCargoInfoData['contDetailsList'];
          this.selectedCargoInfoData['contDetailsList'] = [];
          this.$nextTick(() => (this.selectedCargoInfoData['contDetailsList'] = temp));
        }
      }
    },
    isTranshipmentRequired: function (newValue) {
      if (!newValue) {
        // Perform the actions when isTranshipmentRequired is false
        this.selectedShippingInfoData['selectedMVesselID'] = null;
        this.selectedShippingInfoData['mVoyNo'] = '';
        this.selectedShippingInfoData['selectedPortDischarge2'] = null;
        this.selectedShippingInfoData['ETATime'] = null;
      }
    },
    'selectedBLInfoData.saveBL': function (newValue) {
      if (!newValue) {
        this.selectedBLInfoData.blNo = '**********';
        this.selectedBLInfoData.selectedPlaceOfReceipt = null;
        this.selectedBLInfoData.selectedPlaceOfDelivery = null;
        this.selectedBLInfoData.selectedPlaceOfIssue = null;
        this.selectedBLInfoData.dateOfIssue = null;
      }
    },
    'selectedGoodsReceivedData.saveAsJob': function (newValue) {
      // enable/disable B/L Info tab based on saveAsJob
      // this is because to save B/L Info, must have job created with job no
      const blTab = this.tabsItems.find((tab) => tab.title === 'B/L Info');
      if (blTab) blTab.disabled = !newValue;

      if (!newValue) {
        this.selectedBLInfoData.saveBL = 0;
        this.selectedGoodsReceivedData.jobNo = '**********';
        this.selectedGoodsReceivedData.jobDateTime = null;
        this.selectedGoodsReceivedData.cargoReadinessTime = null;
        this.selectedGoodsReceivedData.requiredDate = null;
        this.selectedGoodsReceivedData.cargoGoDown = '';
        this.selectedGoodsReceivedData.isFumigation = false;
        this.selectedGoodsReceivedData.selectedFumigator = null;
        this.selectedGoodsReceivedData.selectedLocation = null;
        this.selectedGoodsReceivedData.fumigateTime = null;
        this.selectedGoodsReceivedData.ventilationTime = null;
        this.selectedGoodsReceivedData.phytoMarking = '';
        this.selectedGoodsReceivedData.subPkgList = [];
        this.selectedGoodsReceivedData.selectedSubPkgs = [];
        this.selectedUrgent = this.urgentRadioButton.find((r) => r.keyValue == 'N');

        this.errorMessages.jobtime = '';
        this.errorMessages.cargoreadinesstime = '';
        this.errorMessages.requireddate = '';
        this.errorMessages.fumigateon = '';
        this.errorMessages.ventilation = '';
        this.errorMessages.fumigator = '';
        this.errorMessages.location = '';
      }
    },
    'selectedGoodsReceivedData.isFumigation': function (newValue) {
      if (!newValue) {
        this.selectedGoodsReceivedData.selectedFumigator = null;
        this.selectedGoodsReceivedData.selectedLocation = null;
        this.selectedGoodsReceivedData.ventilationTime = null;
        this.selectedGoodsReceivedData.fumigateTime = null;
        this.selectedGoodsReceivedData.phytoMarking = '';
        this.errorMessages.fumigateon = '';
        this.errorMessages.ventilation = '';
        this.errorMessages.fumigator = '';
        this.errorMessages.location = '';
      }
    },
  },
  beforeDestroy() {
    this.hub.off('VesselScheduleUpdate');
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.import-job-main {
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  font-size: 12px;
  display: flex;

  > .left-menu {
    display: flex;
    justify-content: center;
  }

  > .right-tabulator {
    width: 100%;
    margin-left: 5px;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    // For new filtering
    > .filter-area {
      background-color: white;
      padding: 5px;
      border-radius: 5px;
      display: flex;
      align-items: center;
      justify-content: flex-end;

      > * {
        margin-left: 5px;
      }

      > .filter-button {
        position: relative;

        > .filter-box {
          position: absolute;
          right: 0;
          top: 100%;
          border: 1px solid black;
          border-radius: 3px;
          padding: 5px;
          background-color: white;
          display: flex;
          flex-direction: column;
          z-index: 99;

          > .filter-item {
            border: 1px solid black;
            border-radius: 5px;
            padding: 5px;
            padding-top: 10px;
            margin: 5px;
            margin-top: 10px;
            position: relative;

            > .filter-check {
              position: absolute;
              top: -10px;
              background-color: white;
              padding: 0 10px;
              left: 10px;
            }

            > .dates {
              display: flex;

              > * {
                margin: 0 3px;
              }
            }
          }

          > button {
            position: relative;
            right: 5px;
            align-self: flex-end;
          }
        }
      }
    }

    > .tabulator-area {
      background-color: white;
      margin-top: 5px;
      border-radius: 5px;
      height: calc(100% - 43px);
      padding: 5px;
    }
  }

  > .ejob-popup {
    position: fixed;
    background-color: rgba(0, 0, 0, 0.5);
    top: 50px;
    left: 0;
    height: calc(100% - 50px);
    width: 100%;
    z-index: 199;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    > .ejob-content {
      background-color: lightgray;
      padding: 5px;
      width: calc(100% - 90px);
      height: calc(100% - 20px);
      position: relative;
      display: flex;
      flex-direction: column;

      > .top-section,
      > .table-section,
      > .bottom-section {
        background-color: white;
        border-radius: 5px;
        padding: 5px;
      }

      > .top-section,
      > .bottom-section {
        display: flex;
        justify-content: flex-end;
      }

      > .top-section {
        align-items: center;

        > :first-child {
          margin-right: 5px;
        }

        > :last-child {
          margin-left: 5px;
        }
      }

      > .table-section {
        height: 100%;
        margin-top: 5px;
        overflow: hidden;

        > .verification-upload {
          display: flex;
          flex-direction: column;
          height: 100%;

          > object {
            background-color: lightgray;
            height: 100%;
          }
        }
      }

      > .bottom-section {
        margin-top: 5px;

        > button {
          margin-left: 5px;
          color: white;
        }

        > :nth-child(1) {
          background-color: green;
        }

        > :nth-child(2) {
          background-color: red;
        }

        > :nth-child(3),
        > .cancel-btn {
          background-color: gray;
        }
      }
    }
  }
  > .print-popup {
    position: absolute;
    background: rgba(128, 128, 128, 0.5);
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
    z-index: 100;
    padding: 30px 65px;
    display: flex;
    flex-direction: column;

    > .title {
      background-color: rgb(50, 54, 57);
      color: white;
      text-align: center;
      font-weight: bold;
      padding: 5px;
    }
  }
}

.delete-popup {
  text-align: left;
  padding: 20px;
}

.popup-milestones {
  border: 1px solid rgba(0, 0, 0, 0.3);
  width: 100%;
  padding: 5px;
  margin-bottom: 8px;
  position: relative;
  font-size: 10px;

  > .milestones-row {
    display: flex;
    align-items: center;

    > * {
      margin-right: 5px;
    }

    > .milestones-block {
      position: relative;
      margin-bottom: 2px;

      > .milestones-item {
        border: 1px solid rgba(128, 128, 128, 0.5);
        max-width: 100px;
        min-width: 100px;
        height: 50px;
        -webkit-line-clamp: 2;
        display: -webkit-box;
        text-overflow: ellipsis;
        overflow: hidden;
        -webkit-box-orient: vertical;
        cursor: default;
        padding: 3px 20px 3px 3px;
      }

      > .milestone-tooltip {
        position: absolute;
        height: 100%;
        width: 100%;
        top: 0;
        left: 0;
      }
    }

    > .legend-items {
      display: flex;
      align-items: center;

      > .legend-box {
        height: 10px;
        width: 10px;
        border: 1px solid rgba(128, 128, 128, 0.5);
        margin-right: 3px;
      }
    }
  }

  > .collapse-milestone {
    position: absolute;
    bottom: 2px;
    right: 2px;
    transition: 0.3s;
    cursor: pointer;
  }
}

.popup-details {
  display: flex;
  height: 100%;

  > .summary {
    width: 115px;
    min-width: 115px;
    margin-right: 5px;
  }
}

.slot-body-block {
  display: flex;
  flex-wrap: nowrap; /* Ensures columns stay in place */
  height: auto; /* Allows dynamic height */
  overflow: visible; /* Lets content expand naturally */

  > .slot-content-column {
    flex-grow: 1;
    display: flex;
    flex-direction: column; /* Stacks elements vertically */
    width: calc(100% / 3);
    margin-right: 5px;

    > .box-container {
      border: 1px solid rgba(0, 0, 0, 0.3);
      border-radius: 5px;
      padding: 10px 5px 5px 5px;
      margin-right: 5px;
      position: relative;
      margin-top: 10px;
    }

    > .address {
      height: 50%;
      border-radius: 3px;
      border: 1px solid rgba(128, 128, 128, 0.5);
      min-height: 100px;
      position: relative;
      display: flex;
      flex-direction: column;
      // padding: 5px;
      overflow: auto;
      margin-bottom: 5px;

      > .edit-area {
        position: absolute;
        top: 3px;
        right: 3px;
        display: flex;
        align-items: center;

        > button {
          padding: 1px 10px;
        }

        > img {
          transition: 0.3s;
          cursor: pointer;
        }

        > .img-hide {
          transform: scaleY(1);
        }

        > .img-show {
          transform: scaleY(-1);
        }
      }

      > .address-block {
        overflow: auto;
        padding: 5px;
      }
    }

    > .address-collapse {
      max-height: 28px;
      min-height: 28px;
      justify-content: center;
      padding-left: 5px;
    }
  }

  > .column-flex {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  > .edit-address-popup {
    position: absolute;
    left: 0;
    top: 0;
    background-color: rgba(128, 128, 128, 0.5);
    height: 100%;
    width: 100%;
    z-index: 999;
    display: flex;
    align-items: center;
    justify-content: center;

    > .edit-content {
      width: 90%;
      max-width: 500px;
      background-color: white;
      padding: 10px;

      > .title {
        font-weight: bold;
        font-size: 13px;
        margin-bottom: 5px;
      }

      > .button-section {
        display: flex;
        justify-content: flex-end;

        > button {
          margin-left: 5px;
        }
      }
    }
  }
}

.shipping-textarea {
  display: flex;
  height: calc(100% - 261px);
  min-height: 100px;

  > * {
    margin: 3px;
  }
}

.status-container {
  border: 1px solid rgb(200, 200, 200);
  padding: 3px;
  margin-right: 5px;
  margin-left: 3px;
}

.outstanding-prog {
  display: flex;
  border: 1px solid rgb(200, 200, 200);
  position: relative;
  width: calc(100% - 2px);
  margin-left: 3px;
  padding: 3px;

  > .progress-ind {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 50%;
    background-color: rgba(51, 231, 255, 0.5);
  }
}

.table-container {
  border: 1px solid rgba(0, 0, 0, 0.5);
  padding: 5px;
  margin-top: 3px;

  > .tabulator-title {
    display: flex;
    align-items: center;
    margin-bottom: 5px;

    > button {
      margin-left: 3px;
    }
  }
}

.tab-block {
  display: flex;
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.5);
  margin-top: 10px;
  margin-bottom: 10px;
  position: relative;
  padding-top: 10px;
  padding-left: 5px;

  > .tab-title {
    width: fit-content;
    background-color: white;
    position: absolute;
    top: -10px;
    left: 10px;
    padding: 0 10px;
  }

  > .details-column {
    width: calc(100% / 3);
    margin-right: 5px;
  }
}

.cargo-block {
  display: flex;
  width: 100%;
  position: relative;
  margin-bottom: 10px;

  > .details-column {
    width: calc(100% / 3);
    margin-right: 5px;
  }
}

.top-button-section {
  display: flex;
  width: 100%;
  background: white;

  > button {
    margin-right: 5px;
  }
}

.save-content {
  max-height: 150px;
  overflow: auto;
  box-shadow: inset 0 0 5px 0 gray;
  padding: 10px;
  text-align: left;
  margin: 5px 0;
}

.popup-error {
  color: red;
  text-align: center;
}

.report-popup-selection {
  position: fixed;
  width: 100%;
  height: 100%;
  padding-top: 50px;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  z-index: 99999;
  transition: 0.3s;

  > .report-popup-container {
    background-color: white;
    width: 100%;
    max-width: 300px;
    max-height: 180px;
    padding: 0 10px;
    overflow: auto;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;

    > .report-link {
      color: black;
      border: 1px solid black;
      width: 100%;
      margin-top: 5px;
      padding: 5px;
      text-align: left;
      background-color: transparent;
      border-radius: 0;

      &:disabled {
        color: rgba(0, 0, 0, 0.3);
        border: 1px solid rgba(0, 0, 0, 0.3);
      }

      &:hover:enabled {
        background-color: rgba(0, 0, 0, 0.2);
      }

      // &:hover:disabled::after {
      //   content: ' - No container available';
      //   color: red;
      // }
    }

    > .report-disabled-container:hover::after {
      content: ' - No container available';
      color: red;
    }

    > .report-disabled-cancelled:hover::after {
      content: ' - Job is cancelled';
      color: red;
    }

    > .report-disabled-cargo-type:hover::after {
      content: ' - Cargo Type invalid';
      color: red;
    }
  }

  > .report-popup-title {
    width: 100%;
    max-width: 300px;
    background-color: white;
    text-align: center;
    font-weight: bold;
    padding: 5px;
  }

  > .report-popup-cancel {
    background-color: white;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: 300px;

    > button {
      margin: 5px 0;
    }
  }
}

.import-cont-table {
  display: flex;
  height: calc(100% - 233px);
  width: 100%;

  > .legend {
    height: 100%;
    min-width: 200px;
    max-width: 200px;
    margin-left: 5px;
    overflow: auto;

    > .legend-item {
      display: flex;
      align-items: center;
      padding: 5px;
      text-align: left;

      > img {
        width: 20px;
        margin-right: 5px;
      }
    }
  }
}

.ejob-reject-content {
  text-align: left;
  margin: 0 10px;

  > .collapsible-title-container {
    display: flex;
    align-items: center;
    cursor: pointer;

    > img {
      height: 20px;
      transition: 0.3s;
    }
  }

  > .message-reason-container {
    height: 100px;
  }
}

.container-popup-row {
  overflow: visible !important;
  display: flex;
  align-items: baseline;
}

.modal-content table {
  width: 100%;
  border-collapse: collapse;
}

.modal-content th,
.modal-content td {
  padding: 8px;
  text-align: center;
  border: 1px solid #ddd;
}

.modal-content th:first-child,
.modal-content td:first-child {
  text-align: left;
}

.disabled-row {
  opacity: 0.5;
  pointer-events: none;
}
.different-value {
  color: red;
}

//attachment popup
.attchWin-main {
  width: calc(100%);
  height: calc(100% - 10px);
  padding: 5px;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  align-items: center;
  background-color: #00000048;
  z-index: 999;

  > .attchWin-close {
    height: 30px;
    width: 30px;
    font-weight: bold;
    border-radius: 50%;
    background-color: white;
    color: black;
    text-align: center;
    cursor: pointer;
    position: absolute;
    right: 0;
    top: 0;
    margin: 20px;
    font-size: 14px;
  }

  > .attchWin-display {
    width: 80%;
    margin: 0 auto;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    background-color: #eaeaea;
    border-radius: 3px;
    display: flex;
    height: 90vh;

    > .left {
      position: relative;
      float: left;
      width: 100%;
      height: 100%;
      border: 1px solid black;
      background: black;
      height: 100%;
      text-align: center;
      display: flex;

      > .leftButton {
        width: auto;
        height: 100%;
        float: left;
        align-items: center;
        left: 0;

        > .navButton-left {
          margin: 0 auot;
          position: relative;
          z-index: 300;
          font-size: 36px;
          color: white;
          background: none;
          top: 50%;

          &:hover {
            cursor: pointer;
            background: rgba(200, 200, 200, 0.8);
          }
        }
      }

      > .rightButton {
        width: auto;
        height: 100%;
        float: right;
        align-items: center;

        > .navButton-right {
          margin: 0;
          position: relative;
          top: 50%;
          float: right;
          /* -webkit-transform: translateY(-50%);
      transform: translateY(-50%); */
          z-index: 300;
          font-size: 36px;
          color: white;
          background: none;

          &:hover {
            cursor: pointer;
            background: rgba(200, 200, 200, 0.8);
          }
        }
      }

      > .attchWin-attachment {
        position: relative;
        //margin: 0 auto;
        width: 100%;
        height: 100%;
        text-align: center;
        -o-object-fit: contain;
        object-fit: contain;
        float: left;
        align-self: center;
        //top: 25%;

        img {
          width: 70%;
          object-fit: contain;
          margin: 0 auto;
          max-height: 90vh;
        }

        &:hover {
          cursor: pointer;
        }

        iframe {
          width: 100%;
          height: 100%;
          min-height: 600px;
        }

        > .attachment-not-projectable-container {
          height: 100%;
          width: 100%;
          background-color: rgba(0, 0, 0, 0.7);
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          text-align: center;
          font-size: 40px;

          > span {
            margin: 10px 0 10px 0;
            color: white;
          }

          > button {
            height: 50px;
            width: 150px;
            margin: 10px 0 10px 0;
            font-size: 16px;

            > img {
              height: 24px;
              width: 24px;
            }
          }
        }
        .video-wrapper {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100%;
          width: 100%;
        }
      }
    }

    > .right {
      //position: relative;
      float: left;
      width: 500px;
      height: 100%;
      min-width: 250px;
      border: 1px solid black;
      display: grid;

      > .attchWin-info {
        position: relative;
        margin: auto 5px 0;
        text-align: left;
        margin: 5px;
        height: fit-content;
        overflow-x: auto;

        #infoHeader {
          width: 100%;
          table-layout: fixed;
          font-size: 12px;
          border-collapse: collapse;
          background: #d4dfeb;
          margin: 5px 0px;
          border: 1px solid black;

          td {
            padding: 3px;
          }
          th {
            font-weight: bold;
            width: 92px;
          }
        }

        #tableContent {
          width: 100%;
          position: relative;
          table-layout: fixed;
          font-size: 12px;
          border-collapse: collapse;
          margin: 20px auto;
          background: #fff;
          align-self: center;
          text-align: left;

          tr {
            border: 1px solid black;
            padding: 3px;
          }
          td {
            padding: 3px;
            border: 1px solid black;
            overflow-x: auto;
          }
          th {
            font-weight: bold;
            background: #d4dfeb;
            border: 1px solid black;
            padding: 3px;
            width: 97px;
          }
        }
      }
      > .count {
        position: relative;
        width: auto;
        text-align: center;
        margin: auto 3px 0;
        height: fit-content;
        border: 1px solid black;
        background: #a1a9b2;
      }
    }
  }
}
</style>
